import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft, ChevronRight, ChevronDown, CalendarDays, Search, X, Plus, Trash2,
  ExternalLink, MapPin, AlertCircle, LayoutGrid, ListTree, Send, Users,
  RadioTower, Star, Clock, CheckCircle2, AlertTriangle, Phone, GripVertical,
  UserCircle2, ArrowRightLeft,
} from "lucide-react";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays,
  addMonths, isSameMonth, parseISO, isToday, differenceInCalendarDays, getISOWeek,
} from "date-fns";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Button, Spinner, EmptyState, Input, Pill, Field, Select, Textarea,
} from "@/components/ui";
import { fmtDate } from "@/lib/dates";
import CrewRosterPanel from "@/features/crews/CrewRosterPanel";
import AssignPersonalBookingModal from "@/features/schedule/AssignPersonalBookingModal";
import { createProjectBooking } from "@/lib/bookingWrites";   // 3 Nov 2026 — one way in for a booking
import { ScheduleStanddownStrip, StopsLane, CalendarWeekStops, useOpenStanddowns, stopCovering, blockedRange } from "@/features/hse/standdownShared";   // mig 425

// =============================================================================
// CrewCalendarScreen — /schedule  (T-125 v3)
//
// Full month-grid calendar. Each active project renders as ONE bar across the
// days it's running. Bar colour = the project's first crew assignment, OR
// grey if no crew assigned. Click a bar → side panel with the project
// summary, the crews on it, and an inline form to assign a new crew.
//
// Lane-packing is greedy with MAX_LANES = 3 (per James — "we never have more
// than 3 builds at once"). Anything beyond that lands in "+N more" overflow.
//
// All assignment UX lives here. Project detail page is read-only display.
//
// 25 Sep 2026: public holidays (the `holidays` table) show on every tab's
// ruler and row backdrop in amber, and the next few are listed under the
// month switcher. Seeded for QLD by mig 388.
//
// 8 Oct 2026 (mig 425): HSE stand-downs and carrier embargoes. James: "good to
// have the ability to put that into Ops so schedule can see it and not plan
// works during one." The DATABASE refuses the booking (every write on this
// screen already shows the database's message); this screen only shows it:
// a red "No bookings" strip under the holidays, and on the Workforce tab a
// booking that was already inside a stop when it was raised wears a red
// dashed outline and says so on hover — it is not removed, and it can be
// shortened or moved out. The two saves in the project side panel that used
// to swallow a refusal (crew dates, add booking) now say why.
// Same day, from James's first smoke ("make Embargos visually on the schedule
// page where we work … could also show the stop works in a bar"): every stop
// is DRAWN — a red hatched "No bookings" lane directly under the date ruler on
// the Workforce, People and Gantt tabs, and the same bars along the bottom of
// each week on the Calendar tab. Each bar opens its stand-down.
// =============================================================================

const UNASSIGNED_GREY = "#475569";
// Won work with no crew booked yet reads as GREEN — it is real work waiting to
// be resourced, not a problem. Tentative bids already render soft-red dashed in
// their own lane, so at a glance: green = won, needs a crew; red = not won yet;
// solid colour = crew actually booked. (James 18 Aug 2026)
const UNBOOKED_GREEN = "#16a34a";

// One person's row on the schedule. Tall enough to READ - the first cut used an
// 18px row with 8px text, and James's own ad-hoc task came out as "Material P..."
// which is not information. A schedule you have to hover to read is a schedule
// that gets read wrong.
const PERSON_ROW_H = 20;

// Mig 400 — a Mon–Fri bar shows its weekends as notches: the bar keeps its
// shape (one booking, one drag) but the Saturday and Sunday inside it read as
// "not on site". Returns the 0-based day offsets within [from, to] that fall
// on a weekend, for the caller to overlay.
function weekendOffsets(from: string, to: string): number[] {
  const out: number[] = [];
  const start = parseISO(from);
  const n = differenceInCalendarDays(parseISO(to), start) + 1;
  for (let i = 0; i < n; i++) {
    const dow = addDays(start, i).getDay();
    if (dow === 0 || dow === 6) out.push(i);
  }
  return out;
}
// Mon–Fri days in a closed range, inclusive.
function workingDays(from: string, to: string): number {
  if (!from || !to || to < from) return 0;
  const n = differenceInCalendarDays(parseISO(to), parseISO(from)) + 1;
  return n - weekendOffsets(from, to).length;
}
const WEEKEND_NOTCH = "repeating-linear-gradient(135deg, rgba(255,255,255,0.55) 0 3px, rgba(255,255,255,0.15) 3px 6px)";

// Sentinel crew id for the "not in a crew" drop target. A real uuid could never
// collide with it, and it keeps "drag somebody out of every crew" as the same
// gesture as "drag them into a different one" rather than a separate control.
const STANDALONE = "__standalone__";

// How each kind of person-bar is drawn. Solid means "this is where they are".
// Hatched means "not on the crew's job" - deliberately NOT a fifth flat colour
// competing with four crew colours.
const PERSON_BAR_STYLE: Record<string, { solid: boolean; hatch?: string; border?: string }> = {
  crew:  { solid: true },
  solo:  { solid: true },
  // Mig 356 — a depot day is planned WORK, not a warning, so it is a solid
  // block like a site day: slate, so it never reads as a crew colour.
  depot: { solid: true },
  task:  { solid: false,
           hatch:  "repeating-linear-gradient(45deg, rgba(255,120,68,0.32) 0 5px, rgba(255,120,68,0.10) 5px 10px)",
           border: "1px solid rgba(255,120,68,0.85)" },
  leave: { solid: false,
           hatch:  "repeating-linear-gradient(45deg, rgba(148,163,184,0.30) 0 5px, transparent 5px 10px)",
           border: "1px solid rgba(148,163,184,0.7)" },
  // A defect visit is going BACK to a site to fix something - red, because it is
  // the one kind of person-bar that represents rework rather than work, and
  // hatched like the other away-work so it never reads as a fifth crew colour.
  defect: { solid: false,
            hatch:  "repeating-linear-gradient(45deg, rgba(220,68,68,0.30) 0 5px, rgba(220,68,68,0.08) 5px 10px)",
            border: "1px solid rgba(220,68,68,0.85)" },
  // 333 - LISTED (standby): on the roster, has access, may attend, NOT counted
  // on site. Greyed and dashed so it reads as "pencilled in", never as a crew
  // colour and never as leave (whose hatch runs the other way at 45deg).
  listed: { solid: false,
            hatch:  "repeating-linear-gradient(90deg, rgba(120,132,152,0.28) 0 6px, rgba(120,132,152,0.08) 6px 12px)",
            border: "1px dashed rgba(120,132,152,0.9)" },
};

// ---------------------------------------------------------------------------
// buildPersonBars — one person's month, as bars. THE definition of "where is
// this person": crew work clipped to their dated membership, jobs they are on
// individually, ad-hoc tasks, approved leave. Shared by the Crews tab and the
// People tab so the two can never disagree.
// ---------------------------------------------------------------------------
// 333 - one person-site stint off the ROSTER. The record, not an inference:
// seeded when a crew is booked, then owned per person, and it survives crew
// moves - which is precisely what the old membership-derived bars did not.
interface RosterRow {
  roster_id:    string;
  employee_id:  string;
  pca_id:       string | null;
  project_id:   string;
  on_site_from: string;
  on_site_to:   string | null;
  status:       "rostered" | "listed";
  role_on_site: string | null;
  seeded_from_crew_id: string | null;
  weekends_off: boolean | null;                       // mig 400 — null = follow the booking
  booking:  { assigned_from: string; assigned_to: string | null; crew_id: string;
              weekends_off: boolean | null } | null;
  projects: { project_name: string; status: string;
              actual_end_date: string | null; scheduled_end_date: string | null;
              sites: { site_name: string | null } | null } | null;
}

interface PersonBarsCtx {
  gridStartKey: string;
  gridEndKey:   string;
  projById:     Map<string, ProjectRow>;
  projectTeam:  ProjectTeamRow[];
  personalBookings: PersonalBookingRow[];
  leaveRows:    LeaveRow[];
  defects:      DefectRow[];
  roster:       RosterRow[];
  crewColour:   Map<string, string>;
}

const spansOverlap = (aF: string, aT: string, bF: string, bT: string) =>
  aF <= bT && bF <= aT;

// Day key in LOCAL time. Never slice a timestamptz string: a task at
// Mon 14 Sep 07:00 AEST is stored as "…09-13T21:00:00+00:00", so slice(0,10)
// buckets it onto SUNDAY (James's 3 Sep bug report). Same reason
// new Date().toISOString().slice(0,10) says "yesterday" before 10am here.
const localDayKey = (ts: string | Date): string => {
  const d = ts instanceof Date ? ts : new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// A date input that only commits when you LEAVE the field (or press Enter).
// James, 3 Sep: "when I put numbers in instead of clicking the date it messes
// it all up for other lines" — a native date input fires onChange on every
// keystroke, so typing "2026" digit by digit briefly commits year 0002, and
// everything clamped against that date scrambles. Typing is now free; only a
// finished, sane date (this decade-ish) is ever committed.
function CommitDateInput({ value, onCommit, min, disabled, className, placeholder }: {
  value: string;
  onCommit: (v: string) => void;
  min?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState(value);
  const last = useRef(value);
  if (last.current !== value) { last.current = value; if (draft !== value) setDraft(value); }
  const commit = () => {
    if (draft === value) return;
    if (draft === "") { onCommit(""); return; }
    const y = Number(draft.slice(0, 4));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft) || y < 2000 || y > 2100) { setDraft(value); return; }
    onCommit(draft);
  };
  return (
    <input type="date" value={draft} min={min} disabled={disabled} placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { commit(); (e.target as HTMLInputElement).blur(); } }}
      className={className} />
  );
}

function buildPersonBars(
  ctx: PersonBarsCtx,
  employeeId: string,
  name: string,
): PersonBar[] {
  const { gridStartKey, gridEndKey, projById, projectTeam, personalBookings, leaveRows,
          defects, roster, crewColour } = ctx;
  const out: PersonBar[] = [];

  // 333 - site work comes off the ROSTER, one bar per stint. Not derived from
  // crew membership any more: moving Ken between crews can no longer erase
  // where he was, ring-ins from other crews show on the site they are actually
  // rostered on, and a "listed" standby (Clint on Sharon) reads as pencilled
  // in. Colour follows the crew whose booking hosts the stint.
  for (const sr of roster) {
    if (sr.employee_id !== employeeId) continue;
    const f = sr.on_site_from;
    const projStatus = sr.projects?.status ?? "";
    const projDead   = ["closed", "cancelled", "lost"].includes(projStatus);
    const projEnd    = sr.projects?.actual_end_date ?? sr.projects?.scheduled_end_date ?? null;
    const t = sr.on_site_to
      ?? sr.booking?.assigned_to
      ?? (projDead ? (projEnd ?? f) : gridEndKey);
    if (!spansOverlap(f, t, gridStartKey, gridEndKey)) continue;
    const site = sr.projects?.sites?.site_name ?? sr.projects?.project_name ?? "Site";
    const hostCrew = sr.booking?.crew_id ?? sr.seeded_from_crew_id;
    const listed = sr.status === "listed";
    // Mig 400 — the person's own flag wins, else the booking's, else through.
    const weekendsOff = sr.weekends_off ?? sr.booking?.weekends_off ?? false;
    out.push({
      weekendsOff,
      key:    `sr:${sr.roster_id}`,
      from:   f < gridStartKey ? gridStartKey : f,
      to:     t > gridEndKey   ? gridEndKey   : t,
      kind:   listed ? "listed" : "crew",
      label:  listed ? `${site} · listed` : site,
      colour: listed ? null : ((hostCrew && crewColour.get(hostCrew)) || SOLO_BLUE),
      title:  `${name} — ${site}, ${listed
                 ? "LISTED (standby: on the list and has project access, not counted on site)"
                 : "on site"}${sr.role_on_site ? ` · ${sr.role_on_site}` : ""}`
              + ` · ${fmtDate(f)} → ${(sr.on_site_to ?? sr.booking?.assigned_to) ? fmtDate(t) : "open"}`
              + (weekendsOff ? " · Mon–Fri (weekends off)" : "")
              + `\nDrag to move their days · drag an edge to change start/finish · click for the visit`
              + `\nCtrl-drag to COPY their days (split a stint) · Shift-click to REMOVE these days`,
      stint: {
        roster_id:    sr.roster_id,
        on_site_from: sr.on_site_from,
        on_site_to:   sr.on_site_to,
        pca_id:       sr.pca_id,
        project_id:   sr.project_id,
        employee_id:  sr.employee_id,
        status:       sr.status,
      },
      editorTarget: sr.pca_id && sr.booking ? {
        pca_id:    sr.pca_id,
        crew_id:   sr.booking.crew_id,
        from:      sr.booking.assigned_from,
        to:        sr.booking.assigned_to,
        projectId: sr.project_id,
        label:     site,
        weekendsOff: sr.booking.weekends_off ?? false,
      } : undefined,
    });
  }

  // On a job on their own - labour hire, a tech sent to cover, somebody put on
  // a project without standing a crew up.
  for (const pt of projectTeam) {
    if (pt.employee_id !== employeeId) continue;
    const f = pt.assigned_from;

    // James, 22 Aug 2026, looking at Douglas Johnson painted across the whole of
    // September: "any project status closed should not be showing as ongoing."
    // Right. His row on Nyngan was open-ended - and Nyngan is CLOSED. An
    // assignment with no end date means "until the job ends", so when the job
    // has ended, that IS the end date. The dead project cannot cap it via
    // projById because the schedule only fetches active projects - which is why
    // the row now carries its own project.
    const projStatus = pt.projects?.status ?? "";
    const projDead   = ["closed", "cancelled", "lost"].includes(projStatus);
    const projEnd    = pt.projects?.actual_end_date ?? pt.projects?.scheduled_end_date ?? null;
    const t = pt.assigned_to
      ?? (projDead ? (projEnd ?? f)   // dead with no recorded end: one day, not forever
                   : gridEndKey);

    if (!spansOverlap(f, t, gridStartKey, gridEndKey)) continue;
    const proj = projById.get(pt.project_id);
    const where = pt.projects
      ? (pt.projects.sites?.site_name ?? pt.projects.project_name)
      : proj ? (proj.sites?.site_name ?? proj.project_name) : "Project";
    out.push({
      key:    `pt:${pt.assignment_id}`,
      from:   f < gridStartKey ? gridStartKey : f,
      to:     t > gridEndKey   ? gridEndKey   : t,
      kind:   "solo",
      label:  where,
      colour: SOLO_BLUE,
      title:  `${name} — ${where}${pt.role_on_project ? ` · ${pt.role_on_project}` : ""} (on this job individually, not with a crew)${projDead ? " · project closed" : ""}`,
    });
  }

  for (const pb of personalBookings) {
    if (pb.assignee_employee_id !== employeeId) continue;
    const f = localDayKey(pb.scheduled_start);
    const t = localDayKey(pb.scheduled_end ?? pb.scheduled_start);
    if (!spansOverlap(f, t, gridStartKey, gridEndKey)) continue;
    const isDepot = pb.booking_kind === "depot";   // mig 356
    out.push({
      key:    `pb:${pb.booking_id}`,
      from:   f < gridStartKey ? gridStartKey : f,
      to:     t > gridEndKey   ? gridEndKey   : t,
      kind:   isDepot ? "depot" : "task",
      label:  isDepot ? (pb.title || "Depot") : (pb.location_address?.trim() || pb.title),
      colour: isDepot ? DEPOT_SLATE : null,
      title:  `${name} — ${pb.title}${pb.location_address ? ` · ${pb.location_address}` : ""} (${isDepot ? "depot day" : "ad-hoc task"})`
              + ` · ${fmtDate(f)}${t !== f ? ` → ${fmtDate(t)}` : ""}`
              + `\nDrag to move it · drag an edge to make it longer or shorter · click to open`,
      pbook:  pb,   // mig 400 — draggable
    });
  }

  for (const lv of leaveRows) {
    if (lv.employee_id !== employeeId) continue;
    if (!spansOverlap(lv.starts_on, lv.ends_on, gridStartKey, gridEndKey)) continue;
    out.push({
      key:    `lv:${lv.request_id}`,
      from:   lv.starts_on < gridStartKey ? gridStartKey : lv.starts_on,
      to:     lv.ends_on   > gridEndKey   ? gridEndKey   : lv.ends_on,
      kind:   "leave",
      label:  "Leave",
      colour: null,
      title:  `${name} — on leave ${fmtDate(lv.starts_on)} to ${fmtDate(lv.ends_on)}`,
    });
  }

  // A defect's ETA is the planned return visit - one day on the assignee's row.
  for (const d of defects) {
    if (d.assigned_to_employee_id !== employeeId) continue;
    out.push({
      key:    `df:${d.defect_id}`,
      from:   d.due_date,
      to:     d.due_date,
      kind:   "defect",
      label:  `DEF · ${d.sites?.site_name ?? d.projects?.project_name ?? d.title}`,
      colour: null,
      title:  `${name} — ${d.defect_number} ${d.title} · ${d.sites?.site_name ?? d.projects?.project_name ?? ""} (${d.severity}, ${d.status.replace(/_/g, " ")}) — defect visit`,
    });
  }

  return out;
}

// Somebody on a job on their own - a project_team row rather than a crew. James:
// "we move them / drag them to a different crew or stand alone, they have a
// default colour." This is that default. Deliberately NOT any crew's colour, and
// deliberately not green or red, both of which already mean something here.
const SOLO_BLUE = "#2563b8";
const DEPOT_SLATE = "#64748b";   // mig 356 — depot-day block

// Mig 401 — a PLANNING BLOCK on a crew row: a coloured text note with dates,
// dragged and stretched for future scheduling. Not a booking (no project, no
// roster, never seen by the field). James, 29 Sep: "a coloured text block
// ('Te…', no project) that can be dragged and stretched".
interface PlanBlockRow {
  block_id:   string;
  crew_id:    string;
  title:      string;
  colour_hex: string;
  plan_from:  string;
  plan_to:    string;
  note:       string | null;
}
// Deliberately none of the crew colours' neighbours: a pencil line must never
// read as a booking. Violet first because it is the one colour nothing else on
// the board uses.
const PLAN_COLOURS = ["#7c3aed", "#0d9488", "#b45309", "#be185d", "#4d7c0f", "#475569", "#0369a1", "#9f1239"];
// An outage or key date promoted onto the company schedule from a project's own
// calendar. Purple because nothing else on this screen is, so a promoted date
// never reads as a crew.
const PROMOTED_PURPLE = "#7c3aed";

// Booking status → Pill tone (for the in-panel deliverables list).
const BK_STATUS_TONE: Record<string, "open" | "pending" | "warning" | "default"> = {
  tentative: "default", confirmed: "open", in_progress: "pending", delayed: "warning",
};

// Layout constants — match CalendarMonth.tsx so the screens read consistently.
const DAY_NUMBER_HEIGHT_PX = 24;
const BAR_HEIGHT_PX        = 18;
const BAR_GAP_PX           = 3;
const LANE_HEIGHT_PX       = BAR_HEIGHT_PX + BAR_GAP_PX;
const LANES_PADDING_PX     = 4;
const MAX_LANES            = 3;

interface ProjectRow {
  project_id:           string;
  project_code:         string;
  project_name:         string;
  status:               string;
  scheduled_start_date: string | null;
  scheduled_end_date:   string | null;
  actual_start_date:    string | null;
  actual_end_date:      string | null;
  sites:    { site_code: string | null; site_name: string | null; suburb: string | null; address_line1: string | null } | null;
  clients:  { legal_name: string | null } | null;
}

// A2 — when a project has actual dates, they ARE the truth and the plan is
// history. Drawing both meant a job that started a week late showed up twice:
// a grey planned bar where it was supposed to be, and crew bars where it
// actually was, with nothing telling you which was real.
// James: "I don't want to see anything scheduled in here if it has actual dates."
//
// One helper, used by every view in this file, so the grid, the row bars and
// the filtering can't drift apart and disagree about when a job happened.
//
// The judgement call, worth knowing about: a project that has STARTED but not
// finished has no actual end date. We run it to its scheduled end if that is
// still ahead of the actual start, otherwise to the edge of the grid — so a job
// running over its forecast keeps rendering instead of collapsing to a one-day
// sliver on the day it started. Settling that properly is A1 (the dates model),
// which is still open.
function effectiveWindow(
  p: { scheduled_start_date: string | null; scheduled_end_date: string | null;
       actual_start_date: string | null; actual_end_date: string | null },
  gridEndKey: string,
): { start: string | null; end: string | null; isActual: boolean } {
  if (!p.actual_start_date && !p.actual_end_date) {
    return { start: p.scheduled_start_date, end: p.scheduled_end_date, isActual: false };
  }
  const start = p.actual_start_date ?? p.scheduled_start_date;
  const end =
    p.actual_end_date ??
    ((p.scheduled_end_date && start && p.scheduled_end_date >= start)
      ? p.scheduled_end_date
      : gridEndKey);
  return { start, end, isActual: true };
}

// -----------------------------------------------------------------------------
// The people layer. Three shapes, none of them new tables.
// -----------------------------------------------------------------------------
interface CrewMemberRow {
  crew_member_id: string;
  crew_id:        string;
  employee_id:    string;
  member_from:    string;          // dated membership - this is the whole trick
  member_to:      string | null;   // null = still on the crew
  is_lead:        boolean;
  crew_role:      string;
  employees: {
    employee_id: string; first_name: string; last_name: string;
    preferred_name: string | null; is_active: boolean; is_schedulable: boolean;
  } | null;
}

interface PersonalBookingRow {
  booking_id:           string;
  assignee_employee_id: string;
  title:                string;
  scheduled_start:      string;    // timestamptz
  scheduled_end:        string | null;
  location_address:     string | null;
  project_id:           string | null;
  site_id:              string | null;
  status:               string;
  notes:                string | null;
  booking_kind?:        string | null;   // mig 356 — 'task' | 'depot'
}

interface LeaveRow {
  request_id:      string;
  employee_id:     string;
  leave_type_code: string;
  starts_on:       string;
  ends_on:         string;
  status:          string;
}

interface PromotedBookingRow {
  booking_id:         string;
  project_id:         string;
  booking_type:       string;
  title:              string;
  description:        string | null;
  status:             string;
  scheduled_start:    string;
  scheduled_end:      string | null;
  location_notes:     string | null;
  crq_number:         string | null;
  customer_impacting: boolean | null;
  outage_approved:    boolean;
  external_contact_name:  string | null;
  external_contact_phone: string | null;
  projects: {
    project_code: string;
    project_name: string;
    sites: { site_name: string } | null;
  } | null;
}

interface DefectRow {
  defect_id:               string;
  defect_number:           string;
  title:                   string;
  severity:                string;
  status:                  string;
  due_date:                string;
  assigned_to_employee_id: string | null;
  project_id:              string;
  sites:    { site_name: string } | null;
  projects: { project_name: string } | null;
}

interface SchedulablePerson {
  employee_id:    string;
  first_name:     string;
  last_name:      string;
  preferred_name: string | null;
}

interface ProjectTeamRow {
  assignment_id:   string;
  project_id:      string;
  employee_id:     string;
  role_on_project: string | null;
  assigned_from:   string;
  assigned_to:     string | null;
  // The project rides along so the schedule can answer two things the row alone
  // cannot: what to CALL the job (the schedule's own project list only fetches
  // ACTIVE statuses, so a closed project is not in it), and when the job ENDED -
  // because an open-ended assignment on a closed project must stop at the
  // project's end, not run forever.
  projects: {
    project_name:       string;
    status:             string;
    actual_end_date:    string | null;
    scheduled_end_date: string | null;
    sites: { site_name: string } | null;
  } | null;
}

// What one person's row on the schedule is made of. The kinds are separated
// because they are different facts and must not look the same: "on site with the
// crew", "on that job on their own", "pulled out for something else", "away".
type PersonBarKind = "crew" | "solo" | "task" | "depot" | "leave" | "defect" | "listed";

interface PersonBar {
  key:    string;
  from:   string;
  to:     string;
  kind:   PersonBarKind;
  label:  string;
  colour: string | null;   // null = kind supplies its own treatment
  title:  string;
  // 335b — roster bars are DIRECTLY DRAGGABLE on the grid (James: "why not
  // drag and change the person's build dates"). The raw stint travels with the
  // bar so the drag writes real dates, not grid-clipped ones; the editor
  // target makes a plain click open the visit this stint belongs to.
  stint?: {
    roster_id:    string;
    on_site_from: string;
    on_site_to:   string | null;   // null = follows the booking
    pca_id:       string | null;
    project_id:   string;
    employee_id:  string;
    status:       "rostered" | "listed";
  };
  editorTarget?: { pca_id: string; crew_id: string; from: string; to: string | null;
                   projectId: string; label: string; weekendsOff?: boolean };
  // Mig 400 — a Mon–Fri stint: Saturday/Sunday inside the bar are drawn as
  // notches and not counted as site days.
  weekendsOff?: boolean;
  // Mig 400 — a depot day / ad-hoc task bar is draggable too (James, 29 Sep:
  // "a depot day can only be placed one day at a time"). The raw booking
  // travels with the bar so the drop shifts real timestamps, keeping the
  // times of day.
  pbook?: PersonalBookingRow;
}

interface CrewAssignmentRow {
  pca_id:        string;
  project_id:    string;
  assigned_from: string;
  assigned_to:   string | null;
  notes:         string | null;
  weekends_off?: boolean | null;   // mig 400 — a Mon–Fri job
  // Carried on the booking so a crew's history still reads properly when the
  // job it was on has since closed. The screen only loads ACTIVE projects, so
  // without this a booking on a finished job rendered as "Unknown job".
  projects: {
    project_code: string;
    project_name: string;
    sites: { site_name: string | null } | null;
  } | null;
  crews: {
    crew_id:    string;
    crew_code:  string;
    crew_name:  string;
    colour_hex: string;
  } | null;
}

interface WeekBar {
  project:    ProjectRow;
  colour:     string;
  crewCode:   string | null;   // the crew this segment is for (null = unassigned)
  crewCount:  number;          // total crews on the project (tooltip context)
  isUnassigned: boolean;
  segKey:     string;          // pca_id, or `${project_id}-none` for the grey unassigned bar
  segFrom:    string;          // this segment's own start (crew window / project window)
  segTo:      string;          // this segment's own end
  dayIndex:   number;
  span:       number;
  startsHere: boolean;
  endsHere:   boolean;
  lane:       number;
}

// =============================================================================

type ViewMode = "calendar" | "gantt" | "crew" | "people";

export default function CrewCalendarScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [viewMode,  setViewMode]  = useState<ViewMode>("calendar");
  const [viewMonth, setViewMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [search,    setSearch]    = useState("");
  const [crewFilter, setCrewFilter] = useState<Set<string>>(new Set());
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [showTentativeBids, setShowTentativeBids] = useState(true); // T-347
  const [openProject, setOpenProject] = useState<ProjectRow | null>(null);
  const [assignTaskOpen, setAssignTaskOpen] = useState(false); // T-249c

  // Month grid window — Mon-start, 6 weeks always.
  const weeks = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    const end   = endOfWeek(endOfMonth(viewMonth),   { weekStartsOn: 1 });
    const out: Date[] = [];
    let d = start;
    while (d <= end) { out.push(d); d = addDays(d, 1); }
    while (out.length < 42) out.push(addDays(out[out.length - 1], 1));
    const trimmed = out.slice(0, 42);
    const grouped: Date[][] = [];
    for (let i = 0; i < 6; i++) grouped.push(trimmed.slice(i * 7, (i + 1) * 7));
    return grouped;
  }, [viewMonth]);

  const gridStart = weeks[0][0];
  const gridEnd   = weeks[5][6];
  // Clicking a person's name opens Assign task already pointed at them. The
  // modal has always accepted a pre-filled assignee; nothing on the schedule
  // ever passed one.
  const [assignFor, setAssignFor] = useState<{ id: string; name: string } | null>(null);
  // The day the user clicked on the calendar, carried into the task modal.
  const [assignDate, setAssignDate] = useState<string | null>(null);
  const [assignEnd, setAssignEnd]   = useState<string | null>(null);   // mig 400 — sweep's last day
  const gridStartKey = format(gridStart, "yyyy-MM-dd");
  const gridEndKey   = format(gridEnd,   "yyyy-MM-dd");

  // Active projects.
  const { data: projects, isLoading: loadingProjects } = useQuery({
    queryKey: ["schedule_projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select(`
          project_id, project_code, project_name, status,
          scheduled_start_date, scheduled_end_date,
          actual_start_date, actual_end_date,
          sites:site_id     (site_code, site_name, suburb, address_line1),
          clients:client_id (legal_name)
        `)
        // "Active" set — includes scheduled (PO awarded, team not yet
        // mobilised — common for Coretel sites where mob is weeks away).
        // Excludes pre-work pursuits (bid, quoted) and dead projects
        // (closed, cancelled, lost).
        .in("status", ["scheduled", "won", "mobilising", "live", "on_hold", "acceptance_pending"])
        .order("scheduled_start_date", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as ProjectRow[];
    },
  });

  // Crew assignments overlapping the visible window.
  // T-414: fetch ALL crew assignments, not just the visible month's. The old
  // window filter meant a project whose stints fell outside the grid (Walcha
  // in July: June visit done, August return booked) looked crewless and drew
  // a full-height "unassigned" slab across its whole stale plan window.
  // Volume is tiny for our size; correctness wins.
  const { data: assignments, isLoading: loadingPcas } = useQuery({
    queryKey: ["schedule_pcas"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_crew_assignments")
        .select(`
          pca_id, project_id, assigned_from, assigned_to, notes, weekends_off,
          projects:project_id (project_code, project_name, sites:site_id (site_name)),
          crews:crew_id (crew_id, crew_code, crew_name, colour_hex)
        `);
      if (error) throw error;
      return (data ?? []) as unknown as CrewAssignmentRow[];
    },
  });

  // EVERY active crew, not just the ones that happen to be booked. The crew
  // view needs a row for a crew with nothing on it — "who is free next week" is
  // half the reason that view exists, and a crew with no bookings is exactly
  // the answer. The chip palette below still derives from assignments, because
  // a legend for a colour nobody can see on screen is noise.
  const { data: allCrews } = useQuery({
    queryKey: ["schedule_all_crews"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crews")
        .select("crew_id, crew_code, crew_name, colour_hex, crew_kind, active, lead_employee_id")
        .eq("active", true)
        .order("crew_name");
      if (error) throw error;
      return (data ?? []) as { crew_id: string; crew_code: string; crew_name: string;
                               colour_hex: string; crew_kind: string; active: boolean;
                               lead_employee_id: string | null }[];
    },
  });

  // ---------------------------------------------------------------------------
  // THE PEOPLE LAYER — 21 Aug 2026
  // ---------------------------------------------------------------------------
  // James: "I want to expand on crews to have an employee list... when they are
  // in crews they colour code, move crew move colour, history reflects the crew
  // they were in, and the site can be audited not just on crews but on personnel
  // on site... when a crew member is out of said crew on an ad-hoc task they
  // show separately with their ad-hoc site / defect site name."
  //
  // No new tables were needed for any of this. crew_members has carried
  // member_from / member_to since the schema was written and nothing has ever
  // read it, so "who was in which crew in March" was already answerable:
  //
  //   on site      = their crew_members row covers the day
  //                  AND their crew has a booking covering it
  //   their colour = their crew's colour ON THAT DAY, so history keeps the crew
  //                  they were actually in rather than the one they are in now
  //   pulled out   = a personal_booking or approved leave over those days
  //
  // Three tables that already modelled it, and the schedule read one.
  // ---------------------------------------------------------------------------
  const { data: crewMembers = [] } = useQuery({
    queryKey: ["schedule_crew_members"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crew_members")
        .select("crew_member_id, crew_id, employee_id, member_from, member_to, is_lead, crew_role, employees(employee_id, first_name, last_name, preferred_name, is_active, is_schedulable)")
        .order("member_from");
      if (error) throw error;
      return (data ?? []) as unknown as CrewMemberRow[];
    },
  });

  // ---------------------------------------------------------------------------
  // THE ROSTER — 27 Aug 2026 (migration 333)
  // ---------------------------------------------------------------------------
  // site_roster is now the RECORD of who is on which site and when. Person bars
  // come off this, not off crew membership ∩ bookings — that inference is what
  // erased Ken's Walcha history the moment his membership was edited. Bookings
  // still seed it (crew booked → members rostered) and bookings dragging around
  // still carry the whole visit; but each person's stint is owned per person,
  // survives crew moves, and can differ from the crew (ring-ins, early leavers,
  // "listed" standbys who may not attend).
  const { data: rosterRows = [] } = useQuery({
    queryKey: ["schedule_roster", gridStartKey, gridEndKey],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("site_roster")
        .select(`
          roster_id, employee_id, pca_id, project_id, on_site_from, on_site_to,
          status, role_on_site, seeded_from_crew_id, weekends_off,
          booking:pca_id (assigned_from, assigned_to, crew_id, weekends_off),
          projects:project_id (project_name, status, actual_end_date, scheduled_end_date,
                               sites:site_id (site_name))
        `)
        .lte("on_site_from", gridEndKey)
        .or(`on_site_to.is.null,on_site_to.gte.${gridStartKey}`);
      if (error) throw error;
      return (data ?? []) as unknown as RosterRow[];
    },
  });

  // Ad-hoc tasks. Written by "Assign task" since T-249 and, until today, read by
  // absolutely nothing - the only trace a task left was a bell notification.
  // James, looking at the modal: "will it show up in the calendar... is it in
  // the schedule?" It was not. It is now.
  const { data: personalBookings = [] } = useQuery({
    queryKey: ["schedule_personal_bookings", gridStartKey, gridEndKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("personal_bookings")
        .select("booking_id, assignee_employee_id, title, scheduled_start, scheduled_end, location_address, project_id, site_id, status, notes, booking_kind")
        .neq("status", "cancelled")
        .lte("scheduled_start", `${gridEndKey}T23:59:59`)
        .order("scheduled_start");
      if (error) throw error;
      return (data ?? []) as unknown as PersonalBookingRow[];
    },
  });

  // Approved leave. A schedule that will happily book somebody who is on annual
  // leave is a schedule that gets somebody rung on their day off.
  const { data: leaveRows = [] } = useQuery({
    queryKey: ["schedule_leave", gridStartKey, gridEndKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leave_requests")
        .select("request_id, employee_id, leave_type_code, starts_on, ends_on, status")
        .eq("status", "approved")
        .lte("starts_on", gridEndKey)
        .gte("ends_on", gridStartKey);
      if (error) throw error;
      return (data ?? []) as unknown as LeaveRow[];
    },
  });

  // ---------------------------------------------------------------------------
  // Key dates promoted off a project's own calendar onto this one.
  //
  // James, 21 Aug 2026: "I would like to be able to flag things in a project's
  // calendar / bookings / outages - a option to tick in the project to show on
  // this calendar, then it's in the build / site bar but maybe a different
  // colour to highlight it, and if you click it you see the details."
  //
  // Same opt-in shape as bids.show_on_schedule, and the same reason: a company
  // schedule that shows every plant delivery on every job is a company schedule
  // nobody reads. Their own lane rather than inside a crew row, because these
  // are not a crew's work - they are dates the whole company has to know.
  // ---------------------------------------------------------------------------
  const { data: promotedBookings = [] } = useQuery({
    queryKey: ["schedule_promoted_bookings", gridStartKey, gridEndKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_bookings")
        .select(`
          booking_id, project_id, booking_type, title, description, status,
          scheduled_start, scheduled_end, location_notes,
          crq_number, customer_impacting, outage_approved,
          external_contact_name, external_contact_phone,
          projects:project_id ( project_code, project_name, sites:site_id ( site_name ) )
        `)
        .eq("show_on_schedule", true)
        .neq("status", "cancelled")
        .lte("scheduled_start", `${gridEndKey}T23:59:59`)
        .order("scheduled_start");
      if (error) throw error;
      return (data ?? []) as unknown as PromotedBookingRow[];
    },
  });

  // Open defects with an ETA. James, on the Coona defect: "one defect, should be
  // on the employee assigned and also the schedule view" and, on the calendar,
  // "where is the defect we have planned at Coona?" The register has carried
  // due_date + assigned_to since T-393; the schedule just never read it. The ETA
  // is the planned visit day, so that is the day it appears.
  const { data: defectRows = [] } = useQuery({
    queryKey: ["schedule_defects", gridStartKey, gridEndKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_defects")
        .select(`defect_id, defect_number, title, severity, status, due_date,
                 assigned_to_employee_id, project_id,
                 sites:site_id ( site_name ), projects:project_id ( project_name )`)
        .in("status", ["open", "in_progress", "awaiting_reinspection"])
        .not("due_date", "is", null)
        .gte("due_date", gridStartKey)
        .lte("due_date", gridEndKey);
      if (error) throw error;
      return (data ?? []) as unknown as DefectRow[];
    },
  });

  // Everybody who can be put on a schedule at all. Needed as its own list rather
  // than derived from crew_members, because the people who most need finding on
  // this screen are the ones who are NOT in a crew this month.
  const { data: schedulablePeople = [] } = useQuery({
    queryKey: ["schedule_people"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, preferred_name")
        .eq("is_active", true)
        .eq("is_schedulable", true)
        .order("first_name");
      if (error) throw error;
      return (data ?? []) as unknown as SchedulablePerson[];
    },
  });

  // Individuals put on a project WITHOUT a crew. project_team has existed since
  // T-131 and is written by "Assign person" on the project page - labour hire, a
  // one-off tech, somebody covering. It is dated the same way crew membership is,
  // and, like the other two, nothing on the schedule has ever read it.
  //
  // James: "the site can be audited not just on crews but on personnel on site."
  // This is the half of "personnel on site" that is not a crew.
  const { data: projectTeam = [] } = useQuery({
    queryKey: ["schedule_project_team", gridStartKey, gridEndKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_team")
        .select(`assignment_id, project_id, employee_id, role_on_project, assigned_from, assigned_to,
                 projects:project_id ( project_name, status, actual_end_date, scheduled_end_date,
                                       sites:site_id ( site_name ) )`)
        .lte("assigned_from", gridEndKey)
        .or(`assigned_to.is.null,assigned_to.gte.${gridStartKey}`)
        .order("assigned_from");
      if (error) throw error;
      return (data ?? []) as unknown as ProjectTeamRow[];
    },
  });

  // Crew leads, by name. Fetched separately rather than embedded on crews so we
  // are not depending on a foreign-key constraint name.
  const { data: employeeNames } = useQuery({
    queryKey: ["schedule_employee_names"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_employee_names")
        .select("employee_id, full_name");
      if (error) throw error;
      return (data ?? []) as { employee_id: string; full_name: string | null }[];
    },
  });
  // ---------------------------------------------------------------------------
  // Day-indexed lookups for the calendar cells. Built once per month rather than
  // filtering the whole list inside all 42 cells, which is what the first cut
  // did - 42 x N is fine at Coretel's size and wasteful for no reason.
  // ---------------------------------------------------------------------------
  const [openTask, setOpenTask] = useState<PersonalBookingRow | null>(null);
  const [openKeyDate, setOpenKeyDate] = useState<PromotedBookingRow | null>(null);

  const tasksByDay = useMemo(() => {
    const m = new Map<string, PersonalBookingRow[]>();
    for (const pb of personalBookings) {
      const f = localDayKey(pb.scheduled_start);
      const t0 = localDayKey(pb.scheduled_end ?? pb.scheduled_start);
      const t = t0 < f ? f : t0;
      for (let d = parseISO(f); format(d, "yyyy-MM-dd") <= t; d = addDays(d, 1)) {
        const k = format(d, "yyyy-MM-dd");
        (m.get(k) ?? m.set(k, []).get(k)!).push(pb);
      }
    }
    return m;
  }, [personalBookings]);

  const defectsByDay = useMemo(() => {
    const m = new Map<string, DefectRow[]>();
    for (const d of defectRows) {
      (m.get(d.due_date) ?? m.set(d.due_date, []).get(d.due_date)!).push(d);
    }
    return m;
  }, [defectRows]);

  // James: "what happens when 4 sites all have outages? We don't want [a lane]
  // but we do need to see when those outages are in the schedule view - maybe by
  // making the cell of that day a colour, only when outage."
  //
  // So a key date is a property OF THE DAY, not a row of its own. Four outages
  // on one day tint one cell and stack four markers inside it; they cost no
  // vertical space at all, which is the whole reason the lane had to go.
  const keyDatesByDay = useMemo(() => {
    const m = new Map<string, PromotedBookingRow[]>();
    for (const b of promotedBookings) {
      const f = localDayKey(b.scheduled_start);
      const t0 = localDayKey(b.scheduled_end ?? b.scheduled_start);
      const t = t0 < f ? f : t0;
      for (let d = parseISO(f); format(d, "yyyy-MM-dd") <= t; d = addDays(d, 1)) {
        const k = format(d, "yyyy-MM-dd");
        (m.get(k) ?? m.set(k, []).get(k)!).push(b);
      }
    }
    return m;
  }, [promotedBookings]);

  const leadNameById = useMemo(
    () => new Map((employeeNames ?? []).map(e => [e.employee_id, e.full_name ?? ""])),
    [employeeNames],
  );
  const nameFor = (id: string) => leadNameById.get(id) || "Unassigned";
  // Initials, so a chip says WHO without spending the width a name costs.
  const initialsFor = (id: string) => {
    const n = leadNameById.get(id);
    if (!n) return "??";
    const parts = n.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase() || "??";
  };

  // Crew chip palette — distinct crews seen in the visible window (the fetch
  // is all-time now, so filter to grid overlap here to keep the legend honest).
  const crewsInWindow = useMemo(() => {
    const m = new Map<string, { id: string; code: string; colour: string }>();
    for (const a of assignments ?? []) {
      if (!a.crews) continue;
      if (a.assigned_from > gridEndKey || (a.assigned_to ?? "9999-12-31") < gridStartKey) continue;
      m.set(a.crews.crew_id, { id: a.crews.crew_id, code: a.crews.crew_code, colour: a.crews.colour_hex });
    }
    return Array.from(m.values()).sort((a, z) => a.code.localeCompare(z.code));
  }, [assignments, gridStartKey, gridEndKey]);

  const projById = useMemo(
    () => new Map((projects ?? []).map(p => [p.project_id, p])),
    [projects]
  );

  // T-414: double-booked crews — the same crew on two DIFFERENT projects with
  // overlapping windows. Warn-not-block: surfaced, never prevented (sometimes
  // a crew genuinely splits across nearby sites). Only counts ACTIVE projects
  // (stale windows on closed jobs aren't real clashes), skips overlaps that
  // ended before today, and each row is dismissible (kept in this browser)
  // because some clashes are known and accepted — nobody wants a nag board.
  const [dismissedConflicts, setDismissedConflicts] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("schedule_dismissed_conflicts") ?? "[]"); }
    catch { return []; }
  });
  function dismissConflict(key: string) {
    setDismissedConflicts(prev => {
      const next = [...prev, key];
      try { localStorage.setItem("schedule_dismissed_conflicts", JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }
  function restoreConflicts() {
    setDismissedConflicts([]);
    try { localStorage.removeItem("schedule_dismissed_conflicts"); } catch { /* private mode */ }
  }
  const crewConflicts = useMemo(() => {
    type Conflict = { key: string; crewCode: string; colour: string; projA: string; projB: string; from: string; to: string };
    const todayKey = localDayKey(new Date());
    const byCrew = new Map<string, CrewAssignmentRow[]>();
    for (const a of assignments ?? []) {
      if (!a.crews) continue;
      if (!projById.has(a.project_id)) continue;     // active projects only
      const arr = byCrew.get(a.crews.crew_id) ?? [];
      arr.push(a);
      byCrew.set(a.crews.crew_id, arr);
    }
    const out: Conflict[] = [];
    for (const arr of byCrew.values()) {
      for (let i = 0; i < arr.length; i++) {
        for (let j = i + 1; j < arr.length; j++) {
          const a = arr[i], b = arr[j];
          if (a.project_id === b.project_id) continue;
          // An open-ended assignment used to be treated as running until
          // 9999-12-31 here, while the SAME assignment is drawn as a bar that
          // ends at the project's end. Picture and warning disagreed, so any
          // crew left open-ended clashed with every future booking they ever
          // got, forever — a warning that cries wolf until people stop reading
          // it. Both now end at the project's effective end.
          const endOf = (pid: string) => {
            const p = projById.get(pid);
            if (!p) return "9999-12-31";
            return effectiveWindow(p, gridEndKey).end ?? "9999-12-31";
          };
          const aTo  = a.assigned_to ?? endOf(a.project_id);
          const bTo  = b.assigned_to ?? endOf(b.project_id);
          const from = a.assigned_from > b.assigned_from ? a.assigned_from : b.assigned_from;
          const to   = aTo < bTo ? aTo : bTo;
          if (from > to) continue;                   // no overlap
          if (to < todayKey) continue;               // history — not actionable
          const key = [a.crews!.crew_code, a.pca_id, b.pca_id].join("|");
          out.push({ key, crewCode: a.crews!.crew_code, colour: a.crews!.colour_hex, projA: a.project_id, projB: b.project_id, from, to });
        }
      }
    }
    return out.filter(c => !dismissedConflicts.includes(c.key));
  }, [assignments, projById, dismissedConflicts, gridEndKey]);
  const anyDismissed = dismissedConflicts.length > 0;
  const [conflictsExpanded, setConflictsExpanded] = useState(false);

  // Active projects that are missing scheduled dates AND have no crew window —
  // truly invisible, so we surface them in a callout. (T-414: a project with a
  // crew assignment now renders off its crew bars even without scheduled dates.)
  const unscheduledActive = useMemo(() => {
    // A2 — a project with an actual start renders off its actual window, so it
    // is no longer invisible and does not belong in the "nobody can see this"
    // callout even if the plan dates were never filled in.
    return (projects ?? []).filter(p =>
      (!p.scheduled_start_date || !p.scheduled_end_date) &&
      !p.actual_start_date &&
      !(assignments ?? []).some(a => a.project_id === p.project_id && a.crews)
    );
  }, [projects, assignments]);

  // Filter project list once, used in both the grid and side panel.
  const visibleProjects = useMemo(() => {
    if (!projects) return [];
    const q = search.trim().toLowerCase();
    return projects.filter(p => {
      // T-414: crew windows are first-class — a project renders if EITHER its
      // scheduled window OR any crew assignment overlaps the grid. Projects
      // without scheduled dates still show off their crew bars (warn-not-block).
      const crewInWindow = (assignments ?? []).some(a =>
        a.project_id === p.project_id && a.crews &&
        a.assigned_from <= gridEndKey &&
        (a.assigned_to ?? "9999-12-31") >= gridStartKey
      );
      const win = effectiveWindow(p, gridEndKey);
      const datesInWindow = win.start != null && win.end != null
        && win.end >= gridStartKey
        && win.start <= gridEndKey;
      if (!crewInWindow && !datesInWindow) return false;

      if (q) {
        const hay = [
          p.project_code, p.project_name,
          p.sites?.site_name ?? "", p.sites?.site_code ?? "",
          p.clients?.legal_name ?? "",
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }

      return true;
    });
  }, [projects, assignments, search, gridStartKey, gridEndKey]);

  // Segments = one bar per crew window (Civil, then Rigging, …), or one grey
  // bar per project when unassigned. This is what makes phased crews show as
  // separate bars instead of one long project slab.
  //
  // T-414 Slice B: crew windows are the truth and are NOT clamped to the
  // project's scheduled window any more — a return visit booked after the
  // original forecast end is real work and must render (Walcha's June/August
  // split). Where the scheduled window is NOT covered by any crew, we emit
  // faint grey "planned" gap segments so the original plan still reads as a
  // light envelope behind the solid crew bars.
  const segments = useMemo(() => {
    type Seg = { project: ProjectRow; colour: string; crewCode: string | null; crewCount: number; isUnassigned: boolean; segKey: string; startKey: string; endKey: string };
    const out: Seg[] = [];
    const dayAfter  = (k: string) => format(addDays(parseISO(k),  1), "yyyy-MM-dd");
    const dayBefore = (k: string) => format(addDays(parseISO(k), -1), "yyyy-MM-dd");
    for (const p of visibleProjects) {
      // A2 — actuals win over the plan. See effectiveWindow above.
      const win    = effectiveWindow(p, gridEndKey);
      const pStart = win.start;
      const pEnd   = win.end;
      const projAs = (assignments ?? []).filter(a => a.project_id === p.project_id && a.crews);
      if (projAs.length === 0) {
        if (!pStart || !pEnd) continue;              // no dates, no crews → callout only
        if (crewFilter.size > 0) continue;           // filtering by crew hides unassigned
        out.push({ project: p, colour: UNBOOKED_GREEN, crewCode: null, crewCount: 0, isUnassigned: true, segKey: `${p.project_id}-none`, startKey: pStart, endKey: pEnd });
        continue;
      }
      // Crew bars — as entered, no clamping. Blank end = runs to the project's
      // scheduled end if that's still ahead, else to the grid edge (ongoing).
      const booked: Array<{ from: string; to: string }> = [];
      for (const a of projAs) {
        const from = a.assigned_from;
        const to   = a.assigned_to ?? ((pEnd && pEnd >= from) ? pEnd : gridEndKey);
        if (to < from) continue;
        booked.push({ from, to });
        // "Unassigned only" now means: show me jobs with NO crew at all, i.e.
        // the ones that need booking. It used to also surface uncovered stretches
        // of the plan, which no longer exist as bars.
        if (showUnassignedOnly) continue;
        if (crewFilter.size > 0 && !crewFilter.has(a.crews!.crew_id)) continue;
        out.push({ project: p, colour: a.crews!.colour_hex, crewCode: a.crews!.crew_code, crewCount: projAs.length, isUnassigned: false, segKey: a.pca_id, startKey: from, endKey: to });
      }

      // Slim "still open, nobody on site" connector BETWEEN crew bookings.
      //
      // Deliberately only between the first and last booking — never leading up
      // to the first visit or trailing after the last. The old version spanned
      // the whole planned window, which filled months of empty calendar with
      // grey and was the thing James asked to be rid of. Between two real
      // visits it means something: the job is open, the crew is off it, and
      // it's coming back.
      //
      // Only while the job is genuinely underway. A job that has finished, or
      // has not started, has no "nobody on site" state worth drawing.
      const isUnderwayNow = !!p.actual_start_date && !p.actual_end_date;
      if (isUnderwayNow && !showUnassignedOnly && crewFilter.size === 0 && booked.length > 1) {
        booked.sort((a, z) => a.from.localeCompare(z.from));
        let gapIdx = 0;
        for (let i = 0; i < booked.length - 1; i++) {
          const gapFrom = dayAfter(booked[i].to);
          const gapTo   = dayBefore(booked[i + 1].from);
          if (gapTo < gapFrom) continue;              // back-to-back, no gap
          out.push({
            project: p, colour: UNASSIGNED_GREY, crewCode: null,
            crewCount: projAs.length, isUnassigned: true,
            segKey: `${p.project_id}-open-${gapIdx++}`,
            startKey: gapFrom, endKey: gapTo,
          });
        }
      }
      // REMOVED 18 Aug 2026 — the "planned but uncovered" grey envelope.
      //
      // It drew a grey bar over every stretch of the planned window not covered
      // by a crew. On a real job that means THE GAPS BETWEEN VISITS get a bar.
      // Walcha started 25 Jun with three crew visits booked, and the calendar
      // filled 27 Jun–9 Aug and 13–16 Aug with grey, reading as though
      // something was happening on site when nothing was.
      //
      // James: "why is Walcha in grey even showing up on the 27/07 week when
      // that site has started… these old grey lines is messy and I don't want
      // it."
      //
      // The rule now: GREY MEANS ONE THING — this job has no crew at all and
      // needs one. That is handled above (projAs.length === 0). Once any crew
      // is booked you see the crew bars and only the crew bars; the gaps
      // between visits are just gaps.
      //
      // The plan is not lost. The project side panel still shows "Planned
      // dates" alongside the actual crew dates, which is where a plan-versus-
      // real comparison belongs. A calendar is for what is happening, not for
      // what somebody forecast in April.
    }
    return out;
  }, [visibleProjects, assignments, crewFilter, showUnassignedOnly, gridEndKey]);

  // For each project, decide a single bar colour.
  function projectBarColour(p: ProjectRow): { colour: string; primaryCrewCode: string | null; crewCount: number } {
    const projAs = (assignments ?? []).filter(a => a.project_id === p.project_id && a.crews);
    if (projAs.length === 0) {
      return { colour: UNBOOKED_GREEN, primaryCrewCode: null, crewCount: 0 };
    }
    // Pick the assignment with the longest overlap window — that's "the
    // dominant crew" for the bar. Tiebreak by earliest start.
    const ranked = [...projAs].sort((a, z) => {
      const aFrom = a.assigned_from;
      const aTo   = a.assigned_to ?? p.scheduled_end_date ?? a.assigned_from;
      const zFrom = z.assigned_from;
      const zTo   = z.assigned_to ?? p.scheduled_end_date ?? z.assigned_from;
      const aDur = differenceInCalendarDays(parseISO(aTo), parseISO(aFrom));
      const zDur = differenceInCalendarDays(parseISO(zTo), parseISO(zFrom));
      if (aDur !== zDur) return zDur - aDur;
      return a.assigned_from.localeCompare(z.assigned_from);
    });
    return {
      colour:          ranked[0].crews!.colour_hex,
      primaryCrewCode: ranked[0].crews!.crew_code,
      crewCount:       projAs.length,
    };
  }

  // Per-week: compute project bars + lanes. Mirrors CalendarMonth's algorithm
  // but inputs are projects, not bookings.
  const weekBars = useMemo(() => {
    return weeks.map(weekDays => {
      const weekStart = weekDays[0];
      const weekEnd   = weekDays[6];
      const weekStartKey = format(weekStart, "yyyy-MM-dd");
      const weekEndKey   = format(weekEnd,   "yyyy-MM-dd");

      type Tmp = WeekBar & { clipStartKey: string; clipEndKey: string };
      const tmps: Tmp[] = [];

      for (const s of segments) {
        const sStart = s.startKey;
        const sEnd   = s.endKey;
        if (sEnd < weekStartKey || sStart > weekEndKey) continue;

        const visStart = sStart < weekStartKey ? weekStartKey : sStart;
        const visEnd   = sEnd   > weekEndKey   ? weekEndKey   : sEnd;
        const dayIndex = differenceInCalendarDays(parseISO(visStart), weekStart);
        const span     = differenceInCalendarDays(parseISO(visEnd),   parseISO(visStart)) + 1;

        tmps.push({
          project:      s.project,
          colour:       s.colour,
          crewCode:     s.crewCode,
          crewCount:    s.crewCount,
          isUnassigned: s.isUnassigned,
          segKey:       s.segKey,
          segFrom:      sStart,
          segTo:        sEnd,
          dayIndex,
          span,
          startsHere:   sStart >= weekStartKey,
          endsHere:     sEnd   <= weekEndKey,
          lane:         0,
          clipStartKey: visStart,
          clipEndKey:   visEnd,
        });
      }

      tmps.sort((a, z) => a.clipStartKey.localeCompare(z.clipStartKey) || z.span - a.span);

      const laneEnds: string[] = [];
      const placed:   WeekBar[] = [];
      const overflow: Tmp[] = [];

      for (const t of tmps) {
        let lane = -1;
        for (let i = 0; i < laneEnds.length; i++) {
          if (laneEnds[i] < t.clipStartKey) { lane = i; break; }
        }
        if (lane === -1) lane = laneEnds.length;

        if (lane >= MAX_LANES) {
          overflow.push(t);
        } else {
          laneEnds[lane] = t.clipEndKey;
          placed.push({
            project:      t.project,
            colour:       t.colour,
            crewCode:     t.crewCode,
            crewCount:    t.crewCount,
            isUnassigned: t.isUnassigned,
            segKey:       t.segKey,
            segFrom:      t.segFrom,
            segTo:        t.segTo,
            dayIndex:     t.dayIndex,
            span:         t.span,
            startsHere:   t.startsHere,
            endsHere:     t.endsHere,
            lane,
          });
        }
      }

      const overflowByDayIndex = new Array(7).fill(0) as number[];
      for (const t of overflow) {
        for (let i = t.dayIndex; i < t.dayIndex + t.span; i++) overflowByDayIndex[i] += 1;
      }

      const laneCount = Math.min(laneEnds.length, MAX_LANES);
      return { bars: placed, laneCount, overflowByDayIndex };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks, segments]);

  // T-347 — tentative BIDs the director opted-in to plan on the schedule.
  // Only open/responded bids with show_on_schedule + a tentative window that
  // overlaps the visible grid. Drawn soft-red / dashed, in their own lane band
  // below the project bars so they never push real crews into overflow.
  const { data: tentativeBids } = useQuery({
    queryKey: ["schedule_tentative_bids", gridStartKey, gridEndKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bids")
        .select(`
          bid_id, bid_number, bid_title, status, tentative_start, tentative_end,
          sites:site_id     (site_name, site_code),
          clients:client_id (legal_name)
        `)
        .eq("show_on_schedule", true)
        .in("status", ["open", "responded"])
        .not("tentative_start", "is", null)
        .not("tentative_end", "is", null)
        .lte("tentative_start", gridEndKey)
        .gte("tentative_end", gridStartKey);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const bidSegments = useMemo(() => {
    if (!showTentativeBids) return [] as Array<{ bid: any; startKey: string; endKey: string }>;
    const q = search.trim().toLowerCase();
    return (tentativeBids ?? [])
      .filter(b => {
        if (!q) return true;
        const hay = [b.bid_number, b.bid_title, b.sites?.site_name ?? "", b.clients?.legal_name ?? ""].join(" ").toLowerCase();
        return hay.includes(q);
      })
      .map(b => ({ bid: b, startKey: b.tentative_start as string, endKey: b.tentative_end as string }));
  }, [tentativeBids, showTentativeBids, search]);

  // Per-week greedy lane packing for bid bars (own lane budget — no MAX cap;
  // tentative bids are few and should always show).
  const weekBidBars = useMemo(() => {
    return weeks.map(weekDays => {
      const weekStartKey = format(weekDays[0], "yyyy-MM-dd");
      const weekEndKey   = format(weekDays[6], "yyyy-MM-dd");
      type T = { bid: any; dayIndex: number; span: number; startsHere: boolean; endsHere: boolean; lane: number; clipStartKey: string; segFrom: string; segTo: string; clipEndKey: string };
      const tmps: T[] = [];
      for (const s of bidSegments) {
        if (s.endKey < weekStartKey || s.startKey > weekEndKey) continue;
        const visStart = s.startKey < weekStartKey ? weekStartKey : s.startKey;
        const visEnd   = s.endKey   > weekEndKey   ? weekEndKey   : s.endKey;
        const dayIndex = differenceInCalendarDays(parseISO(visStart), weekDays[0]);
        const span     = differenceInCalendarDays(parseISO(visEnd), parseISO(visStart)) + 1;
        tmps.push({ bid: s.bid, dayIndex, span, startsHere: s.startKey >= weekStartKey, endsHere: s.endKey <= weekEndKey, lane: 0, clipStartKey: visStart, clipEndKey: visEnd, segFrom: s.startKey, segTo: s.endKey });
      }
      tmps.sort((a, z) => a.clipStartKey.localeCompare(z.clipStartKey) || z.span - a.span);
      const laneEnds: string[] = [];
      for (const t of tmps) {
        let lane = -1;
        for (let i = 0; i < laneEnds.length; i++) { if (laneEnds[i] < t.clipStartKey) { lane = i; break; } }
        if (lane === -1) lane = laneEnds.length;
        laneEnds[lane] = t.clipEndKey;
        t.lane = lane;
      }
      return { bidBars: tmps, bidLaneCount: laneEnds.length };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks, bidSegments]);

  function toggleCrew(id: string) {
    setCrewFilter(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const isLoading = loadingProjects || loadingPcas;
  const hasAnything = visibleProjects.length > 0 || bidSegments.length > 0;

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[20px] font-semibold inline-flex items-center gap-2">
            <CalendarDays size={18} className="text-accent"/> Schedule
          </h1>
          {/* One line that changes with the view, so each tab says what it is
              for - James, 27 Aug: "similar views that are different". The four
              tabs answer four different questions; say which. */}
          <p className="text-[12px] text-text-muted mt-1">
            {viewMode === "calendar" &&
              "THE MONTH — active jobs as bars. Click a bar for details or to book a crew. Solid = crew on site · green dashed = won, needs a crew · red dashed = tentative bid."}
            {viewMode === "gantt" &&
              "THE JOBS — one row per project across the window, with its crews and key dates."}
            {viewMode === "crew" &&
              "THE CREWS — one row per crew. Drag a job on to book them · drag a block to move it · click a block for its dates and who's going · drag people between crews (their site visits follow)."}
            {viewMode === "people" &&
              "THE PEOPLE — one row per person: sites in their crew's colour, plus tasks, leave and defects. Grey dashed = listed (standby)."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={viewMode === "calendar" ? "primary" : "secondary"}
            size="sm"
            icon={LayoutGrid}
            onClick={() => setViewMode("calendar")}
          >Calendar</Button>
          <Button
            variant={viewMode === "gantt" ? "primary" : "secondary"}
            size="sm"
            icon={ListTree}
            onClick={() => setViewMode("gantt")}
          >Gantt</Button>
          <Button
            variant={viewMode === "crew" ? "primary" : "secondary"}
            size="sm"
            icon={Users}
            onClick={() => setViewMode("crew")}
            /* James, 5 Sep: "'Crews' is a mislabel on what the screen is now —
               it's really how we move and schedule everyone." Label only; the
               viewMode key, routes and logic are untouched on purpose. */
            title="Crews, their people, moves, depot days — the whole workforce, one screen"
          >Workforce</Button>
          <Button
            variant={viewMode === "people" ? "primary" : "secondary"}
            size="sm"
            icon={UserCircle2}
            onClick={() => setViewMode("people")}
            title="Every schedulable person, one row each — who is where, all month"
          >People</Button>
          {hasRole(user, "manager") && (
            <Button
              variant="secondary"
              size="sm"
              icon={Send}
              onClick={() => setAssignTaskOpen(true)}
              title="One-off ad-hoc task to a single employee"
            >Assign task</Button>
          )}
          <Link to="/crews" className="text-[12px] text-text-muted hover:text-accent ml-2">Manage crews →</Link>
        </div>
      </header>

      <Card>
        <div className="p-3 space-y-3">
          {/* Month nav + search */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setViewMonth(addMonths(viewMonth, -1))}
              className="w-8 h-8 rounded border border-line text-text-muted hover:border-accent hover:text-accent inline-flex items-center justify-center transition-colors"
              aria-label="Previous month"
            ><ChevronLeft size={14}/></button>
            <div className="text-[15px] font-semibold min-w-[160px] text-center">
              {format(viewMonth, "MMMM yyyy")}
            </div>
            <button
              type="button"
              onClick={() => setViewMonth(addMonths(viewMonth, 1))}
              className="w-8 h-8 rounded border border-line text-text-muted hover:border-accent hover:text-accent inline-flex items-center justify-center transition-colors"
              aria-label="Next month"
            ><ChevronRight size={14}/></button>
            <Button variant="secondary" size="sm" onClick={() => setViewMonth(startOfMonth(new Date()))}>Today</Button>

            <div className="ml-auto inline-flex items-center gap-2">
              <Search size={14} className="text-text-muted"/>
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search project / site / client"
                className="w-[260px]"
              />
            </div>
          </div>
          {/* 25 Sep 2026 - the next public holidays, so a booking is never a surprise. */}
          <UpcomingHolidays />
          {/* Mig 425 - stand-downs and carrier embargoes, so nobody plans into one. */}
          <ScheduleStanddownStrip />

          {/* Crew chips + unassigned toggle */}
          <div className="flex flex-wrap items-center gap-1.5">
            {crewsInWindow.map(c => {
              const on = crewFilter.size === 0 || crewFilter.has(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCrew(c.id)}
                  className={`px-2 py-0.5 rounded-full text-[11px] inline-flex items-center gap-1.5 border transition-opacity ${on ? "border-line" : "border-line/50 opacity-40"}`}
                  title={c.code}
                >
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: c.colour }}/>
                  <span className="font-mono">{c.code}</span>
                </button>
              );
            })}
            {crewFilter.size > 0 && (
              <button
                type="button"
                onClick={() => setCrewFilter(new Set())}
                className="px-2 py-0.5 rounded-full text-[11px] text-accent hover:underline"
              >Clear</button>
            )}
            <label className="inline-flex items-center gap-1.5 text-[11px] text-text-muted ml-2">
              <input
                type="checkbox"
                checked={showUnassignedOnly}
                onChange={e => setShowUnassignedOnly(e.target.checked)}
              />
              Unassigned only
            </label>
            {/* T-347 — tentative bids toggle */}
            <label className="inline-flex items-center gap-1.5 text-[11px] text-text-muted ml-2">
              <input
                type="checkbox"
                checked={showTentativeBids}
                onChange={e => setShowTentativeBids(e.target.checked)}
              />
              Tentative bids
            </label>
            <span className="ml-auto inline-flex items-center gap-3 text-[11px] text-text-muted">
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: UNBOOKED_GREEN, opacity: 0.5 }}/>
                <span>= won, no crew booked</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-3 h-2.5 rounded-sm border border-dashed border-danger/60 bg-danger/10"/>
                <span>= tentative bid</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: PROMOTED_PURPLE }}/>
                <span>= key date from a project</span>
              </span>
            </span>
          </div>
        </div>
      </Card>

      {isLoading && (
        <div className="flex justify-center py-8"><Spinner size={20}/></div>
      )}

      {!isLoading && !hasAnything && (
        <EmptyState
          icon={CalendarDays}
          title="Nothing on this month"
          description="A job appears here once it has planned dates, or a crew booked on site. Set the dates on the project, or book a crew from here. Tentative bids show too — tick 'Show on schedule' on the bid."
        />
      )}

      {/* T-414 — double-booked crews: one quiet line, expand for detail,
          dismiss per clash (remembered on this browser). Warn-not-block. */}
      {!isLoading && crewConflicts.length > 0 && (
        <Card>
          <div className="px-3 py-2">
            <button
              type="button"
              onClick={() => setConflictsExpanded(v => !v)}
              className="w-full flex items-center gap-2 text-left"
            >
              <AlertCircle size={13} className="text-warn flex-shrink-0"/>
              <span className="text-[12px] uppercase tracking-wider text-text-muted">Double-booked crews</span>
              <span className="text-[11px] text-text-muted">· {crewConflicts.length}</span>
              <span className="text-[11px] text-accent ml-auto">{conflictsExpanded ? "Hide" : "Show"}</span>
            </button>
            {conflictsExpanded && (
              <div className="space-y-1 mt-2">
                {crewConflicts.map(c => {
                  const pa = projById.get(c.projA);
                  const pb = projById.get(c.projB);
                  return (
                    <div key={c.key} className="text-[12px] flex items-center gap-1.5 flex-wrap">
                      <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: c.colour }}/>
                      <span className="font-mono">{c.crewCode}</span>
                      <span className="text-text-muted">is booked on</span>
                      <button type="button" className="text-accent hover:underline"
                        onClick={() => pa && setOpenProject(pa)}>{pa?.sites?.site_name ?? pa?.project_name ?? "a project"}</button>
                      <span className="text-text-muted">and</span>
                      <button type="button" className="text-accent hover:underline"
                        onClick={() => pb && setOpenProject(pb)}>{pb?.sites?.site_name ?? pb?.project_name ?? "another project"}</button>
                      <span className="text-text-muted">at the same time · {fmtDate(c.from)} → {fmtDate(c.to)}</span>
                      <button
                        type="button"
                        onClick={() => dismissConflict(c.key)}
                        className="text-text-muted hover:text-text p-0.5 rounded hover:bg-ink ml-auto"
                        title="Dismiss — known and accepted (stays hidden on this browser; changing either booking brings it back)"
                      >
                        <X size={12}/>
                      </button>
                    </div>
                  );
                })}
                {anyDismissed && (
                  <button type="button" onClick={restoreConflicts} className="text-[11px] text-text-muted hover:text-accent hover:underline">
                    Show {dismissedConflicts.length} dismissed
                  </button>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {!isLoading && hasAnything && viewMode === "calendar" && (
        <Card>
          <div className="p-3">
            {/* Weekday header */}
            <div className="grid grid-cols-7 gap-px bg-line border border-line rounded-t overflow-hidden">
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
                <div key={d} className="bg-ink-raised text-[10px] font-mono uppercase tracking-wider2 text-text-muted text-center py-1.5">{d}</div>
              ))}
            </div>

            {/* Week rows */}
            <div className="flex flex-col gap-px bg-line border-l border-r border-b border-line rounded-b overflow-hidden">
              {weeks.map((weekDays, wi) => {
                const { bars, laneCount, overflowByDayIndex } = weekBars[wi];
                const { bidBars, bidLaneCount } = weekBidBars[wi];
                const totalLanes = laneCount + bidLaneCount;
                const lanesAreaPx = totalLanes > 0 ? (totalLanes * LANE_HEIGHT_PX) + LANES_PADDING_PX : 0;
                const cellPaddingTopPx = DAY_NUMBER_HEIGHT_PX + lanesAreaPx;

                return (
                  <div key={wi} className="relative grid grid-cols-7 gap-px">
                    {weekDays.map((day, di) => {
                      const inMonth = isSameMonth(day, viewMonth);
                      const today   = isToday(day);
                      const overflow = overflowByDayIndex[di];
                      const dKey = format(day, "yyyy-MM-dd");
                      const keyDates = keyDatesByDay.get(dKey) ?? [];
                      const anyOutage = keyDates.some(k => k.booking_type === "outage");
                      const anyImpacting = keyDates.some(k => k.booking_type === "outage" && k.customer_impacting);
                      return (
                        <div
                          key={di}
                          className={`relative min-h-[100px] px-1.5 pb-1.5 group/day ${inMonth ? "" : "opacity-40"} ${
                            keyDates.length === 0 ? "bg-ink" : ""}`}
                          style={{
                            paddingTop: `${cellPaddingTopPx}px`,
                            // The tint IS the signal. Kept faint on purpose - it
                            // has to survive behind four crew bars without
                            // competing with them, because the bars are the work
                            // and this is the constraint on the work.
                            background: keyDates.length
                              ? (anyOutage ? "rgba(124,58,237,0.13)" : "rgba(124,58,237,0.07)")
                              : undefined,
                            boxShadow: anyImpacting
                              ? "inset 0 0 0 1px rgba(124,58,237,0.55)" : undefined,
                          }}
                        >
                          <div className={`absolute top-1.5 left-1.5 text-[11px] font-mono inline-flex items-center justify-center w-5 h-5 rounded-full ${today ? "bg-accent text-white font-semibold" : inMonth ? "text-text" : "text-text-dim"}`}>
                            {format(day, "d")}
                          </div>

                          {/* Key-date markers. One small button per promoted
                              booking, top-right, above the + . Four outages on
                              one day is four markers on one line, not four rows
                              added to every crew's month. */}
                          {keyDates.length > 0 && (
                            <div className="absolute top-1 right-6 flex items-center gap-0.5">
                              {keyDates.slice(0, 3).map(k => (
                                <button key={k.booking_id} type="button"
                                  onClick={() => setOpenKeyDate(k)}
                                  title={`${k.title}${k.projects?.sites?.site_name ? ` · ${k.projects.sites.site_name}` : ""} — click for details`}
                                  className="w-4 h-4 rounded-sm inline-flex items-center justify-center text-white hover:brightness-110 transition"
                                  style={{ background: PROMOTED_PURPLE }}>
                                  {k.booking_type === "outage"
                                    ? <RadioTower size={9}/> : <Star size={9}/>}
                                </button>
                              ))}
                              {keyDates.length > 3 && (
                                <span className="text-[9px] font-mono" style={{ color: PROMOTED_PURPLE }}>
                                  +{keyDates.length - 3}
                                </span>
                              )}
                            </div>
                          )}

                          {/* ---- click a day to schedule somebody ----------
                              James: "would love a way to click a day and
                              schedule not just a crew but an employee to a
                              task and see it here."

                              A + in the corner of the day rather than making
                              the whole cell clickable: the cell is covered in
                              project bars that already have their own click,
                              and a cell that opens a form whenever a bar is
                              missed is a cell that fights the user.       */}
                          {hasRole(user, "manager") && inMonth && (
                            <button
                              type="button"
                              onClick={() => setAssignDate(dKey)}
                              title={`Assign somebody a task on ${format(day, "EEE d MMM")}`}
                              className="absolute top-1 right-1 w-5 h-5 rounded inline-flex items-center justify-center
                                         text-text-dim opacity-0 group-hover/day:opacity-100 hover:text-accent
                                         hover:bg-ink-raised transition-opacity focus:opacity-100"
                            >
                              <Plus size={12}/>
                            </button>
                          )}

                          {/* ---- ad-hoc tasks on this day ------------------
                              James: "the way we see the task, I don't love it.
                              I know we can't have it all shown or it gets too
                              big, but still I think we could do just a little
                              better on the visuals here."

                              The first cut was a grey dot and the word "1 task",
                              which told you a task existed and nothing else - so
                              you had to hover to learn anything, and hovering is
                              not scanning. Now: initials + what it is. Two chips
                              maximum, then a count, because the ceiling is what
                              keeps the cell from growing.                    */}
                          {(() => {
                            const onDay = tasksByDay.get(dKey);
                            if (!onDay || onDay.length === 0) return null;
                            const shown = onDay.slice(0, 2);
                            const rest  = onDay.length - shown.length;
                            return (
                              <div className="mt-0.5 space-y-0.5">
                                {shown.map(pb => (
                                  <button
                                    key={pb.booking_id}
                                    type="button"
                                    onClick={() => setOpenTask(pb)}
                                    title={`${nameFor(pb.assignee_employee_id)} — ${pb.title}${pb.location_address ? ` · ${pb.location_address}` : ""}`}
                                    className="w-full flex items-center gap-1 rounded-sm border border-[rgba(255,120,68,0.55)]
                                               bg-[rgba(255,120,68,0.10)] hover:bg-[rgba(255,120,68,0.20)]
                                               transition-colors px-1 py-px text-left"
                                  >
                                    <span className="flex-shrink-0 text-[8px] font-mono font-semibold leading-none
                                                     text-[rgb(214,88,38)] bg-[rgba(255,120,68,0.22)] rounded px-1 py-0.5">
                                      {initialsFor(pb.assignee_employee_id)}
                                    </span>
                                    <span className="truncate text-[9px] leading-[13px] text-text">
                                      {pb.location_address?.trim() || pb.title}
                                    </span>
                                  </button>
                                ))}
                                {rest > 0 && (
                                  <div className="text-[9px] text-text-muted pl-1">+{rest} more task{rest === 1 ? "" : "s"}</div>
                                )}
                              </div>
                            );
                          })()}

                          {/* Defect visits due this day. Red-edged chips so
                              rework never blends in with work. James: "where is
                              the defect we have planned at Coona?" - here. */}
                          {(() => {
                            const dd = defectsByDay.get(dKey);
                            if (!dd || dd.length === 0) return null;
                            return (
                              <div className="mt-0.5 space-y-0.5">
                                {dd.slice(0, 2).map(d => (
                                  <button key={d.defect_id} type="button"
                                    onClick={() => navigate("/acceptance/defects")}
                                    title={`${d.defect_number} — ${d.title}\n${d.sites?.site_name ?? d.projects?.project_name ?? ""} · ${d.severity} · ${d.status.replace(/_/g, " ")}${d.assigned_to_employee_id ? `\nAssigned: ${nameFor(d.assigned_to_employee_id)}` : "\nUNASSIGNED"}\nClick to open the defect register`}
                                    className="w-full flex items-center gap-1 rounded-sm border border-[rgba(220,68,68,0.6)]
                                               bg-[rgba(220,68,68,0.08)] hover:bg-[rgba(220,68,68,0.16)]
                                               transition-colors px-1 py-px text-left"
                                  >
                                    <AlertTriangle size={8} className="flex-shrink-0 text-[rgb(200,60,60)]"/>
                                    <span className="truncate text-[9px] leading-[13px] text-text">
                                      {d.sites?.site_name ?? d.projects?.project_name ?? d.title}
                                    </span>
                                  </button>
                                ))}
                                {dd.length > 2 && (
                                  <div className="text-[9px] text-text-muted pl-1">+{dd.length - 2} more defects</div>
                                )}
                              </div>
                            );
                          })()}

                          {overflow > 0 && (
                            <div className="text-[9px] text-text-muted px-1.5">+{overflow} more</div>
                          )}
                        </div>
                      );
                    })}

                    {/* Mig 425 — stand-downs / embargoes along the bottom of the week. */}
                    <CalendarWeekStops weekStartKey={format(weekDays[0], "yyyy-MM-dd")} />

                    {/* Bars overlay — one bar per project per week */}
                    <div
                      className="absolute left-0 right-0 grid grid-cols-7 gap-px pointer-events-none"
                      style={{ top: `${DAY_NUMBER_HEIGHT_PX}px` }}
                    >
                      {bars.map(bar => {
                        const p = bar.project;
                        const site = p.sites?.site_name ?? p.project_name;
                        const tipParts = [
                          `${p.project_code} · ${site}`,
                          p.clients?.legal_name ?? "",
                          bar.isUnassigned
                            ? (bar.crewCount > 0
                                ? `Planned — no crew booked ${fmtDate(bar.segFrom)} → ${fmtDate(bar.segTo)}`
                                : "Unassigned — click to assign a crew")
                            : `Crew ${bar.crewCode ?? ""}  ·  ${fmtDate(bar.segFrom)} → ${fmtDate(bar.segTo)}`,
                        ].filter(Boolean);
                        const roundLeft  = bar.startsHere ? "rounded-l" : "";
                        const roundRight = bar.endsHere   ? "rounded-r" : "";
                        // isUnassigned + has crews = the slim "job open, nobody
                        // on site" connector between two crew visits. Renders as
                        // a 5px faded strip rather than a full bar, so it reads as
                        // "still ours, just quiet" and not as work happening.
                        const isPlanGap = bar.isUnassigned && bar.crewCount > 0;
                        // Label: site name is the must-have (colour already signals
                        // the crew); append the crew code after it if assigned + it fits.
                        const label = bar.crewCode ? `${site} · ${bar.crewCode}` : site;
                        return (
                          <button
                            key={`${bar.segKey}-w${wi}`}
                            type="button"
                            onClick={() => setOpenProject(p)}
                            style={{
                              gridColumn: `${bar.dayIndex + 1} / span ${bar.span}`,
                              gridRow: 1,
                              marginTop: `${bar.lane * LANE_HEIGHT_PX + (isPlanGap ? (BAR_HEIGHT_PX - 5) / 2 : 0)}px`,
                              height: `${isPlanGap ? 5 : BAR_HEIGHT_PX}px`,
                              // A job with no crew booked is not work happening — it is
                              // work WAITING to be booked. Drawing it as a solid slab made
                              // it compete with real crew bars for attention and read as
                              // clutter. It is now an outline: same footprint, obviously
                              // hollow, so the eye skips it until you look for it.
                              // Soft tint + dashed edge, not a fully hollow box:
                              // hollow read as an error state. The 8-digit hex is
                              // the bar colour with an alpha suffix - 22 is ~13%
                              // fill, 99 is ~60% for the border.
                              background: bar.isUnassigned && !isPlanGap ? `${bar.colour}22` : bar.colour,
                              border: bar.isUnassigned && !isPlanGap ? `1px dashed ${bar.colour}99` : undefined,
                              color: bar.isUnassigned && !isPlanGap ? bar.colour : undefined,
                              opacity: isPlanGap ? 0.25 : 1,
                            }}
                            className={`pointer-events-auto ${isPlanGap ? "rounded-full" : `${roundLeft} ${roundRight}`} text-[10px] leading-[18px] ${bar.isUnassigned && !isPlanGap ? "" : "text-white"} truncate hover:brightness-110 transition px-1.5 mx-0.5 text-center font-medium`}
                            title={tipParts.join("\n")}
                          >
                            {isPlanGap ? "" : label}
                          </button>
                        );
                      })}

                      {/* T-347 — tentative bid bars, in their own lane band below projects */}
                      {bidBars.map(bar => {
                        const b = bar.bid;
                        const site = b.sites?.site_name ?? b.bid_title;
                        const label = `${site} · ${b.bid_number}`;
                        const roundLeft  = bar.startsHere ? "rounded-l" : "";
                        const roundRight = bar.endsHere   ? "rounded-r" : "";
                        return (
                          <button
                            key={`bid-${b.bid_id}-w${wi}`}
                            type="button"
                            onClick={() => navigate(`/bids/${b.bid_id}`)}
                            style={{
                              gridColumn: `${bar.dayIndex + 1} / span ${bar.span}`,
                              gridRow: 1,
                              marginTop: `${(laneCount + bar.lane) * LANE_HEIGHT_PX}px`,
                              height: `${BAR_HEIGHT_PX}px`,
                            }}
                            className={`pointer-events-auto ${roundLeft} ${roundRight} text-[10px] leading-[16px] text-danger truncate hover:bg-danger/20 transition px-1.5 mx-0.5 text-center font-medium border border-dashed border-danger/60 bg-danger/10`}
                            title={[`TENTATIVE bid · ${b.bid_number} · ${site}`, b.clients?.legal_name ?? "", `${fmtDate(bar.segFrom)} → ${fmtDate(bar.segTo)}`, "Click to open the bid"].filter(Boolean).join("\n")}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* Crew view — one row per CREW, blocks are the jobs they are on. This is
          the management surface: the thing being scheduled is a crew's time, so
          the crew is the row. Double-booking shows up as two blocks overlapping
          in one row rather than as a warning banner somewhere else, and a crew
          with an empty row is the answer to "who is free next week".
          Read-only for now; drag lands here, not on the Gantt. */}
      {!isLoading && viewMode === "crew" && (
        <CrewLaneView
          crews={allCrews ?? []}
          leadNameById={leadNameById}
          assignments={assignments ?? []}
          projects={visibleProjects}
          allProjects={projects ?? []}
          weeks={weeks}
          onSelectProject={p => setOpenProject(p)}
          crewMembers={crewMembers}
          personalBookings={personalBookings}
          leaveRows={leaveRows}
          projectTeam={projectTeam}
          schedulablePeople={schedulablePeople}
          defectRows={defectRows}
          rosterRows={rosterRows}
          onSelectPerson={(id, name, date, end) => {
            setAssignFor({ id, name });
            if (date) setAssignDate(date);
            setAssignEnd(end && date && end !== date ? end : null);
          }}
          onSelectTask={setOpenTask}
        />
      )}

      {!isLoading && viewMode === "people" && (
        <PeopleView
          schedulablePeople={schedulablePeople}
          crewMembers={crewMembers}
          crews={allCrews ?? []}
          assignments={assignments ?? []}
          projects={projects ?? []}
          projectTeam={projectTeam}
          personalBookings={personalBookings}
          leaveRows={leaveRows}
          defectRows={defectRows}
          rosterRows={rosterRows}
          weeks={weeks}
          onSelectPerson={(id, name, date) => { setAssignFor({ id, name }); if (date) setAssignDate(date); }}
          onMoved={() => {
            qc.invalidateQueries({ queryKey: ["schedule_crew_members"] });
            qc.invalidateQueries({ queryKey: ["schedule_roster"] });  // 335 - roster follows the move
            qc.invalidateQueries({ queryKey: ["crew_membership_now"] });
            qc.invalidateQueries({ queryKey: ["crews_member_counts"] });
          }}
        />
      )}

      {/* Gantt view — one row per project, bar across the visible window */}
      {!isLoading && visibleProjects.length > 0 && viewMode === "gantt" && (
        <GanttView
          projects={visibleProjects}
          assignments={assignments ?? []}
          weeks={weeks}
          onSelectProject={p => setOpenProject(p)}
          getColour={projectBarColour}
          personalBookings={personalBookings}
          nameFor={nameFor}
          onSelectTask={setOpenTask}
        />
      )}

      {/* Unscheduled active projects — visible-but-not-renderable. Surfaces
          projects whose status puts them in the active set but where the
          scheduled_start_date / scheduled_end_date hasn't been filled in.
          Click → opens project detail so dates can be set. */}
      {!isLoading && unscheduledActive.length > 0 && (
        <Card>
          <div className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle size={13} className="text-warn"/>
              <span className="text-[12px] uppercase tracking-wider text-text-muted">
                Not on the calendar yet
              </span>
              <span className="text-[11px] text-text-muted">
                · {unscheduledActive.length} project{unscheduledActive.length === 1 ? "" : "s"}
              </span>
            </div>
            <p className="text-[11px] text-text-muted mb-2">
              No planned dates and no crew booked. Book a crew on site, or set the dates on the project, and it'll appear above.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {unscheduledActive.map(p => (
                <Link
                  key={p.project_id}
                  to={`/projects/${p.project_id}`}
                  className="px-2 py-1 rounded bg-ink border border-line/50 text-[11px] hover:border-accent transition-colors"
                  title={`${p.project_name}${p.sites?.site_name ? ` · ${p.sites.site_name}` : ""}\nStatus: ${p.status}`}
                >
                  <span className="font-mono text-accent">{p.project_code}</span>
                  {p.sites?.site_name && (
                    <span className="text-text-muted ml-1.5">· {p.sites.site_name}</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Side panel — project summary + crew assignment UX */}
      {openProject && (
        <ProjectSchedulePanel
          project={openProject}
          assignments={(assignments ?? []).filter(a => a.project_id === openProject.project_id)}
          onClose={() => setOpenProject(null)}
        />
      )}

      {/* T-249c — quick-assign one-off task to an employee.
          Opened either from the header button (nobody pre-selected) or from a
          person's row on the Crews tab, in which case it arrives pointed at
          them. The modal has always taken assigneeEmployeeId; the schedule
          never had a person to pass. */}
      {openKeyDate && (
        <KeyDateDetail booking={openKeyDate} onClose={() => setOpenKeyDate(null)} />
      )}

      {openTask && (
        <TaskDetail
          task={openTask}
          who={nameFor(openTask.assignee_employee_id)}
          onClose={() => setOpenTask(null)}
          onChanged={() => {
            setOpenTask(null);
            qc.invalidateQueries({ queryKey: ["schedule_personal_bookings"] });
          }}
        />
      )}

      {(assignTaskOpen || assignFor || assignDate) && (
        <AssignPersonalBookingModal
          assigneeEmployeeId={assignFor?.id}
          assigneeName={assignFor?.name}
          defaultDate={assignDate ?? undefined}
          defaultEndDate={assignEnd ?? undefined}
          onClose={() => { setAssignTaskOpen(false); setAssignFor(null); setAssignDate(null); setAssignEnd(null); }}
          onCreated={() => {
            setAssignTaskOpen(false); setAssignFor(null); setAssignDate(null); setAssignEnd(null);
            // The task is now a bar on this screen, so the screen has to know.
            qc.invalidateQueries({ queryKey: ["schedule_personal_bookings"] });
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// GanttView — one row per project, bar across the visible window.
// Useful when you want to see "which project starts when" in a single
// horizontal flow. Same colour rules + side panel as the calendar view.
// =============================================================================
// =============================================================================
// BookingEditor — move, resize or reassign one crew booking
// =============================================================================
// The rule, in James's words, 19 Aug 2026:
//
//   "Start date + total days = end date. Or start date and end date dictate
//    total days. Start date is not changed by say an end date and total days,
//    as that's working backwards. So start date is always needed - or say you
//    drag the block to a new location / crew, it lands on the first day, that's
//    the start day, then the rules apply."
//
// Three numbers, two of them independent. START IS THE ANCHOR:
//   * change the start  -> the booking MOVES, keeping its length
//   * change the days   -> the END moves
//   * change the end    -> the DAYS change
//   * nothing ever moves the start except the start (or a drop)
//
// Drag will set exactly these same three values, which is why this is built
// first: it makes the drag testable against something already proven.
// =============================================================================
function BookingEditor({
  booking, crews, projectLabel, onClose,
}: {
  booking: { pca_id: string; crew_id: string; from: string; to: string | null; projectId: string;
             weekendsOff?: boolean };
  crews: { crew_id: string; crew_name: string; colour_hex: string }[];
  projectLabel: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [crewId, setCrewId]       = useState(booking.crew_id);
  // Mig 400 — "a quick 'off on weekends' tweak per block" (James, 29 Sep).
  const [weekendsOff, setWeekendsOff] = useState(booking.weekendsOff ?? false);
  const [from, setFrom]           = useState(booking.from);
  const [openEnded, setOpenEnded] = useState(booking.to == null);
  const [to, setTo]               = useState(booking.to ?? booking.from);
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState<string | null>(null);

  const daysBetween = (a: string, z: string) =>
    Math.max(1, differenceInCalendarDays(parseISO(z), parseISO(a)) + 1);
  const days = openEnded ? null : daysBetween(from, to);

  // Change the START — the booking MOVES. Length is preserved.
  function changeStart(next: string) {
    if (!next) return;
    const keep = openEnded ? null : daysBetween(from, to);
    setFrom(next);
    if (keep != null) {
      setTo(format(addDays(parseISO(next), keep - 1), "yyyy-MM-dd"));
    }
  }
  // Change the DAYS — the END moves. Start stays put.
  function changeDays(nextRaw: string) {
    const n = Math.max(1, Math.floor(Number(nextRaw) || 1));
    setTo(format(addDays(parseISO(from), n - 1), "yyyy-MM-dd"));
  }
  // Change the END — the DAYS change. Start stays put. An end before the start
  // is meaningless, so it clamps to a one-day booking rather than silently
  // inverting the dates.
  function changeEnd(next: string) {
    if (!next) return;
    setTo(next < from ? from : next);
  }

  async function save() {
    setSaving(true);
    setErr(null);
    const { error } = await (supabase as any)
      .from("project_crew_assignments")
      .update({
        crew_id: crewId,
        assigned_from: from,
        assigned_to: openEnded ? null : to,
        weekends_off: weekendsOff,   // mig 400
      })
      .eq("pca_id", booking.pca_id);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    qc.invalidateQueries({ queryKey: ["schedule_pcas"] });
    // 333 - roster rows that track the whole visit follow the new dates.
    qc.invalidateQueries({ queryKey: ["schedule_roster"] });
    qc.invalidateQueries({ queryKey: ["project_crew_assignments"] });
    onClose();
  }

  const movedCrew = crewId !== booking.crew_id;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}>
      <div className="w-full max-w-3xl rounded-lg border border-line bg-ink-card shadow-xl
                      max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-text truncate">{projectLabel}</div>
            <div className="text-[11px] text-text-muted">Crew booking</div>
          </div>
          <button type="button" onClick={onClose}
            className="p-1 rounded hover:bg-ink-hover text-text-muted"><X size={16} /></button>
        </div>

        {/* Two columns: the BOOKING on the left (crew + dates), the VISIT's
            people on the right with room to breathe. One modal, one visit,
            nothing stacked on top of anything - James, 27 Aug: "its bunched". */}
        <div className="p-4 grid gap-5 md:grid-cols-[250px_minmax(0,1fr)]">
        <div className="space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider2 text-text-dim">Crew</span>
            <select value={crewId} onChange={e => setCrewId(e.target.value)}
              className="mt-1 block w-full bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text">
              {crews.map(c => <option key={c.crew_id} value={c.crew_id}>{c.crew_name}</option>)}
            </select>
            {movedCrew && (
              <span className="text-[11px] text-warn">Moving this job to a different crew. Dates are unchanged.</span>
            )}
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wider2 text-text-dim">Start on site</span>
            <CommitDateInput value={from} onCommit={changeStart}
              className="mt-1 block w-full bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text" />
            <span className="text-[11px] text-text-dim">Changing this moves the whole booking and keeps its length.</span>
          </label>

          <label className="flex items-center gap-2 text-[12px] text-text-muted cursor-pointer select-none">
            <input type="checkbox" checked={openEnded} className="accent-accent"
              onChange={e => { setOpenEnded(e.target.checked); if (!e.target.checked) setTo(to < from ? from : to); }} />
            No finish date yet
          </label>

          {!openEnded && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider2 text-text-dim">Days on site</span>
                <input type="number" min={1} value={days ?? 1} onChange={e => changeDays(e.target.value)}
                  className="mt-1 block w-full bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider2 text-text-dim">Last day</span>
                <CommitDateInput value={to} min={from} onCommit={changeEnd}
                  className="mt-1 block w-full bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text" />
              </label>
            </div>
          )}

          {openEnded && (
            <div className="text-[11px] text-text-dim">
              For a crew that is on site with no agreed finish — the bar runs to the
              job's expected end and keeps moving until you set a last day.
            </div>
          )}

          {/* Mig 400 — a local Mon–Fri job: the crew goes home at the weekend.
              Weekends inside the bar are notched on the schedule and not counted
              as site days on anyone's My schedule. Per-person exceptions are
              set in the roster on the right. */}
          <label className="flex items-start gap-2 text-[12px] text-text-muted cursor-pointer select-none">
            <input type="checkbox" checked={weekendsOff} className="accent-accent mt-0.5"
              onChange={e => setWeekendsOff(e.target.checked)} />
            <span>
              Off on weekends (Mon–Fri job)
              <span className="block text-[10px] text-text-dim">
                Saturdays and Sundays inside the booking don't count as site days. Untick for a remote job that works through.
              </span>
            </span>
          </label>

          <div className="text-[11px] text-text-muted">
            {openEnded
              ? <>On site from {fmtDate(from)}, no finish date set.</>
              : <>{fmtDate(from)} → {fmtDate(to)} · {days} day{days === 1 ? "" : "s"} on site{
                  weekendsOff ? <> · {workingDays(from, to)} working (Mon–Fri)</> : null}.</>}
          </div>

          {err && <div className="text-[11px] text-danger">{err}</div>}
        </div>

        {/* 333/335 — the visit's ROSTER. Who is actually going, person by
            person: filled in from the crew automatically, owned here. This is
            the record the schedule draws and audits read. */}
        <VisitRoster
          pcaId={booking.pca_id}
          projectId={booking.projectId}
          crewId={crewId}
          bookingFrom={from}
          bookingTo={openEnded ? null : to}
          bookingWeekendsOff={weekendsOff}
        />
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-line">
          <Link to={`/projects/${booking.projectId}`}
            className="text-[11px] text-accent hover:underline inline-flex items-center gap-1">
            Open project <ExternalLink size={11} />
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// VisitRoster — who is on THIS visit, person by person (migration 333)
// =============================================================================
// Lives inside the BookingEditor because the booking IS the visit: click the
// site bar, and this is where you manage who is going. Seeded from the crew
// when the booking was made; owned per person from then on, so a ring-in from
// another crew, a tech leaving two days early, or a "listed" maybe (Clint on
// Sharon) are all one edit here - and none of them touch crew membership.
//
// THE RULE (James, 27 Aug 2026): "we don't need schedule changes prior to
// works starting recorded." Before the person's stint starts, the roster is a
// PLAN - Remove really removes. Once their stint has started it is a RECORD -
// Remove becomes "off site from today" and the history stays put.
// =============================================================================
interface VisitRosterRow {
  roster_id:    string;
  employee_id:  string;
  on_site_from: string;
  on_site_to:   string | null;
  status:       "rostered" | "listed";
  role_on_site: string | null;
  weekends_off: boolean | null;   // mig 400 — null = follow the booking
  employees: { first_name: string; last_name: string; preferred_name: string | null } | null;
}

function VisitRoster({
  pcaId, projectId, crewId, bookingFrom, bookingTo, bookingWeekendsOff,
}: {
  pcaId: string;
  projectId: string;
  crewId: string;             // the crew currently hosting the visit (live editor value)
  bookingFrom: string;        // live editor dates - defaults for people added here
  bookingTo: string | null;   // null = open-ended
  bookingWeekendsOff: boolean; // mig 400 — the block's setting (live editor value)
}) {
  const qc = useQueryClient();
  const todayKey = format(new Date(), "yyyy-MM-dd");
  const [err, setErr]       = useState<string | null>(null);
  const [busy, setBusy]     = useState(false);
  const [adding, setAdding] = useState(false);
  const [addEmp, setAddEmp] = useState("");
  const [addListed, setAddListed] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["visit_roster", pcaId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("site_roster")
        .select("roster_id, employee_id, on_site_from, on_site_to, status, role_on_site, weekends_off, employees:employee_id (first_name, last_name, preferred_name)")
        .eq("pca_id", pcaId)
        .order("on_site_from");
      if (error) throw error;
      return (data ?? []) as unknown as VisitRosterRow[];
    },
  });

  // The add-person picker - only fetched once it's opened.
  const { data: people = [] } = useQuery({
    queryKey: ["visit_roster_people"],
    enabled: adding,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, preferred_name")
        .eq("is_active", true)
        .eq("is_schedulable", true)
        .order("first_name");
      if (error) throw error;
      return (data ?? []) as { employee_id: string; first_name: string;
                               last_name: string; preferred_name: string | null }[];
    },
  });

  const pname = (e: VisitRosterRow["employees"]) =>
    e ? `${e.preferred_name?.trim() || e.first_name} ${e.last_name}`.trim() : "Unknown";

  // Every write funnels through here: one error slot, one pair of refetches
  // (this list + the calendar bars behind the modal).
  function done(error: { message: string } | null): boolean {
    setBusy(false);
    if (error) { setErr(error.message); return false; }
    setErr(null);
    qc.invalidateQueries({ queryKey: ["visit_roster", pcaId] });
    qc.invalidateQueries({ queryKey: ["schedule_roster"] });
    return true;
  }

  async function patch(rosterId: string, fields: Record<string, unknown>) {
    setBusy(true);
    const { error } = await (supabase as any).from("site_roster")
      .update(fields).eq("roster_id", rosterId);
    done(error);
  }

  async function removePerson(r: VisitRosterRow) {
    setBusy(true);
    if (r.on_site_from > todayKey) {
      // Plan - works haven't started for them; no record needed.
      const { error } = await (supabase as any).from("site_roster")
        .delete().eq("roster_id", r.roster_id);
      done(error);
    } else {
      // Record - they were there; end the stint today, keep the history.
      const { error } = await (supabase as any).from("site_roster")
        .update({ on_site_to: todayKey }).eq("roster_id", r.roster_id);
      done(error);
    }
  }

  async function addPerson() {
    if (!addEmp) return;
    setBusy(true);
    const { error } = await (supabase as any).from("site_roster").insert({
      project_id: projectId, pca_id: pcaId, employee_id: addEmp,
      on_site_from: bookingFrom > todayKey ? bookingFrom : todayKey,
      on_site_to: null,   // open = follows the booking's finish
      status: addListed ? "listed" : "rostered",
      source: "manual",
    });
    if (done(error)) { setAddEmp(""); setAddListed(false); setAdding(false); }
  }

  // Pull in whoever is in the hosting crew TODAY and not on the visit yet -
  // for a booking made before a new member joined the crew.
  async function syncFromCrew() {
    setBusy(true);
    const { data: cm, error: cmErr } = await supabase
      .from("crew_members")
      .select("employee_id")
      .eq("crew_id", crewId)
      .lte("member_from", todayKey)
      .or(`member_to.is.null,member_to.gte.${todayKey}`);
    if (cmErr) { done(cmErr); return; }
    const have = new Set(rows.map(r => r.employee_id));
    const missing = (cm ?? []).map(x => x.employee_id)
      .filter((id): id is string => !!id && !have.has(id));
    if (missing.length === 0) {
      setBusy(false);
      setErr("Everyone currently in the crew is already on this visit.");
      return;
    }
    const { error } = await (supabase as any).from("site_roster").insert(
      missing.map(id => ({
        project_id: projectId, pca_id: pcaId, employee_id: id,
        on_site_from: bookingFrom > todayKey ? bookingFrom : todayKey,
        on_site_to: null,
        status: "rostered", source: "manual", seeded_from_crew_id: crewId,
      })));
    done(error);
  }

  const counted = rows.filter(r => r.status === "rostered").length;
  const listed  = rows.filter(r => r.status === "listed").length;

  return (
    <div className="rounded border border-line/70 bg-ink/40 flex flex-col min-h-0">
      <div className="flex items-center justify-between px-3 py-2 border-b border-line/50">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider2 text-text-dim">
          <Users size={11} /> Who's going
          <span className="normal-case tracking-normal text-text-muted">
            · {counted} on site{listed > 0 ? ` · ${listed} listed` : ""}
          </span>
        </div>
        <div className="inline-flex items-center gap-1.5">
          <button type="button" disabled={busy} onClick={syncFromCrew}
            title="Adds anyone in the crew who isn't on this visit yet. Never removes anyone."
            className="text-[10px] px-2 py-1 rounded border border-line text-text-muted hover:bg-ink-hover">
            Add missing crew members
          </button>
          <button type="button" disabled={busy} onClick={() => setAdding(a => !a)}
            className="text-[10px] px-2 py-1 rounded border border-accent/50 text-accent hover:bg-ink-hover inline-flex items-center gap-1">
            <Plus size={10} /> Add person
          </button>
        </div>
      </div>

      {adding && (
        <div className="flex items-center gap-2 px-2.5 py-2 border-b border-line/40 bg-ink-raised/30">
          <select value={addEmp} onChange={e => setAddEmp(e.target.value)}
            className="flex-1 bg-ink border border-line rounded px-2 py-1 text-[12px] text-text">
            <option value="">Pick a person…</option>
            {people
              .filter(p => !rows.some(r => r.employee_id === p.employee_id
                                        && (r.on_site_to == null || r.on_site_to >= todayKey)))
              .map(p => (
                <option key={p.employee_id} value={p.employee_id}>
                  {`${p.preferred_name?.trim() || p.first_name} ${p.last_name}`.trim()}
                </option>
              ))}
          </select>
          <label className="inline-flex items-center gap-1 text-[11px] text-text-muted cursor-pointer select-none"
            title="On the list and has project access, but may not attend - not counted on site">
            <input type="checkbox" checked={addListed} className="accent-accent"
              onChange={e => setAddListed(e.target.checked)} />
            Listed only
          </label>
          <Button variant="primary" size="sm" disabled={busy || !addEmp} onClick={addPerson}>Add</Button>
        </div>
      )}

      {isLoading ? (
        <div className="px-3 py-3 text-[11px] text-text-dim">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="px-3 py-4 text-[12px] text-text-dim">
          Nobody on this visit yet. Booking a crew fills this in automatically —
          or use <span className="text-text-muted">Add person</span> to roster anyone.
        </div>
      ) : (
        <>
          {/* Column headers once, instead of a tooltip hidden on every input. */}
          <div className="grid grid-cols-[56px_minmax(0,1fr)_100px_100px_44px_24px] gap-2 px-3 pt-2 pb-1
                          text-[9px] uppercase tracking-wider2 text-text-dim">
            <span></span><span>Who</span><span>First day</span><span>Last day</span>
            <span title="Weekends: as the booking, Mon–Fri only, or 7 days">Wknd</span><span></span>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-line/30">
            {rows.map(r => {
              const startedStint = r.on_site_from <= todayKey;
              const ended = r.on_site_to != null && r.on_site_to < todayKey;
              return (
                <div key={r.roster_id}
                  className={`grid grid-cols-[56px_minmax(0,1fr)_100px_100px_44px_24px] gap-2 items-center px-3 py-2 ${
                    ended ? "opacity-55" : ""}`}>
                  <button type="button" disabled={busy}
                    onClick={() => patch(r.roster_id,
                      { status: r.status === "listed" ? "rostered" : "listed" })}
                    title={r.status === "listed"
                      ? "LISTED = standby: has project access, may attend, not counted on site. Click to put them ON."
                      : "ON this visit. Click to make them LISTED (standby - access, not counted)."}
                    className={`text-[9px] px-1.5 py-0.5 rounded-full border text-center ${
                      r.status === "listed"
                        ? "border-dashed border-text-dim/70 text-text-dim"
                        : "border-accent/60 text-accent"}`}>
                    {r.status === "listed" ? "listed" : "on"}
                  </button>
                  <span className="text-[12px] text-text truncate min-w-0">
                    {pname(r.employees)}
                    {r.role_on_site && <span className="text-text-dim"> · {r.role_on_site}</span>}
                    {ended && <span className="text-text-dim"> · off site {fmtDate(r.on_site_to!)}</span>}
                  </span>
                  <CommitDateInput value={r.on_site_from} disabled={busy}
                    onCommit={(v) => { if (v) patch(r.roster_id, { on_site_from: v,
                      ...(r.on_site_to && r.on_site_to < v ? { on_site_to: v } : {}) }); }}
                    className="bg-ink border border-line rounded px-1.5 py-1 text-[11px] text-text w-full" />
                  <CommitDateInput value={r.on_site_to ?? ""} disabled={busy}
                    min={r.on_site_from}
                    onCommit={(v) => patch(r.roster_id,
                      { on_site_to: v ? (v < r.on_site_from ? r.on_site_from : v) : null })}
                    className="bg-ink border border-line rounded px-1.5 py-1 text-[11px] text-text w-full"
                    placeholder="—" />
                  {/* Mig 400 — per-person weekends: follow the booking → Mon–Fri
                      → 7 days → back. Null means "as the booking", which is
                      what nearly everyone is; only the exceptions get a value. */}
                  <button type="button" disabled={busy}
                    onClick={() => patch(r.roster_id, {
                      weekends_off: r.weekends_off == null ? true : r.weekends_off ? false : null })}
                    title={r.weekends_off == null
                      ? `As the booking (${bookingWeekendsOff ? "Mon–Fri" : "7 days"}). Click: Mon–Fri only for this person.`
                      : r.weekends_off
                        ? "Mon–Fri only for this person. Click: works 7 days."
                        : "Works 7 days (even if the booking is Mon–Fri). Click: back to as-the-booking."}
                    className={`text-[9px] px-1 py-0.5 rounded border text-center whitespace-nowrap ${
                      r.weekends_off == null
                        ? "border-line text-text-dim"
                        : r.weekends_off ? "border-accent/60 text-accent" : "border-warn/60 text-warn"}`}>
                    {r.weekends_off == null ? (bookingWeekendsOff ? "M–F" : "7d") + " ·" : r.weekends_off ? "M–F" : "7d"}
                  </button>
                  <button type="button" disabled={busy} onClick={() => removePerson(r)}
                    title={startedStint
                      ? "Take them off site from today (their history on this visit stays)"
                      : "Remove from the plan (works haven't started - nothing is recorded)"}
                    className="p-1 rounded text-text-dim hover:text-danger hover:bg-ink-hover justify-self-center">
                    <Trash2 size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {err && <div className="px-3 py-1.5 text-[11px] text-danger border-t border-line/40">{err}</div>}

      <div className="px-3 py-1.5 text-[10px] text-text-dim border-t border-line/40 mt-auto">
        Blank last day = stays for the whole booking{bookingTo ? ` (to ${fmtDate(bookingTo)})` : ""}.
        Moving someone between crews updates this for you — this panel is for the exceptions.
      </div>
    </div>
  );
}

// =============================================================================
// ScheduleAxis / ScheduleBackdrop — the shared time ruler
// =============================================================================
// James, 21 Aug 2026: "the date header is nice, lets get that into gantt as well."
//
// Three tabs compute gridStart / totalDays / weeks identically, so there is no
// reason for three different headers. Two rows: the week (ISO number and the
// Monday's date) over the individual day numbers, with weekends dimmed - because
// you cannot drop a block on a day you cannot see.
//
// labelCol is the width of the left-hand name column, and label is what sits in
// it, so the Crews tab can say "crew" and the Gantt "project · site" without
// forking the component.
// =============================================================================
// =============================================================================
// Public holidays on the ruler — James, 25 Sep 2026: "add in public holidays in
// the schedule so we can see upcoming holidays."
// =============================================================================
// The `holidays` table has been in the schema since the SharePoint HolidayList
// came across (app.working_days_between already reads it); nothing on screen
// ever did. One cached query per visible window, keyed by the grid's first and
// last day, shared by every axis and every row backdrop through react-query -
// so a screen with sixty person rows still asks once. A day with a holiday is
// washed amber behind the blocks and named on the ruler, the way a weekend is
// dimmed: you should not book a crew onto a day you cannot see is a holiday.
// Every state in the table shows; the name carries the state so a NSW day on
// a QLD crew reads as what it is.
// =============================================================================
const HOLIDAY_WASH   = "rgba(234,179,8,0.18)";   // amber, behind the blocks
const HOLIDAY_STRIPE = "rgba(234,179,8,0.85)";   // the line under the day number

type HolidayRow = { holiday_date: string; holiday_name: string; state: string };

function useHolidays(gridStart: Date, totalDays: number): Map<string, HolidayRow[]> {
  const fromKey = format(gridStart, "yyyy-MM-dd");
  const toKey   = format(addDays(gridStart, Math.max(0, totalDays - 1)), "yyyy-MM-dd");
  const { data } = useQuery({
    queryKey: ["schedule_holidays", fromKey, toKey],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("holidays")
        .select("holiday_date, holiday_name, state")
        .gte("holiday_date", fromKey)
        .lte("holiday_date", toKey)
        .order("holiday_date");
      if (error) throw error;
      return (data ?? []) as HolidayRow[];
    },
  });
  return useMemo(() => {
    const m = new Map<string, HolidayRow[]>();
    for (const h of data ?? []) {
      const k = String(h.holiday_date).slice(0, 10);
      const list = m.get(k) ?? [];
      list.push(h);
      m.set(k, list);
    }
    return m;
  }, [data]);
}
function holidayLabel(list: HolidayRow[] | undefined): string | null {
  if (!list?.length) return null;
  return list.map(h => `${h.holiday_name} (${h.state})`).join(" · ");
}

function ScheduleAxis({
  weeks, gridStart, totalDays, labelCol, label, highlightIdx, highlightEndIdx,
}: {
  weeks: Date[][];
  gridStart: Date;
  totalDays: number;
  labelCol: string;
  label: string;
  // James, 22 Aug: "when dragging can we highlight the above date in the header
  // with a colour so we can see where we are extending the dates / moving this
  // to." The bar under the pointer already previews the landing - but your eye
  // is on the bar, and the DATE is up here. Both ends of the drag light up:
  // start index always, end index when a whole block is moving.
  highlightIdx?: number | null;
  highlightEndIdx?: number | null;
}) {
  const cols = `${labelCol} repeat(${totalDays}, minmax(8px, 1fr))`;
  const holidays = useHolidays(gridStart, totalDays);   // 25 Sep 2026
  const isHot = (i: number) =>
    (highlightIdx != null && i === highlightIdx)
    || (highlightEndIdx != null && i === highlightEndIdx);
  // The days in between get a soft wash so the whole landing window reads
  // as one span, not two lit ends with a gap.
  const isWithin = (i: number) =>
    highlightIdx != null && highlightEndIdx != null
    && i > Math.min(highlightIdx, highlightEndIdx) && i < Math.max(highlightIdx, highlightEndIdx);
  return (
    <div className="border-b border-line/50">
      {/* Week row */}
      <div className="grid items-center text-[9px] text-text-muted font-mono pt-2 px-3"
        style={{ gridTemplateColumns: cols }}>
        <div className="text-[10px] uppercase tracking-wider2 text-text-muted">{label}</div>
        {weeks.map((w, wi) => (
          <div key={wi}
            className="text-center text-[9px] text-text-dim border-l border-line/40"
            style={{ gridColumn: `${wi * 7 + 2} / span 7` }}>
            W{getISOWeek(w[0])} · {format(w[0], "d MMM")}
          </div>
        ))}
      </div>
      {/* Day row */}
      <div className="grid items-center text-[9px] font-mono pb-2 px-3"
        style={{ gridTemplateColumns: cols }}>
        <div />
        {Array.from({ length: totalDays }).map((_, i) => {
          const d = addDays(gridStart, i);
          const weekend = d.getDay() === 0 || d.getDay() === 6;
          const monday  = d.getDay() === 1;
          const hot = isHot(i);
          const within = !hot && isWithin(i);
          // 1 Sep 2026 - James: "make the full date block highlight and
          // expand or retract as you drag, a slightly lighter colour". One
          // continuous lighter band across the landing window; the two ends
          // just read bolder so the exact first/last day is still obvious.
          const lit = hot || within;
          const first = hot && (highlightIdx == null || highlightEndIdx == null || i === Math.min(highlightIdx, highlightEndIdx));
          const last  = hot && (highlightIdx == null || highlightEndIdx == null || i === Math.max(highlightIdx, highlightEndIdx));
          // 25 Sep 2026 - a public holiday: amber wash and a line under the
          // number, the name on hover. A drag highlight still wins visually.
          const hol = holidayLabel(holidays.get(format(d, "yyyy-MM-dd")));
          return (
            <div key={i}
              title={hol ?? undefined}
              className={`text-center transition-colors ${
                hot ? "text-text font-semibold"
                    : within ? "text-text"
                    : hol ? "text-warn font-semibold"
                    : weekend ? "text-text-dim" : "text-text-muted"}`}
              style={{
                borderLeft: monday && !lit ? "1px solid rgba(51,65,85,0.16)" : "none",
                background: lit ? "color-mix(in srgb, var(--accent, #e2661a) 38%, transparent)"
                          : hol ? HOLIDAY_WASH : undefined,
                borderTopLeftRadius: first ? 4 : 0, borderBottomLeftRadius: first ? 4 : 0,
                borderTopRightRadius: last ? 4 : 0, borderBottomRightRadius: last ? 4 : 0,
                boxShadow: hot ? "inset 0 -2px 0 var(--accent, #e2661a)"
                         : hol ? `inset 0 -2px 0 ${HOLIDAY_STRIPE}` : undefined,
              }}>
              <span className="block text-[7.5px] leading-none opacity-70">{format(d, "EEEEE")}</span>
              {format(d, "d")}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Week gridlines + weekend shading behind the blocks, so a bar's start and end
// can be read off the row without counting pixels. Absolutely positioned, so it
// goes inside a `relative` lane.
function ScheduleBackdrop({ gridStart, totalDays }: { gridStart: Date; totalDays: number }) {
  const holidays = useHolidays(gridStart, totalDays);   // 25 Sep 2026 - cached, one fetch per window
  return (
    <>
      {Array.from({ length: totalDays }).map((_, i) => {
        const d = addDays(gridStart, i);
        const weekend = d.getDay() === 0 || d.getDay() === 6;
        const monday  = d.getDay() === 1;
        const holiday = holidays.has(format(d, "yyyy-MM-dd"));
        if (!weekend && !monday && !holiday) return null;
        return (
          <div key={`bg-${i}`} className="absolute top-0 bottom-0 pointer-events-none"
            style={{
              left:  `calc(${(i / totalDays) * 100}%)`,
              width: `calc(${(1 / totalDays) * 100}%)`,
              // A holiday reads over a weekend: it is the thing you must not
              // miss, and a Saturday you already were not booking.
              background: holiday ? HOLIDAY_WASH : weekend ? "rgba(51,65,85,0.065)" : undefined,
              borderLeft: monday ? "1px solid rgba(51,65,85,0.16)" : undefined,
            }} />
        );
      })}
    </>
  );
}

// "so we can see upcoming holidays" - the next few from today, in one line
// under the month switcher, whatever month is on screen.
function UpcomingHolidays() {
  const todayKey = format(new Date(), "yyyy-MM-dd");
  const { data } = useQuery({
    queryKey: ["schedule_holidays_upcoming", todayKey],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("holidays")
        .select("holiday_date, holiday_name, state")
        .gte("holiday_date", todayKey)
        .order("holiday_date")
        .limit(4);
      if (error) throw error;
      return (data ?? []) as HolidayRow[];
    },
  });
  if (!data?.length) return null;
  return (
    <div className="mt-2 text-[11px] text-text-muted flex items-center gap-2 flex-wrap">
      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: HOLIDAY_WASH, boxShadow: `inset 0 -2px 0 ${HOLIDAY_STRIPE}` }}/>
      <span className="text-text-dim">Public holidays coming up:</span>
      {data.map((h, i) => (
        <span key={`${h.holiday_date}-${h.state}-${i}`} className="inline-flex items-center gap-1">
          <span className="font-medium text-text">{h.holiday_name}</span>
          <span className="font-mono">{format(parseISO(h.holiday_date), "EEE d MMM")}</span>
          <span className="text-text-dim">({h.state})</span>
          {i < data.length - 1 && <span className="text-text-dim">·</span>}
        </span>
      ))}
    </div>
  );
}

// =============================================================================
// CrewLaneView — ONE ROW PER CREW. The management surface.
// =============================================================================
// James, 19 Aug 2026: "have the team on the left and the site in the blocks that
// can be dragged and moved between crews, that way the crew stays constant and
// we can see which sites they worked on… have a crew view and that is where we
// manage, move and change, then the calendar and gantt reflect the changes."
//
// Right call, and a better model than dragging on the Gantt:
//   * what is actually being scheduled is a CREW'S TIME, so the crew is the row
//   * a clash is two blocks overlapping in one row - visible, not a banner
//   * an empty row answers "who is free next week", which project-per-row cannot
//   * moving a job from RIG-A to RIG-B is a drag between rows; there is no way
//     to express that on a project row at all
//
// Read-only in this pass. Drag to move dates, drag between rows to change crew,
// and drag the edges to resize all land here next.
// =============================================================================
type BookingDropTarget = { pcaId: string; projectId: string; crewId: string; label: string; from: string; to: string | null };

// -----------------------------------------------------------------------------
// 3 Nov 2026 — Send a person to a job for a stint. One roster row on that
// booking (source manual); their crew is untouched; access follows the stint
// (app.is_on_project). From = the day they were dropped on; to = the booking's
// finish by default, or any day.
// -----------------------------------------------------------------------------
function SendToJobModal({ s, crews, onClose, onSaved }: {
  s: { employeeId: string; name: string; crewId: string | null; target: BookingDropTarget; dayKey: string };
  crews: { crew_id: string; crew_name: string; colour_hex: string }[];
  onClose: () => void; onSaved: () => void;
}) {
  const todayKey = format(new Date(), "yyyy-MM-dd");
  const [from, setFrom] = useState(s.dayKey < s.target.from ? s.target.from : s.dayKey);
  const [to, setTo] = useState(s.target.to ?? s.dayKey);
  const [role, setRole] = useState("");
  const [listed, setListed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const hostCrew = crews.find(c => c.crew_id === s.target.crewId);
  const homeCrew = s.crewId ? crews.find(c => c.crew_id === s.crewId) : null;
  async function save() {
    if (!from || !to || to < from) { setErr("The finish must be on or after the start."); return; }
    setBusy(true); setErr(null);
    try {
      const { error } = await (supabase as any).from("site_roster").insert({
        project_id: s.target.projectId, pca_id: s.target.pcaId, employee_id: s.employeeId,
        on_site_from: from, on_site_to: to, status: listed ? "listed" : "rostered",
        role_on_site: role.trim() || null, source: "manual",
        note: `Sent to the job from the schedule${homeCrew ? ` (home crew ${homeCrew.crew_name})` : " (from the pool)"}`,
      });
      if (error) throw error;
      onSaved();
    } catch (e: any) { setErr(e?.message ?? String(e)); setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-line">
          <div className="font-semibold text-[15px]">Send {s.name} to {s.target.label}</div>
          <div className="text-[12px] text-text-muted mt-0.5">
            {hostCrew ? <>With <span className="inline-block w-2 h-2 rounded-full align-middle mr-1" style={{ background: hostCrew.colour_hex }}/>{hostCrew.crew_name}'s job</> : "On this job"} · {homeCrew ? `${s.name} stays in ${homeCrew.crew_name}` : `${s.name} is not in a crew`} · they get the site diary, documents and site details for these days (and the usual tail after).
          </div>
        </div>
        <div className="p-4 space-y-3 text-[13px]">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-[10px] uppercase tracking-wider2 text-text-muted block mb-1">From</span><input type="date" value={from} onChange={e => setFrom(e.target.value)} className="w-full bg-ink border border-line rounded px-2 py-1.5 text-text"/></label>
            <label className="block"><span className="text-[10px] uppercase tracking-wider2 text-text-muted block mb-1">To</span><input type="date" value={to} min={from} onChange={e => setTo(e.target.value)} className="w-full bg-ink border border-line rounded px-2 py-1.5 text-text"/></label>
          </div>
          <div className="text-[11px] text-text-dim">The job runs {fmtDate(s.target.from)} → {s.target.to ? fmtDate(s.target.to) : "open"}. {from < todayKey ? "This starts in the past — recording days already worked." : ""}</div>
          <label className="block"><span className="text-[10px] uppercase tracking-wider2 text-text-muted block mb-1">Role on site (optional)</span><input value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. site lead, rigger, cover" className="w-full bg-ink border border-line rounded px-2 py-1.5 text-text"/></label>
          <label className="inline-flex items-center gap-2 cursor-pointer text-[12px]"><input type="checkbox" checked={listed} onChange={e => setListed(e.target.checked)} className="accent-accent"/> Listed only (standby — has access, not counted on site)</label>
          {err && <div className="text-[12px] text-danger">{err}</div>}
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy}>{busy ? "Saving…" : "Send them"}</Button>
        </div>
      </div>
    </div>
  );
}

function CrewLaneView({
  crews, leadNameById, assignments, projects, allProjects, weeks, onSelectProject,
  crewMembers, personalBookings, leaveRows, projectTeam, schedulablePeople,
  defectRows, rosterRows, onSelectPerson, onSelectTask,
}: {
  crews: { crew_id: string; crew_code: string; crew_name: string; colour_hex: string;
           crew_kind: string; lead_employee_id: string | null }[];
  leadNameById: Map<string, string>;
  assignments: CrewAssignmentRow[];
  projects: ProjectRow[];
  allProjects: ProjectRow[];
  weeks: Date[][];
  onSelectProject: (p: ProjectRow) => void;
  crewMembers: CrewMemberRow[];
  personalBookings: PersonalBookingRow[];
  leaveRows: LeaveRow[];
  projectTeam: ProjectTeamRow[];
  schedulablePeople: SchedulablePerson[];
  defectRows: DefectRow[];
  rosterRows: RosterRow[];
  // Mig 400 — an optional LAST day: a sweep across empty days books the run.
  onSelectPerson: (employeeId: string, name: string, dateKey?: string, endKey?: string) => void;
  onSelectTask: (pb: PersonalBookingRow) => void;
}) {
  const gridStart    = weeks[0][0];
  const gridEnd      = weeks[5][6];
  const totalDays    = differenceInCalendarDays(gridEnd, gridStart) + 1;
  const gridStartKey = format(gridStart, "yyyy-MM-dd");
  const gridEndKey   = format(gridEnd,   "yyyy-MM-dd");

  const projById = useMemo(
    () => new Map(allProjects.map(p => [p.project_id, p])),
    [allProjects],
  );

  function clipIdx(key: string): number {
    if (key < gridStartKey) return 0;
    if (key > gridEndKey)   return totalDays - 1;
    return differenceInCalendarDays(parseISO(key), gridStart);
  }

  const todayIdx = differenceInCalendarDays(new Date(), gridStart);
  const todayInRange = todayIdx >= 0 && todayIdx < totalDays;
  const TODAY_KEY = format(new Date(), "yyyy-MM-dd");   // mig 400 — the line the past sits behind

  // A crew that is real but quiet still clutters the view. Not the same as
  // archiving it - it is active, just not on anything this month.
  const [hideIdle, setHideIdle] = useState(false);

  // Which booking is open in the editor. Clicking a block in THIS view edits the
  // booking - that is what the view is for. The project itself is one click
  // further in, from inside the editor.
  const [editing, setEditing] = useState<
    { pca_id: string; crew_id: string; from: string; to: string | null; projectId: string; label: string;
      weekendsOff?: boolean } | null
  >(null);

  // ---- DRAG ---------------------------------------------------------------
  // Drag sets exactly the same three values the editor does, which is why the
  // editor was built first: the rule is already proven.
  //   grab the middle -> MOVE. Start shifts, length preserved.
  //   grab an edge    -> RESIZE. The other end stays put.
  //   drop on another crew row -> reassign, dates unchanged.
  //
  // Saves on drop rather than asking to confirm. A booking date is reversible -
  // drag it back, or open the editor and type it - so a confirm on every drag
  // would be friction for no safety. Manager+ only.
  const qcDrag = useQueryClient();
  const dragUser = useRequireAuth();
  const canDrag = hasRole(dragUser, "manager");

  // A drag always ends with a pointerup, and the browser then fires a CLICK on
  // the same element. Without this the editor opened straight after every drag,
  // holding the dates from BEFORE the drag - so saving it silently undid the
  // move, and cancelling was the only way to keep it. Found by James, 19 Aug.
  const draggedRef = useRef(false);

  const [drag, setDrag] = useState<null | {
    pcaId: string; mode: "move" | "start" | "end";
    fromKey: string; toKey: string; openEnded: boolean;
    crewId: string; startX: number; dayW: number;
    dxDays: number; overCrew: string | null;
    plan?: boolean;   // mig 401 — a planning block, not a booking
  }>(null);

  const dayKey = (base: string, delta: number) =>
    format(addDays(parseISO(base), delta), "yyyy-MM-dd");

  // Where the pointer is vertically decides the crew. Read off the DOM rather
  // than tracked in state - rows can scroll under the cursor mid-drag.
  function crewAtPoint(x: number, y: number): string | null {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    const row = el?.closest("[data-crew-row]") as HTMLElement | null;
    return row?.getAttribute("data-crew-row") ?? null;
  }

  useEffect(() => {
    if (!drag) return;
    function onMove(e: PointerEvent) {
      setDrag(d => d && ({
        ...d,
        dxDays: Math.round((e.clientX - d.startX) / d.dayW),
        overCrew: crewAtPoint(e.clientX, e.clientY),
      }));
    }
    async function onUp() {
      const d = drag;
      setDrag(null);
      if (!d) return;
      // Anything that actually moved suppresses the click that follows.
      if (d.dxDays !== 0 || (d.overCrew && d.overCrew !== d.crewId)) {
        draggedRef.current = true;
      }

      let newFrom = d.fromKey;
      let newTo: string | null = d.openEnded ? null : d.toKey;
      if (d.mode === "move") {
        newFrom = dayKey(d.fromKey, d.dxDays);
        if (!d.openEnded) newTo = dayKey(d.toKey, d.dxDays);
      } else if (d.mode === "start") {
        // Resizing from the left edge is the ONE case where the start moves
        // without the booking moving - the last day stays where it is.
        const candidate = dayKey(d.fromKey, d.dxDays);
        newFrom = (!d.openEnded && candidate > d.toKey) ? d.toKey : candidate;
      } else {
        if (d.openEnded) return;                        // nothing to drag
        const candidate = dayKey(d.toKey, d.dxDays);
        newTo = candidate < d.fromKey ? d.fromKey : candidate;
      }
      const newCrew = d.overCrew ?? d.crewId;
      const unchanged = newFrom === d.fromKey
        && (newTo ?? null) === (d.openEnded ? null : d.toKey)
        && newCrew === d.crewId;
      if (unchanged) return;

      // Mig 401 — a planning block is a pencil line, not a record: it just moves.
      if (d.plan) {
        const { error } = await (supabase as any)
          .from("crew_plan_blocks")
          .update({ crew_id: newCrew, plan_from: newFrom, plan_to: newTo ?? newFrom })
          .eq("block_id", d.pcaId);
        if (error) window.alert(`Could not move that planning block: ${error.message}`);
        qcDrag.invalidateQueries({ queryKey: ["schedule_plan_blocks"] });
        return;
      }

      // Mig 400 — THE PAST IS A RECORD (James, 29 Sep: "past days should stay
      // where they were, or the drag should split at today"). A booking that
      // started before today:
      //   * finished already      -> not draggable; the editor corrects records
      //   * left edge (the start) -> not draggable; the start is history
      //   * moved / re-crewed     -> SPLIT AT TODAY: worked days stay put on the
      //                              crew that worked them, the rest moves as a
      //                              new booking (app.split_booking_from_today)
      //   * right edge            -> fine, the finish is the future
      const started = d.fromKey < TODAY_KEY && !editPast;   // 3 Nov 2026 — a director with "Edit the past" on moves the record whole
      const finished = !d.openEnded && d.toKey < TODAY_KEY;
      if (started && (d.mode === "move" || newCrew !== d.crewId)) {
        if (finished) {
          window.alert("That booking has already finished - it is history and stays where it was.\nOpen it and correct the dates if the record is wrong.");
          return;
        }
        const { error } = await (supabase as any).schema("app")
          .rpc("split_booking_from_today", {
            p_pca_id: d.pcaId, p_delta_days: d.dxDays,
            p_new_crew_id: newCrew !== d.crewId ? newCrew : null,
          });
        if (error) { window.alert(`Could not move that booking: ${error.message}`); }
        qcDrag.invalidateQueries({ queryKey: ["schedule_pcas"] });
        qcDrag.invalidateQueries({ queryKey: ["schedule_roster"] });
        qcDrag.invalidateQueries({ queryKey: ["visit_roster"] });
        qcDrag.invalidateQueries({ queryKey: ["project_crew_assignments"] });
        return;
      }
      if (started && d.mode === "start") {
        window.alert("That booking has already started - the start date is history and stays where it was.\nDrag the finish, or open the booking to correct the record.");
        return;
      }

      const { error } = await supabase
        .from("project_crew_assignments")
        .update({ crew_id: newCrew, assigned_from: newFrom, assigned_to: newTo })
        .eq("pca_id", d.pcaId);
      if (error) { window.alert(`Could not save that move: ${error.message}`); }
      qcDrag.invalidateQueries({ queryKey: ["schedule_pcas"] });
      // 333 - the roster follows the booking (whole-visit rows track the drag,
      // custom stints shift with a pure move), so refetch it too.
      qcDrag.invalidateQueries({ queryKey: ["schedule_roster"] });
      // The visit editor and the project panel read the same booking.
      qcDrag.invalidateQueries({ queryKey: ["visit_roster"] });
      qcDrag.invalidateQueries({ queryKey: ["project_crew_assignments"] });
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [drag, qcDrag]);

  // ---------------------------------------------------------------------------
  // Dragging a PERSON between crews.
  // ---------------------------------------------------------------------------
  // James, 21 Aug: "how do we drag out and split up the crews?" and, earlier,
  // "we will need to make it so employees move from crew to crew on dates so
  // they nest into different sites or stand alone and get their own colour, the
  // history then you can drag back and see the site names the employee worked
  // on and the colour + the crew they were in on that job."
  //
  // THE GESTURE CARRIES THE DATE. Dropping vertically on a crew says which crew;
  // where you land horizontally says from when. That matters because a crew move
  // without a date is not a fact - it is a guess that quietly rewrites history.
  // A confirm dialog asking "from which date?" would have been the alternative,
  // and it would have been a worse answer: the axis is already on screen.
  //
  // Underneath it is exactly what the columns were built for - close one dated
  // membership, open another. Nothing is overwritten, so dragging somebody back
  // leaves both stints in the record rather than erasing the first.
  const [personDrag, setPersonDrag] = useState<null | {
    employeeId: string; name: string;
    crewId: string | null; crewMemberId: string | null;
    memberFrom: string | null; crewRole: string;
    gridLeft: number; dayW: number;
    x: number; y: number;
    overCrew: string | null; dayIdx: number;
    /** 3 Nov 2026 — the pointer is over a JOB BAR: the drop sends them to that job for a stint, crew untouched */
    overBooking: BookingDropTarget | null;
  }>(null);
  // 3 Nov 2026 — James: "people are currently getting put all over the place …
  // Crews with people hard set but sites get set on crews, then we move people
  // in … you get dragged into the crew when the build starts … after the job
  // we return to pool." The model already had it (333/335: the roster is the
  // person's record, the crew is the seed) - the GESTURE did not: the only
  // thing you could do with a person was move their crew. Now:
  //   * drop a person on a JOB BAR  -> a stint on that job (site_roster, manual),
  //     their crew untouched; they come back to their crew's own jobs after.
  //   * drop a person on a CREW ROW -> the crew move as before, with a confirm.
  // And for directors, "Edit the past": finished bars and stints drag again
  // and a started one moves whole instead of splitting at today - the guys
  // worked days the tool was not used for, and the record has to be fixed.
  const [sendTo, setSendTo] = useState<null | { employeeId: string; name: string; crewId: string | null; target: BookingDropTarget; dayKey: string }>(null);
  const isDirector = hasRole(dragUser, "director");
  const [editPast, setEditPast] = useState(false);

  function beginPersonDrag(
    e: ReactPointerEvent,
    p: { employeeId: string; name: string; crewId: string | null;
         crewMemberId: string | null; memberFrom: string | null; crewRole: string },
  ) {
    if (!canDrag) return;
    e.preventDefault();
    e.stopPropagation();
    // Every lane on this grid shares one column template, so any lane gives the
    // geometry. Read from the DOM rather than tracked in state because the grid
    // is horizontally scrollable and a stored width goes stale.
    const lane = document.querySelector("[data-lane]") as HTMLElement | null;
    if (!lane) return;
    const r = lane.getBoundingClientRect();
    const dayW = r.width / totalDays;
    setPersonDrag({
      ...p,
      gridLeft: r.left, dayW,
      x: e.clientX, y: e.clientY,
      overCrew: p.crewId, overBooking: null,
      dayIdx: Math.min(totalDays - 1, Math.max(0, Math.floor((e.clientX - r.left) / dayW))),
    });
  }

  // ---------------------------------------------------------------------------
  // Dragging an UNBOOKED PROJECT onto a crew - James, 22 Aug: "can we include
  // the needs-a-crew projects... the ones in planning but not allocated can't
  // be dragged or moved but the crew bars can, why not all."
  //
  // Right - the queue row held the exact jobs this screen exists to allocate,
  // and they were the one thing on it you could not drag. Dropping one on a
  // crew books that crew for the project's planned window (the dates already
  // live on the project; the drop decides WHO, not WHEN - moving the dates
  // afterwards is the drag that already existed on the booked bar).
  // ---------------------------------------------------------------------------
  const [projDrag, setProjDrag] = useState<null | {
    projectId: string; label: string; from: string; to: string | null;
    x: number; y: number; overCrew: string | null;
  }>(null);

  function beginProjDrag(
    e: ReactPointerEvent,
    p: { projectId: string; label: string; from: string; to: string | null },
  ) {
    if (!canDrag) return;
    e.preventDefault();
    setProjDrag({ ...p, x: e.clientX, y: e.clientY, overCrew: null });
  }

  useEffect(() => {
    if (!projDrag) return;
    function onMove(e: PointerEvent) {
      setProjDrag(d => d && ({
        ...d, x: e.clientX, y: e.clientY,
        overCrew: crewDropAtPoint(e.clientX, e.clientY),
      }));
    }
    async function onUp() {
      const d = projDrag;
      setProjDrag(null);
      if (!d) return;
      draggedRef.current = true;   // swallow the click that follows
      const crewId = d.overCrew;
      if (!crewId || crewId === STANDALONE) return;   // missed - not an instruction
      const { error } = await supabase.from("project_crew_assignments").insert({
        project_id: d.projectId, crew_id: crewId,
        assigned_from: d.from, assigned_to: d.to,
      });
      if (error) window.alert(`Could not book that crew: ${error.message}`);
      qcDrag.invalidateQueries({ queryKey: ["schedule_pcas"] });
      // 333 - booking a crew seeds its current members onto the roster.
      qcDrag.invalidateQueries({ queryKey: ["schedule_roster"] });
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [projDrag, qcDrag]);

  function crewDropAtPoint(x: number, y: number): string | null {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    const row = el?.closest("[data-crew-drop]") as HTMLElement | null;
    return row?.getAttribute("data-crew-drop") ?? null;
  }
  function bookingDropAtPoint(x: number, y: number): BookingDropTarget | null {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    const bar = el?.closest("[data-booking-drop]") as HTMLElement | null;
    if (!bar) return null;
    return {
      pcaId: bar.getAttribute("data-booking-drop")!, projectId: bar.getAttribute("data-booking-project") ?? "",
      crewId: bar.getAttribute("data-booking-crew") ?? "", label: bar.getAttribute("data-booking-label") ?? "",
      from: bar.getAttribute("data-booking-from") ?? "", to: bar.getAttribute("data-booking-to") || null,
    };
  }

  useEffect(() => {
    if (!personDrag) return;
    function onMove(e: PointerEvent) {
      setPersonDrag(d => d && ({
        ...d,
        x: e.clientX, y: e.clientY,
        overCrew: crewDropAtPoint(e.clientX, e.clientY),
        overBooking: bookingDropAtPoint(e.clientX, e.clientY),
        dayIdx: Math.min(totalDays - 1, Math.max(0, Math.floor((e.clientX - d.gridLeft) / d.dayW))),
      }));
    }
    async function onUp() {
      const d = personDrag;
      setPersonDrag(null);
      if (!d) return;
      // Dropped on nothing at all - cancel rather than guess. A person landing
      // in the gap between two rows is an aimed-and-missed, not an instruction.
      const fromKey = format(addDays(gridStart, d.dayIdx), "yyyy-MM-dd");
      // 3 Nov 2026 — on a job bar: send them to that job for a stint, crew untouched
      if (d.overBooking) {
        draggedRef.current = true;
        setSendTo({ employeeId: d.employeeId, name: d.name, crewId: d.crewId, target: d.overBooking, dayKey: fromKey });
        return;
      }
      if (!d.overCrew) return;
      const target = d.overCrew === STANDALONE ? null : d.overCrew;
      if (target === d.crewId) return;                       // same crew, no move
      const targetName = target ? (crews.find(c => c.crew_id === target)?.crew_name ?? "that crew") : "no crew (the pool)";
      if (!window.confirm(`Move ${d.name} into ${targetName} from ${fmtDate(fromKey)}?\n\nThis changes their HOME crew — their site days follow the new crew's jobs from that day.\nTo send them to one job for a stint without changing crew, drop them on the job's bar instead.`)) return;

      const dayBefore = format(addDays(parseISO(fromKey), -1), "yyyy-MM-dd");

      try {
        // 1. Close the stint they are leaving.
        if (d.crewMemberId) {
          if (d.memberFrom && dayBefore < d.memberFrom) {
            // They would be leaving before they ever joined - the old row never
            // covered a real day, so it is a correction rather than a move.
            const { error } = await supabase.from("crew_members")
              .delete().eq("crew_member_id", d.crewMemberId);
            if (error) throw error;
          } else {
            const { error } = await supabase.from("crew_members")
              .update({ member_to: dayBefore }).eq("crew_member_id", d.crewMemberId);
            if (error) throw error;
          }
        }
        // 2. Open the new one. crew_role travels with them; is_lead does NOT -
        //    leading RIG-A has never implied leading RIG-B.
        if (target) {
          const { error } = await supabase.from("crew_members").insert({
            crew_id: target, employee_id: d.employeeId,
            member_from: fromKey, member_to: null,
            is_lead: false, crew_role: d.crewRole || "tech",
          });
          if (error) throw error;
        }
      } catch (err: any) {
        // 23P01 is the exclusion constraint from migration 326 - one person, one
        // crew, one day. It fires when the person is ALREADY in another crew
        // over these dates, which is the legacy mess rather than anything the
        // user just did wrong, so say that instead of showing them raw Postgres.
        if (err?.code === "23P01") {
          window.alert(
            `${d.name} is already in another crew over those dates, so this move would ` +
            `put them in two places at once.\n\n` +
            `Open the crew they are still in and end their membership there first, ` +
            `then drag them across.`,
          );
        } else {
          window.alert(`Could not move ${d.name}: ${err?.message ?? err}`);
        }
      }
      qcDrag.invalidateQueries({ queryKey: ["schedule_crew_members"] });
      // 335 - THE ROSTER FOLLOWS THE PERSON. The database syncs their site
      // stints with the move (off the old crew's remaining visits, onto the
      // new crew's, history untouched) - we just refetch what it did.
      qcDrag.invalidateQueries({ queryKey: ["schedule_roster"] });
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [personDrag, qcDrag, gridStart, totalDays]);

  // ---------------------------------------------------------------------------
  // 335b — dragging a person's OWN SITE BAR. James: "why not drag and change
  // the person's build dates." Same grammar as the crew blocks, one row down:
  //   grab the middle -> MOVE their stint, length kept
  //   grab an edge    -> change their first or last day
  //   plain click     -> open the visit (dates + who's going)
  // Writes the person's site_roster row directly - the schedule IS the roster.
  // ---------------------------------------------------------------------------
  const [stintDrag, setStintDrag] = useState<null | {
    rosterId: string; mode: "move" | "start" | "end";
    fromKey: string; toKey: string;         // toKey = effective end for preview
    openEnded: boolean;                     // true = follows the booking
    // Ctrl/Alt held at grab = DRAG A COPY. James: "if I can copy the first
    // Sharon block and drag it the other side of Gooburrum" - splitting one
    // person's time around another job. The original stays put; the drop
    // writes a NEW stint with the dragged dates.
    copy: boolean;
    // Shift held at grab = a click REMOVES these days (undo for a Ctrl-copy
    // gone wrong, or "he's not doing that stretch after all"). Same
    // plan/record rule as everywhere: not started -> gone, started -> ends
    // today and the worked days stay.
    shift: boolean;
    stint: NonNullable<PersonBar["stint"]>;
    editorTarget: PersonBar["editorTarget"];
    startX: number; dayW: number; dxDays: number;
  }>(null);

  function beginStintDrag(e: ReactPointerEvent, pb: PersonBar) {
    if (!canDrag || !pb.stint) return;
    e.preventDefault();
    e.stopPropagation();
    const bar = e.currentTarget as HTMLElement;
    const r = bar.getBoundingClientRect();
    const lane = bar.closest("[data-lane]") as HTMLElement | null;
    if (!lane) return;
    const dayW = lane.getBoundingClientRect().width / totalDays;
    const rel = e.clientX - r.left;
    const copy = e.ctrlKey || e.altKey;
    const mode: "move" | "start" | "end" =
      copy ? "move" : rel <= 7 ? "start" : rel >= r.width - 7 ? "end" : "move";
    setStintDrag({
      rosterId:  pb.stint.roster_id,
      mode, copy, shift: e.shiftKey,
      fromKey:   pb.stint.on_site_from,
      toKey:     pb.stint.on_site_to ?? pb.to,
      openEnded: pb.stint.on_site_to == null,
      stint:     pb.stint,
      editorTarget: pb.editorTarget,
      startX: e.clientX, dayW, dxDays: 0,
    });
  }

  useEffect(() => {
    if (!stintDrag) return;
    function onMove(e: PointerEvent) {
      setStintDrag(d => d && ({
        ...d, dxDays: Math.round((e.clientX - d.startX) / d.dayW),
      }));
    }
    async function onUp() {
      const d = stintDrag;
      setStintDrag(null);
      if (!d) return;
      draggedRef.current = true;

      // No movement = a CLICK. Shift-click removes these days; a plain click
      // opens the visit this stint belongs to.
      if (d.dxDays === 0) {
        if (d.shift) {
          const today = format(new Date(), "yyyy-MM-dd");
          const s = d.stint;
          const started = s.on_site_from <= today;
          const span = `${fmtDate(s.on_site_from)} → ${s.on_site_to ? fmtDate(s.on_site_to) : "open"}`;
          const ok = window.confirm(started
            ? `Take them off site from today?\n(${span} - days already worked stay on record)`
            : `Remove these days from the plan?\n(${span} - works haven't started, nothing is recorded)`);
          if (!ok) return;
          const { error } = started
            ? await (supabase as any).from("site_roster")
                .update({ on_site_to: today }).eq("roster_id", d.rosterId)
            : await (supabase as any).from("site_roster")
                .delete().eq("roster_id", d.rosterId);
          if (error) window.alert(`Could not remove those days: ${error.message}`);
          qcDrag.invalidateQueries({ queryKey: ["schedule_roster"] });
          qcDrag.invalidateQueries({ queryKey: ["visit_roster"] });
          return;
        }
        if (d.editorTarget) setEditing(d.editorTarget);
        return;
      }

      const shift = (k: string, n: number) => dayKey(k, n);
      let newFrom = d.fromKey;
      let newTo: string | null = d.openEnded ? null : d.toKey;
      if (d.mode === "move") {
        newFrom = shift(d.fromKey, d.dxDays);
        if (!d.openEnded) newTo = shift(d.toKey, d.dxDays);
      } else if (d.mode === "start") {
        const cand = shift(d.fromKey, d.dxDays);
        newFrom = (!d.openEnded && cand > d.toKey) ? d.toKey : cand;
      } else {
        // Dragging the end of an open stint PINS a finish date - that is the
        // gesture for "he's coming off on Thursday".
        const cand = shift(d.toKey, d.dxDays);
        newTo = cand < d.fromKey ? d.fromKey : cand;
      }

      // Mig 400 — the person's worked days are a record too. A started stint
      // that is moved splits at today; its start cannot be dragged; a finished
      // stint cannot be dragged at all. (Copy is a new row, so it is exempt.)
      if (!d.copy && d.fromKey < TODAY_KEY && !editPast) {   // 3 Nov 2026 — directors editing the past skip the split and move the record whole
        const finished = !d.openEnded && d.toKey < TODAY_KEY;
        if (finished) {
          window.alert("Those days have been worked - they are history and stay where they were.\nOpen the visit to correct the record if it is wrong.");
          return;
        }
        if (d.mode === "start") {
          window.alert("That stint has already started - the first day is history and stays where it was.");
          return;
        }
        if (d.mode === "move") {
          const { error } = await (supabase as any).schema("app")
            .rpc("split_stint_from_today", { p_roster_id: d.rosterId, p_delta_days: d.dxDays });
          if (error) window.alert(`Could not move those days: ${error.message}`);
          qcDrag.invalidateQueries({ queryKey: ["schedule_roster"] });
          qcDrag.invalidateQueries({ queryKey: ["visit_roster"] });
          return;
        }
      }

      if (d.copy) {
        // A dropped COPY is a second stint on the same visit - both blocks now
        // dragged/resized/removed independently. A copy always gets concrete
        // dates (two open-ended stints on one visit would fight each other).
        const { error } = await (supabase as any)
          .from("site_roster")
          .insert({
            project_id: d.stint.project_id, pca_id: d.stint.pca_id,
            employee_id: d.stint.employee_id,
            on_site_from: newFrom,
            on_site_to: newTo ?? shift(d.toKey, d.dxDays),
            status: d.stint.status, source: "manual",
            note: "split - copied on the schedule",
          });
        if (error) window.alert(`Could not copy those days: ${error.message}`);
      } else {
        const { error } = await (supabase as any)
          .from("site_roster")
          .update({ on_site_from: newFrom, on_site_to: newTo })
          .eq("roster_id", d.rosterId);
        if (error) window.alert(`Could not move those days: ${error.message}`);
      }
      qcDrag.invalidateQueries({ queryKey: ["schedule_roster"] });
      qcDrag.invalidateQueries({ queryKey: ["visit_roster"] });
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [stintDrag, qcDrag]);

  // ---------------------------------------------------------------------------
  // Mig 400 — dragging a DEPOT DAY / AD-HOC TASK bar. James, 29 Sep: "a depot
  // day can only be placed one day at a time - wants to drag one across
  // several days." Same grammar as every other bar: middle = move, edge =
  // stretch. The booking keeps its times of day; only the dates shift.
  // ---------------------------------------------------------------------------
  const [pbDrag, setPbDrag] = useState<null | {
    booking: PersonalBookingRow; mode: "move" | "start" | "end";
    fromKey: string; toKey: string;
    startX: number; dayW: number; dxDays: number;
  }>(null);

  function beginPbDrag(e: ReactPointerEvent, pb: PersonBar) {
    if (!canDrag || !pb.pbook) return;
    e.preventDefault();
    e.stopPropagation();
    const bar = e.currentTarget as HTMLElement;
    const r = bar.getBoundingClientRect();
    const lane = bar.closest("[data-lane]") as HTMLElement | null;
    if (!lane) return;
    const dayW = lane.getBoundingClientRect().width / totalDays;
    const rel = e.clientX - r.left;
    const mode: "move" | "start" | "end" = rel <= 7 ? "start" : rel >= r.width - 7 ? "end" : "move";
    setPbDrag({
      booking: pb.pbook, mode,
      fromKey: localDayKey(pb.pbook.scheduled_start),
      toKey:   localDayKey(pb.pbook.scheduled_end ?? pb.pbook.scheduled_start),
      startX: e.clientX, dayW, dxDays: 0,
    });
  }

  // Shift a timestamptz by whole days, keeping the time of day (local).
  const shiftTs = (ts: string, days: number) => {
    const d = new Date(ts);
    d.setDate(d.getDate() + days);
    return d.toISOString();
  };

  useEffect(() => {
    if (!pbDrag) return;
    function onMove(e: PointerEvent) {
      setPbDrag(d => d && ({ ...d, dxDays: Math.round((e.clientX - d.startX) / d.dayW) }));
    }
    async function onUp() {
      const d = pbDrag;
      setPbDrag(null);
      if (!d) return;
      draggedRef.current = true;
      if (d.dxDays === 0) { onSelectTask(d.booking); return; }   // a click opens it
      const b = d.booking;
      const startTs = b.scheduled_start;
      // No end recorded = same day as the start, at the usual knock-off.
      const endTs = b.scheduled_end ?? (() => {
        const t = new Date(b.scheduled_start); t.setHours(15, 30, 0, 0); return t.toISOString();
      })();
      let newStart = startTs, newEnd = endTs;
      if (d.mode === "move") {
        newStart = shiftTs(startTs, d.dxDays); newEnd = shiftTs(endTs, d.dxDays);
      } else if (d.mode === "start") {
        const cand = dayKey(d.fromKey, d.dxDays);
        newStart = shiftTs(startTs, differenceInCalendarDays(parseISO(cand > d.toKey ? d.toKey : cand), parseISO(d.fromKey)));
      } else {
        const cand = dayKey(d.toKey, d.dxDays);
        newEnd = shiftTs(endTs, differenceInCalendarDays(parseISO(cand < d.fromKey ? d.fromKey : cand), parseISO(d.toKey)));
      }
      const { error } = await supabase
        .from("personal_bookings")
        .update({ scheduled_start: newStart, scheduled_end: newEnd })
        .eq("booking_id", b.booking_id);
      if (error) window.alert(`Could not move that day: ${error.message}`);
      qcDrag.invalidateQueries({ queryKey: ["schedule_personal_bookings"] });
      qcDrag.invalidateQueries({ queryKey: ["my_week_personal_bookings"] });
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pbDrag, qcDrag]);

  // ---------------------------------------------------------------------------
  // Mig 400 — SWEEP EMPTY DAYS on a person's row to book a run of depot days
  // (or a multi-day task) in one go. Press on an empty day, drag across, let
  // go: the modal opens with first and last day filled in. A press-and-release
  // on one day is still the single-day click it always was.
  // ---------------------------------------------------------------------------
  const [sweep, setSweep] = useState<null | {
    employeeId: string; name: string; rowKey: string;
    startIdx: number; curIdx: number; laneEl: HTMLElement;
    crewId?: string;   // mig 401 — a sweep on the CREW row pencils in a planning block
  }>(null);
  const sweptRef = useRef(false);

  useEffect(() => {
    if (!sweep) return;
    function idxAt(x: number) {
      const rect = sweep!.laneEl.getBoundingClientRect();
      return Math.min(totalDays - 1, Math.max(0, Math.floor(((x - rect.left) / rect.width) * totalDays)));
    }
    function onMove(e: PointerEvent) {
      setSweep(sw => sw && ({ ...sw, curIdx: idxAt(e.clientX) }));
    }
    function onUp() {
      const sw = sweep;
      setSweep(null);
      if (!sw) return;
      if (sw.curIdx === sw.startIdx) return;           // plain click - the lane's onClick handles it
      sweptRef.current = true;                         // swallow the click that follows
      window.setTimeout(() => { sweptRef.current = false; }, 250);   // …but not the next real one
      const a = Math.min(sw.startIdx, sw.curIdx), z = Math.max(sw.startIdx, sw.curIdx);
      if (sw.crewId) {
        setEditingPlan({ crew_id: sw.crewId, plan_from: dayKey(gridStartKey, a), plan_to: dayKey(gridStartKey, z) });
        return;
      }
      onSelectPerson(sw.employeeId, sw.name, dayKey(gridStartKey, a), dayKey(gridStartKey, z));
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sweep, totalDays, gridStartKey]);

  type Block = {
    key: string; from: string; to: string; lane: number;
    project: ProjectRow | undefined; label: string;
    openEnded: boolean; clash: boolean;
    crewId: string; projectId: string; rawTo: string | null;
    weekendsOff: boolean;   // mig 400
    plan?: PlanBlockRow;    // mig 401 — a planning block, drawn in its own lane
  };

  // Mig 401 — planning blocks for the visible window. RLS keeps them to
  // manager+; the query simply comes back empty for anyone else.
  const { data: planBlocks = [] } = useQuery({
    queryKey: ["schedule_plan_blocks", gridStartKey, gridEndKey],
    enabled: canDrag,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("crew_plan_blocks")
        .select("block_id, crew_id, title, colour_hex, plan_from, plan_to, note")
        .lte("plan_from", gridEndKey)
        .gte("plan_to", gridStartKey)
        .order("plan_from");
      if (error) throw error;
      return (data ?? []) as PlanBlockRow[];
    },
  });
  // Mig 425 — open stand-downs / embargoes, to mark a booking that sits inside one.
  const { data: standData } = useOpenStanddowns();
  const [editingPlan, setEditingPlan] = useState<null | {
    block?: PlanBlockRow; crew_id: string; plan_from: string; plan_to: string;
  }>(null);

  // One lane set per crew. Overlapping bookings stack AND get flagged - that is
  // the double-booking, shown where it happens instead of in a banner.
  function blocksFor(crewId: string): { blocks: Block[]; laneCount: number } {
    const mine = assignments
      .filter(a => a.crews?.crew_id === crewId)
      .filter(a => a.assigned_from <= gridEndKey && (a.assigned_to ?? "9999-12-31") >= gridStartKey)
      .sort((a, z) => a.assigned_from.localeCompare(z.assigned_from));

    const laneEnd: string[] = [];
    const blocks: Block[] = [];
    for (const a of mine) {
      const p = projById.get(a.project_id);
      const from = a.assigned_from;
      const openEnded = a.assigned_to == null;
      const eff = p ? effectiveWindow(p, gridEndKey) : { end: null as string | null };
      const to = a.assigned_to ?? ((eff.end && eff.end >= from) ? eff.end : gridEndKey);
      if (to < from) continue;
      let lane = laneEnd.findIndex(end => end < from);
      const clash = lane === -1 && laneEnd.length > 0;
      if (lane === -1) { lane = laneEnd.length; laneEnd.push(to); } else { laneEnd[lane] = to; }
      blocks.push({
        key: a.pca_id, from, to, lane, project: p,
        // Prefer the loaded project, fall back to what the booking carries -
        // a crew's stint on a job that has since CLOSED still has a name.
        label: p?.sites?.site_name
            ?? a.projects?.sites?.site_name
            ?? p?.project_name
            ?? a.projects?.project_name
            ?? "Unknown job",
        openEnded, clash,
        crewId, projectId: a.project_id, rawTo: a.assigned_to,
        weekendsOff: a.weekends_off ?? false,
      });
    }
    // Mig 401 — planning blocks pack into their OWN lanes under the bookings,
    // so a pencil line never sits where a booking would and never flags a
    // clash against one.
    const bookingLanes = Math.max(1, laneEnd.length);
    const planLaneEnd: string[] = [];
    for (const pb of planBlocks) {
      if (pb.crew_id !== crewId) continue;
      if (pb.plan_from > gridEndKey || pb.plan_to < gridStartKey) continue;
      let lane = planLaneEnd.findIndex(end => end < pb.plan_from);
      if (lane === -1) { lane = planLaneEnd.length; planLaneEnd.push(pb.plan_to); } else { planLaneEnd[lane] = pb.plan_to; }
      blocks.push({
        key: pb.block_id, from: pb.plan_from, to: pb.plan_to, lane: bookingLanes + lane,
        project: undefined, label: pb.title, openEnded: false, clash: false,
        crewId, projectId: "", rawTo: pb.plan_to, weekendsOff: false, plan: pb,
      });
    }
    return { blocks, laneCount: bookingLanes + planLaneEnd.length };
  }

  // ---------------------------------------------------------------------------
  // PEOPLE UNDER CREWS
  // ---------------------------------------------------------------------------
  // Collapsed by default, and that is deliberate. James: "I don't want to clog
  // up the main screen." Four crews is four rows; four crews with everybody
  // showing is twenty, and a screen you have to scroll to answer "who is on
  // site this week" has stopped answering it. Open the crews you care about.
  // ---------------------------------------------------------------------------
  // 335b — people are ON by default. James: "to move people I have to go
  // Schedule, Crew, tick show people in this little obscure tick box." The
  // people ARE this screen now; hiding them was hiding the product. The
  // toggle stays for a compact jobs-only look, and chevrons hide single crews.
  const [closedCrews, setClosedCrews] = useState<Set<string>>(new Set());
  const [showPeople, setShowPeople] = useState(true);

  function toggleCrew(crewId: string) {
    setClosedCrews(prev => {
      const next = new Set(prev);
      if (next.has(crewId)) next.delete(crewId); else next.add(crewId);
      return next;
    });
  }

  const empName = (m: CrewMemberRow) => {
    const e = m.employees;
    if (!e) return "Unknown";
    return `${e.preferred_name?.trim() || e.first_name} ${e.last_name}`.trim();
  };

  // Members of a crew whose DATED membership overlaps the visible grid. Somebody
  // who left the crew in June does not appear on an August screen, and somebody
  // who left mid-month appears only across the days they were actually in it.
  function membersFor(crewId: string): CrewMemberRow[] {
    return crewMembers
      .filter(m => m.crew_id === crewId)
      .filter(m => m.employees?.is_active && m.employees?.is_schedulable)
      .filter(m => m.member_from <= gridEndKey && (m.member_to ?? "9999-12-31") >= gridStartKey)
      .sort((a, z) =>
        Number(z.is_lead) - Number(a.is_lead) || empName(a).localeCompare(empName(z)));
  }

  const overlaps = (aF: string, aT: string, bF: string, bT: string) => aF <= bT && bF <= aT;

  // One person's bars for the month.
  //
  // 333 — site bars now come off the ROSTER (site_roster), not off "their crew's
  // bookings clipped to membership". Same row, same collisions drawn over the
  // top (leave, tasks, defects) — but the site stints are records that survive
  // crew moves. Colour still follows the crew whose booking hosts the stint.
  // Thin wrapper over the shared builder - see buildPersonBars at module
  // scope. The People tab needs the identical logic and two copies of "where
  // is this person" is how views drift apart.
  const crewColour = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of crews) if (c.colour_hex) m.set(c.crew_id, c.colour_hex);
    for (const a of assignments) if (a.crews?.crew_id && a.crews.colour_hex) m.set(a.crews.crew_id, a.crews.colour_hex);
    return m;
  }, [crews, assignments]);

  const barsForPerson = (employeeId: string, name: string): PersonBar[] =>
    buildPersonBars(
      { gridStartKey, gridEndKey, projById, projectTeam, personalBookings, leaveRows,
        defects: defectRows, roster: rosterRows, crewColour },
      employeeId, name,
    );

  // Won work with dates and nobody on it. Its own row at the bottom, because it
  // is the queue of things that still need a crew - the reason to open this view.
  const unbooked = useMemo(() => {
    const booked = new Set(assignments.filter(a => a.crews).map(a => a.project_id));
    return projects.filter(p => !booked.has(p.project_id));
  }, [projects, assignments]);

  const cols = `200px repeat(${totalDays}, minmax(8px, 1fr))`;

  // Which day(s) the current drag is pointing at, for the axis highlight.
  // Block drag: preview start (and end, when the whole block moves). Person or
  // project drag: the landing day / window ends.
  const hotDays: { start: number | null; end: number | null } = (() => {
    // 1 Sep 2026 - James: "I want it highlighted from the 18th when it starts
    // to the end, drag either way and it pulls in or out and the header
    // reflects it." So: the WHOLE block's window, every mode. Move shifts
    // both ends; an edge drag holds the other end still. Open-ended blocks
    // run to the edge of the grid.
    const span = (d: { mode: "move" | "start" | "end"; fromKey: string; toKey: string; openEnded: boolean; dxDays: number }) => {
      let f = d.fromKey;
      let t: string | null = d.openEnded ? null : d.toKey;
      // Mig 400 - a started block moving splits at today, so the window that
      // lights up starts from today, not from the (unmoving) history.
      if (d.mode === "move") { f = dayKey(d.fromKey < TODAY_KEY ? TODAY_KEY : d.fromKey, d.dxDays); if (t != null) t = dayKey(d.toKey, d.dxDays); }
      else if (d.mode === "start") { const c = dayKey(d.fromKey, d.dxDays); f = (t != null && c > t) ? t : c; }
      else { const c = dayKey(d.toKey, d.dxDays); t = c < f ? f : c; }
      return { start: clipIdx(f), end: t == null ? totalDays - 1 : clipIdx(t) };
    };
    if (drag) return span(drag);
    // The per-person stint drag (roster rebuild) never fed the axis before.
    if (stintDrag) return span(stintDrag);
    if (personDrag) return { start: personDrag.dayIdx, end: null };
    if (projDrag)   return {
      start: clipIdx(projDrag.from),
      end:   projDrag.to ? clipIdx(projDrag.to) : null,
    };
    return { start: null, end: null };
  })();

  const Axis = (
    <ScheduleAxis weeks={weeks} gridStart={gridStart} totalDays={totalDays}
      labelCol="200px" label="crew"
      highlightIdx={hotDays.start} highlightEndIdx={hotDays.end} />
  );
  const RowBackdrop = <ScheduleBackdrop gridStart={gridStart} totalDays={totalDays} />;

  // ---------------------------------------------------------------------------
  // One person, one row. Used under a crew and again in the stand-alone bucket,
  // so somebody who moves out of a crew mid-month is the same row in both places
  // rather than two things that happen to look alike.
  // ---------------------------------------------------------------------------
  // Deliberately a render function and not a nested component: a component
  // declared inside another component is a new type on every render, so React
  // tears the whole subtree down and rebuilds it each time the month changes.
  // Nothing here holds state so it would still WORK - it would just churn the
  // DOM for twenty rows for no reason.
  function renderPersonRow({
    rowKey, employeeId, name, dotColour, isLead, bars,
    crewId, crewMemberId, memberFrom, crewRole,
  }: {
    rowKey: string;
    employeeId: string;
    name: string;
    dotColour: string;
    isLead: boolean;
    bars: PersonBar[];
    crewId: string | null;         // null = currently in no crew
    crewMemberId: string | null;   // the dated membership row being dragged
    memberFrom: string | null;
    crewRole: string;
  }) {
    const beingDragged = personDrag?.employeeId === employeeId;

    // Double-booked = TWO LINES (James, 27 Aug: "I thought double bookings
    // would be two lines, not nested behind"). Site bars that overlap in time
    // get their own lane and the row grows; leave/task/defect hatches stay
    // drawn ACROSS all lanes because they are warnings over the top of the
    // work, not bookings competing with it.
    const laneOf = new Map<string, number>();
    {
      const solids = bars
        .filter(b => PERSON_BAR_STYLE[b.kind].solid || b.kind === "listed")
        .sort((a, z) => a.from.localeCompare(z.from) || a.to.localeCompare(z.to));
      const laneEnd: string[] = [];
      for (const b of solids) {
        let l = laneEnd.findIndex(end => b.from > end);
        if (l === -1) { l = laneEnd.length; laneEnd.push(b.to); }
        else laneEnd[l] = b.to;
        laneOf.set(b.key, l);
      }
    }
    const laneCount = Math.max(1, ...Array.from(laneOf.values()).map(l => l + 1));
    const rowH = laneCount * PERSON_ROW_H;

    return (
      <div
        key={rowKey}
        className={`grid items-center px-3 border-b border-line/20 transition-colors ${
          beingDragged ? "bg-accent/10" : "bg-ink-raised/20"}`}
        style={{ gridTemplateColumns: cols, paddingTop: 3, paddingBottom: 3 }}>

        <div className="pl-5 min-w-0 pr-3 flex items-center gap-1.5">
          {/* The grip is the drag handle, and the name is still a button. Two
              separate targets, because "move this person" and "give this person
              a task" are different intentions and merging them into one
              click-or-drag gesture makes both feel unreliable. */}
          {canDrag ? (
            <span
              onPointerDown={e => beginPersonDrag(e, { employeeId, name, crewId, crewMemberId, memberFrom, crewRole })}
              className="flex-shrink-0 -ml-3.5 w-3 flex items-center justify-center text-text-dim hover:text-accent cursor-grab active:cursor-grabbing"
              title={`Drag ${name} onto another crew — where you drop across the month is the date they move`}>
              <GripVertical size={11}/>
            </span>
          ) : <span className="flex-shrink-0 -ml-3.5 w-3" />}

          <span className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ background: dotColour }} />
          <button type="button"
            onClick={() => onSelectPerson(employeeId, name)}
            className="text-[11px] text-text hover:text-accent truncate text-left"
            title={`Assign ${name} a one-off task`}>
            {name}
          </button>
          {isLead && (
            <span className="text-[8px] uppercase font-mono px-1 rounded bg-accent/15 text-accent flex-shrink-0">
              lead
            </span>
          )}
        </div>

        <div className="relative" data-lane
          style={{ gridColumn: `2 / span ${totalDays}`, height: `${rowH}px` }}
          /* Mig 356 — click an EMPTY day on a person's row to give them a
             depot day or an ad-hoc task on that date (James, 5 Sep: "select
             an empty space on an employee and it adds a depot day or an
             ad-hoc task"). Bars keep their own click/drag — they carry
             data-personbar and are excluded here; empty grid only. */
          onClick={canDrag ? (e => {
            if ((e.target as HTMLElement).closest("[data-personbar]")) return;
            if (sweptRef.current) { sweptRef.current = false; return; }   // mig 400 - a sweep already opened the modal
            const rect = e.currentTarget.getBoundingClientRect();
            if (rect.width <= 0) return;
            const idx = Math.min(totalDays - 1, Math.max(0,
              Math.floor(((e.clientX - rect.left) / rect.width) * totalDays)));
            onSelectPerson(employeeId, name, dayKey(gridStartKey, idx));
          }) : undefined}
          /* Mig 400 - press on an empty day and drag across to book a RUN of
             depot days / a multi-day task in one go. */
          onPointerDown={canDrag ? (e => {
            if ((e.target as HTMLElement).closest("[data-personbar]")) return;
            if (e.button !== 0) return;
            const lane = e.currentTarget as HTMLElement;
            const rect = lane.getBoundingClientRect();
            if (rect.width <= 0) return;
            const idx = Math.min(totalDays - 1, Math.max(0,
              Math.floor(((e.clientX - rect.left) / rect.width) * totalDays)));
            setSweep({ employeeId, name, rowKey, startIdx: idx, curIdx: idx, laneEl: lane });
          }) : undefined}
          title={canDrag ? `Click an empty day — give ${name} a depot day or an ad-hoc task · drag across days to book a run of them` : undefined}>
          {RowBackdrop}
          {sweep && sweep.rowKey === rowKey && sweep.curIdx !== sweep.startIdx && (() => {
            const a = Math.min(sweep.startIdx, sweep.curIdx), z = Math.max(sweep.startIdx, sweep.curIdx);
            return (
              <div className="absolute top-0.5 bottom-0.5 rounded-sm border border-dashed border-accent/80 bg-accent/15 pointer-events-none flex items-center px-1.5 text-[9px] text-accent"
                style={{ left: `calc(${(a / totalDays) * 100}%)`, width: `calc(${((z - a + 1) / totalDays) * 100}% - 2px)`, zIndex: 25 }}>
                {z - a + 1} days
              </div>
            );
          })()}
          {bars.length === 0 && (
            <div className="absolute inset-0 flex items-center">
              <span className="text-[9px] text-text-dim italic">
                {crewId ? "Nothing booked" : "Free all month — no crew, nothing booked"}
              </span>
            </div>
          )}
          {/* Today line UNDER the bars, not sawing through them. */}
          {todayInRange && (
            <div className="absolute top-0 bottom-0 border-l border-accent/50 pointer-events-none"
              style={{ left: `calc(${(todayIdx / totalDays) * 100}%)`, zIndex: 0 }} />
          )}
          {bars.map(pb => {
            // 335b - a roster bar being dragged previews live, same as blocks.
            // Mig 400 - a depot/task bar previews the same way (pbDrag).
            const sd = stintDrag && pb.stint && stintDrag.rosterId === pb.stint.roster_id
              ? stintDrag : null;
            const bd = pbDrag && pb.pbook && pbDrag.booking.booking_id === pb.pbook.booking_id
              ? pbDrag : null;
            const d = sd ?? (bd ? { ...bd, copy: false, openEnded: false } : null);
            // A started stint moving = a split at today; the stub stays put.
            const splitting = !!sd && sd.mode === "move" && !sd.copy && sd.dxDays !== 0 && pb.from < TODAY_KEY && pb.to >= TODAY_KEY;
            let pFrom = pb.from, pTo = pb.to;
            if (d && d.dxDays !== 0 && !d.copy) {
              if (d.mode === "move") {
                pFrom = dayKey(splitting ? TODAY_KEY : pb.from, d.dxDays);
                pTo   = d.openEnded ? pb.to : dayKey(pb.to, d.dxDays);
              } else if (d.mode === "start") {
                pFrom = dayKey(pb.from, d.dxDays); if (pFrom > pTo) pFrom = pTo;
              } else {
                pTo = dayKey(pb.to, d.dxDays); if (pTo < pFrom) pTo = pFrom;
              }
            }
            // (Copy drag: the original stays put - the tracking ghost is
            // rendered once, after the bars.)
            const startIdx = clipIdx(pFrom);
            const endIdx   = clipIdx(pTo < pFrom ? pFrom : pTo);
            const span     = Math.max(1, endIdx - startIdx + 1);
            const st = PERSON_BAR_STYLE[pb.kind];
            const solid = st.solid;
            const draggable = canDrag && (!!pb.stint || !!pb.pbook);
            const notches = pb.weekendsOff && !d ? weekendOffsets(pFrom, pTo) : [];
            // Site bars (incl. listed) sit in their lane; warning hatches span
            // every lane so a clash stays visible over whichever line it hits.
            const inLane = laneOf.has(pb.key);
            const laneTop = inLane ? 2 + (laneOf.get(pb.key)! * PERSON_ROW_H) : 1;
            return (
              <div key={pb.key} style={{ display: "contents" }}>
              {splitting && clipIdx(dayKey(TODAY_KEY, -1)) >= clipIdx(pb.from) && (
                <div key="stub" className="absolute rounded-sm text-[9px] leading-[16px] px-1.5 truncate font-medium text-white/80 pointer-events-none"
                  style={{
                    left:  `calc(${(clipIdx(pb.from) / totalDays) * 100}%)`,
                    width: `calc(${((clipIdx(dayKey(TODAY_KEY, -1)) - clipIdx(pb.from) + 1) / totalDays) * 100}% - 2px)`,
                    top: laneTop, height: inLane ? 16 : rowH - 2,
                    background: pb.colour ?? SOLO_BLUE, opacity: 0.55, zIndex: 1,
                  }}>
                  worked
                </div>
              )}
              <div key="bar" title={pb.title} data-personbar
                onPointerDown={draggable ? (e => pb.stint ? beginStintDrag(e, pb) : beginPbDrag(e, pb)) : undefined}
                onClick={!draggable && pb.pbook ? (e => { e.stopPropagation(); onSelectTask(pb.pbook!); }) : undefined}
                className={`absolute rounded-sm text-[9px] leading-[16px] px-1.5 truncate font-medium flex items-center gap-1 ${
                  solid ? "text-white/95" : "text-text"} ${
                  draggable ? "cursor-grab active:cursor-grabbing hover:brightness-110 hover:shadow" : ""}`}
                style={{
                  left:   `calc(${(startIdx / totalDays) * 100}%)`,
                  width:  `calc(${(span / totalDays) * 100}% - 2px)`,
                  top:    laneTop,
                  height: inLane ? 16 : rowH - 2,
                  background: solid ? (pb.colour ?? undefined) : st.hatch,
                  border:     solid ? undefined : st.border,
                  zIndex:     d ? 20 : solid ? 1 : 2,
                  boxShadow:  d ? "0 0 0 2px rgba(255,255,255,0.6)" : undefined,
                  touchAction: draggable ? "none" : undefined,
                }}>
                {span > 2 && <span className="truncate relative z-[1]">{pb.label}{pb.weekendsOff && span > 5 ? <span className="opacity-75"> · M–F</span> : null}</span>}
                {d && d.dxDays !== 0 && !d.copy && (
                  <span className="text-[8px] opacity-90 relative z-[1]">
                    {fmtDate(pFrom)} → {d.openEnded && d.mode !== "end" ? "open" : fmtDate(pTo)}
                  </span>
                )}
                {notches.map(i => (
                  <span key={i} className="absolute top-0 bottom-0 pointer-events-none"
                    style={{ left: `${(i / span) * 100}%`, width: `${(1 / span) * 100}%`, background: WEEKEND_NOTCH }} />
                ))}
              </div>
              </div>
            );
          })}
          {/* The copy-drag ghost - rendered once, over everything. */}
          {(() => {
            const d = stintDrag;
            if (!d || !d.copy || d.dxDays === 0) return null;
            const src = bars.find(b => b.stint && b.stint.roster_id === d.rosterId);
            if (!src) return null;
            const gF = dayKey(src.from, d.dxDays);
            const gT = dayKey(src.to,   d.dxDays);
            const gs = clipIdx(gF), ge = clipIdx(gT < gF ? gF : gT);
            const gspan = Math.max(1, ge - gs + 1);
            const gTop = laneOf.has(src.key) ? 2 + (laneOf.get(src.key)! * PERSON_ROW_H) : 2;
            return (
              <div className="absolute rounded-sm text-[9px] leading-[16px] px-1.5 truncate font-medium text-white/95 pointer-events-none"
                style={{
                  left:  `calc(${(gs / totalDays) * 100}%)`,
                  width: `calc(${(gspan / totalDays) * 100}% - 2px)`,
                  top: gTop, height: 16,
                  background: src.colour ?? SOLO_BLUE,
                  opacity: 0.75, zIndex: 30,
                  boxShadow: "0 0 0 2px rgba(255,255,255,0.7)",
                  outline: "1px dashed rgba(255,255,255,0.9)",
                }}>
                {src.label} · copy {fmtDate(gF)} → {fmtDate(gT)}
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // People with no crew this month. James: "we move them / drag them to a
  // different crew or stand alone." Stand-alone has to be somewhere, or a person
  // who leaves a crew simply disappears off the schedule - which is the opposite
  // of what a schedule is for.
  //
  // Only people with SOMETHING on - a project of their own, an ad-hoc task,
  // leave. Fifteen empty rows underneath the crews would clog exactly the screen
  // James asked not to clog.
  // ---------------------------------------------------------------------------
  const standalone = useMemo(() => {
    const inACrew = new Set(
      crewMembers
        .filter(m => m.member_from <= gridEndKey && (m.member_to ?? "9999-12-31") >= gridStartKey)
        .map(m => m.employee_id),
    );
    return schedulablePeople
      .filter(p => !inACrew.has(p.employee_id))
      .map(p => ({
        person: p,
        name: `${p.preferred_name?.trim() || p.first_name} ${p.last_name}`.trim(),
      }))
      .map(x => ({ ...x, bars: barsForPerson(x.person.employee_id, x.name) }))
      // Everybody, INCLUDING people with nothing on. The first cut filtered
      // those out to keep the screen short, and that was the wrong call.
      //
      // James, 21 Aug 2026: "flagging if an employee who is tagged to show up in
      // the schedule is missing from a crew - not in a banner but in the table,
      // so we can see them and drag them / allocate them / schedule them, by
      // seeing where everyone is. That's the beauty of this."
      //
      // Right: an empty row is not clutter here, it is the answer. Somebody with
      // nothing booked and no crew is precisely who you are looking for when you
      // need a body, and hiding them made the one question this view is best at
      // answering unanswerable. Sorted with unbooked people FIRST for the same
      // reason.
      .sort((a, z) =>
        (a.bars.length === 0 ? 0 : 1) - (z.bars.length === 0 ? 0 : 1)
        || a.name.localeCompare(z.name));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulablePeople, crewMembers, projectTeam, personalBookings, leaveRows,
      rosterRows, crewColour, gridStartKey, gridEndKey, projById]);

  // How many of them have nothing on at all - the "free right now" count.
  const standaloneFree = standalone.filter(x => x.bars.length === 0).length;

  const idleCount = crews.filter(c => blocksFor(c.crew_id).blocks.length === 0).length;
  const shownCrews = hideIdle
    ? crews.filter(c => blocksFor(c.crew_id).blocks.length > 0)
    : crews;

  return (
    <Card>
      <div className="flex items-center gap-3 px-3 py-2 border-b border-line/50 text-[11px]">
        {/* People on/off is THE control on this bar - this is the main
            scheduling surface now (James, 27 Aug), so it reads as a proper
            button, not a tick box you have to know about. */}
        <button type="button"
          onClick={() => {
            const next = !showPeople;
            setShowPeople(next);
            if (next) setClosedCrews(new Set());   // back on = everyone showing
          }}
          title={showPeople
            ? "Hide the people rows - crews and bookings only"
            : "Show every crew member's own row and site days"}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border font-medium ${
            showPeople
              ? "bg-accent text-white border-accent"
              : "border-line text-text-muted hover:bg-ink-hover"}`}>
          <Users size={12} /> People {showPeople ? "on" : "off"}
        </button>
        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
          <input type="checkbox" checked={hideIdle} onChange={e => setHideIdle(e.target.checked)}
            className="accent-accent" />
          <span className="text-text-muted">Hide crews with nothing booked</span>
        </label>
        {isDirector && (
          <label className={`inline-flex items-center gap-1.5 cursor-pointer select-none px-2 py-0.5 rounded border ${editPast ? "border-danger/60 bg-danger/10 text-danger" : "border-line text-text-muted"}`}
            title="Directors only. Finished bars and stints drag again, and a started one moves whole instead of splitting at today — for fixing days the tool was not used for.">
            <input type="checkbox" checked={editPast} onChange={e => setEditPast(e.target.checked)} className="accent-danger" />
            <span>Edit the past</span>
          </label>
        )}
        {idleCount > 0 && (
          <span className="text-text-dim">
            {idleCount} of {crews.length} idle this month
          </span>
        )}
        {showPeople && (
          <button type="button"
            onClick={() => setClosedCrews(closedCrews.size ? new Set() : new Set(crews.map(c => c.crew_id)))}
            className="text-accent hover:underline">
            {closedCrews.size ? "Expand all" : "Collapse all"}
          </button>
        )}
        {/* The people marks, explained exactly when they are on screen. Four
            colours already compete on this row; a legend for marks that are
            not showing would be a fifth thing to ignore. */}
        {showPeople && (
          <span className="inline-flex items-center gap-3 text-[10px] text-text-muted">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-2 rounded-sm bg-text-dim/60"/>
              <span>with the crew</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-2 rounded-sm" style={{ background: SOLO_BLUE }}/>
              <span>on a job alone</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-2 rounded-sm" style={{ background: DEPOT_SLATE }}/>
              <span>depot</span>
            </span>
            <span className="inline-flex items-center gap-1" title="Mig 401 — a pencilled plan on the crew row, not a booking">
              <span className="inline-block w-3 h-2 rounded-sm border border-dashed border-white/70" style={{ background: PLAN_COLOURS[0], opacity: 0.9 }}/>
              <span>plan</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-2.5 rounded-sm"
                style={{
                  background: "repeating-linear-gradient(45deg, rgba(255,120,68,0.30) 0 4px, transparent 4px 8px)",
                  border: "1px solid rgba(255,120,68,0.8)",
                }}/>
              <span>ad-hoc task</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-2.5 rounded-sm"
                style={{
                  background: "repeating-linear-gradient(45deg, rgba(148,163,184,0.30) 0 4px, transparent 4px 8px)",
                  border: "1px solid rgba(148,163,184,0.7)",
                }}/>
              <span>leave</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-2.5 rounded-sm"
                style={{ background: PERSON_BAR_STYLE.defect.hatch, border: PERSON_BAR_STYLE.defect.border }}/>
              <span>defect visit</span>
            </span>
          </span>
        )}
        {canDrag && (
          <span className="ml-auto text-text-dim">
            Drag a block to move it · drag an edge to change the days · drop on another crew to reassign
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        {Axis}
        {/* Mig 425 — stand-downs and carrier embargoes drawn as red bars on the
            same day grid, right under the dates, where the planning happens. */}
        <StopsLane gridStartKey={gridStartKey} totalDays={totalDays} labelCol="200px" />

        {/* Key dates used to be a lane right here. James, after testing:
            "Key dates nested like this won't work for me, needs to be on the
            schedule view not the crew view - what happens when 4 sites all have
            outages." Right: a lane costs the same vertical space whether it
            holds one outage or ten, and this tab is about crews. They live on
            the Calendar tab now, as a tint on the day itself. */}

        {shownCrews.map(c => {
          const { blocks, laneCount } = blocksFor(c.crew_id);
          const rowHeight = laneCount * 24;
          const personOver = personDrag
            && personDrag.overCrew === c.crew_id
            && personDrag.crewId !== c.crew_id;
          const projOver = projDrag && projDrag.overCrew === c.crew_id;
          return (
            <div key={c.crew_id}
              data-crew-drop={c.crew_id}
              className={personOver ? "ring-1 ring-inset ring-accent/70 bg-accent/[0.06]"
                : projOver ? "ring-1 ring-inset ring-[#16a34a]/70 bg-[#16a34a]/[0.06]" : ""}>
            <div
              data-crew-row={c.crew_id}
              className={`grid items-center px-3 py-2 border-b border-line/30 ${
                drag && drag.overCrew === c.crew_id && drag.overCrew !== drag.crewId
                  ? "bg-accent/10" : ""}`}
              style={{ gridTemplateColumns: `200px repeat(${totalDays}, minmax(8px, 1fr))` }}>
              <div className="min-w-0 pr-3 flex items-center gap-2">
                {showPeople && (
                  <button type="button" onClick={() => toggleCrew(c.crew_id)}
                    className="text-text-muted hover:text-accent flex-shrink-0"
                    title={closedCrews.has(c.crew_id) ? "Show who is in this crew" : "Hide the crew"}>
                    <ChevronDown size={13}
                      className={`transition-transform ${closedCrews.has(c.crew_id) ? "-rotate-90" : ""}`} />
                  </button>
                )}
                <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ background: c.colour_hex }} />
                <div className="min-w-0">
                  {/* Crew name first, lead underneath. The code was the top line
                      and meant nothing to anyone reading the screen. */}
                  <div className="text-[12px] text-text truncate">{c.crew_name}</div>
                  {/* James, 3 Sep: "leads are not really leads at the moment —
                      just team names with everyone moving about." A bare name
                      under the crew read as if that person owned the team, so
                      it's labelled now, and says nothing when no lead is set. */}
                  {c.lead_employee_id && leadNameById.get(c.lead_employee_id) && (
                    <div className="text-[10px] text-text-muted truncate">
                      Lead · {leadNameById.get(c.lead_employee_id)}
                    </div>
                  )}
                </div>
                {/* Mig 401 — pencil a planning block in (next Mon–Fri by default;
                    sweeping empty days on the row does the same with the dates). */}
                {canDrag && (
                  <button type="button"
                    onClick={() => {
                      const t = new Date();
                      const dow = t.getDay();                       // 0 Sun … 6 Sat
                      const toMon = ((8 - dow) % 7) || 7;           // next Monday
                      const mon = format(addDays(t, toMon), "yyyy-MM-dd");
                      setEditingPlan({ crew_id: c.crew_id, plan_from: mon, plan_to: dayKey(mon, 4) });
                    }}
                    title="Pencil in a planning block — a coloured note with dates, not a booking"
                    className="ml-auto flex-shrink-0 text-[9px] px-1.5 py-0.5 rounded border border-dashed border-line text-text-dim hover:text-accent hover:border-accent/60">
                    + plan
                  </button>
                )}
              </div>

              <div className="relative" data-lane
                style={{ gridColumn: `2 / span ${totalDays}`, height: `${rowHeight}px` }}
                /* Mig 401 — sweep empty days on the crew row to pencil in a
                   planning block across them. Blocks keep their own drag. */
                onPointerDown={canDrag ? (e => {
                  if ((e.target as HTMLElement).closest("button")) return;
                  if (e.button !== 0) return;
                  const lane = e.currentTarget as HTMLElement;
                  const rect = lane.getBoundingClientRect();
                  if (rect.width <= 0) return;
                  const idx = Math.min(totalDays - 1, Math.max(0,
                    Math.floor(((e.clientX - rect.left) / rect.width) * totalDays)));
                  setSweep({ employeeId: "", name: c.crew_name, rowKey: `crew:${c.crew_id}`, crewId: c.crew_id,
                             startIdx: idx, curIdx: idx, laneEl: lane });
                }) : undefined}>
                {RowBackdrop}
                {sweep && sweep.rowKey === `crew:${c.crew_id}` && sweep.curIdx !== sweep.startIdx && (() => {
                  const a = Math.min(sweep.startIdx, sweep.curIdx), z = Math.max(sweep.startIdx, sweep.curIdx);
                  return (
                    <div className="absolute top-0.5 bottom-0.5 rounded border border-dashed border-[#7c3aed] bg-[#7c3aed]/15 pointer-events-none flex items-center px-1.5 text-[9px] text-[#c4b5fd]"
                      style={{ left: `calc(${(a / totalDays) * 100}%)`, width: `calc(${((z - a + 1) / totalDays) * 100}% - 2px)`, zIndex: 25 }}>
                      plan · {z - a + 1} days
                    </div>
                  );
                })()}
                {blocks.length === 0 && (
                  <div className="absolute inset-0 flex items-center">
                    <span className="text-[10px] text-text-dim italic">Nothing booked this month</span>
                  </div>
                )}
                {blocks.map(b => {
                  // Live preview while dragging — the bar follows the pointer
                  // before anything is saved, so you can see where it will land.
                  const d = drag && drag.pcaId === b.key ? drag : null;
                  // Mig 400 — a started block being MOVED previews the split:
                  // the worked days stay as a dimmed stub, the rest follows the
                  // pointer from today. (The save does exactly this.)
                  // Only once the pointer has actually moved: at dx 0 the bar
                  // must stay under the pointer or the click that follows is lost.
                  const splitting = !!d && d.mode === "move" && d.dxDays !== 0 && b.from < TODAY_KEY && b.to >= TODAY_KEY;
                  const moveBase = splitting ? TODAY_KEY : b.from;
                  const previewFrom = d
                    ? (d.mode === "end" ? b.from : dayKey(moveBase, d.dxDays))
                    : b.from;
                  const previewTo = d
                    ? (d.mode === "start" ? b.to : dayKey(b.to, d.dxDays))
                    : b.to;
                  const pFrom = previewTo < previewFrom && d?.mode !== "move" ? previewTo : previewFrom;
                  const pTo   = previewTo < previewFrom && d?.mode !== "move" ? previewFrom : previewTo;
                  const startIdx = clipIdx(pFrom);
                  const endIdx   = clipIdx(pTo < pFrom ? pFrom : pTo);
                  const span     = Math.max(1, endIdx - startIdx + 1);
                  // Mig 400 — weekend notches on a Mon–Fri booking (drawn on the
                  // bar as it sits, not on the preview).
                  const notches = b.weekendsOff && !d ? weekendOffsets(pFrom, pTo) : [];
                  const stubStart = clipIdx(b.from);
                  const stubEnd   = clipIdx(dayKey(TODAY_KEY, -1));
                  const stopHit = b.plan ? null : stopCovering(standData, b.projectId, b.from, b.rawTo);   // mig 425
                  return (
                    <div key={b.key} style={{ display: "contents" }}>
                    {/* keyed: the stub appearing mid-drag must not remount the
                        button, or the browser drops the click that follows */}
                    {splitting && stubEnd >= stubStart && (
                      <div key="stub" className="absolute rounded flex items-center px-2 text-[10px] text-white/80 truncate font-medium pointer-events-none"
                        style={{
                          top: `${b.lane * 24}px`, height: "21px",
                          left:  `calc(${(stubStart / totalDays) * 100}%)`,
                          width: `calc(${((stubEnd - stubStart + 1) / totalDays) * 100}%)`,
                          background: c.colour_hex, opacity: 0.55, zIndex: 1,
                        }}
                        title="Days already worked - these stay where they are">
                        {b.label} · worked
                      </div>
                    )}
                    <button
                      key="bar"
                      type="button"
                      onClick={() => {
                        // Swallow the click that follows a drag — see draggedRef.
                        if (draggedRef.current) { draggedRef.current = false; return; }
                        if (drag) return;
                        if (b.plan) {   // mig 401
                          setEditingPlan({ block: b.plan, crew_id: b.plan.crew_id, plan_from: b.plan.plan_from, plan_to: b.plan.plan_to });
                          return;
                        }
                        setEditing({
                          pca_id: b.key, crew_id: b.crewId, from: b.from,
                          to: b.rawTo, projectId: b.projectId, label: b.label,
                          weekendsOff: b.weekendsOff,
                        });
                      }}
                      onPointerDown={(e) => {
                        if (!canDrag) return;
                        const el = e.currentTarget as HTMLElement;
                        // The lane, not the parent: the block sits in a display:contents
                        // wrapper (mig 400 split stub) whose width is zero.
                        const lane = (el.closest("[data-lane]") as HTMLElement | null) ?? (el.parentElement as HTMLElement);
                        const rect = el.getBoundingClientRect();
                        const EDGE = 8;
                        const mode: "move" | "start" | "end" =
                          e.clientX - rect.left <= EDGE ? "start"
                          : rect.right - e.clientX <= EDGE ? "end"
                          : "move";
                        setDrag({
                          pcaId: b.key, mode,
                          fromKey: b.from, toKey: b.to, openEnded: b.openEnded,
                          crewId: b.crewId,
                          startX: e.clientX,
                          dayW: Math.max(1, lane.clientWidth / totalDays),
                          dxDays: 0, overCrew: b.crewId,
                          plan: !!b.plan,
                        });
                      }}
                      data-booking-drop={b.plan ? undefined : b.key}
                      data-booking-project={b.plan ? undefined : b.projectId}
                      data-booking-crew={b.plan ? undefined : b.crewId}
                      data-booking-label={b.plan ? undefined : b.label}
                      data-booking-from={b.plan ? undefined : b.from}
                      data-booking-to={b.plan ? undefined : (b.openEnded ? "" : (b.rawTo ?? b.to))}
                      className="absolute rounded flex items-center px-2 text-[10px] text-white truncate font-medium hover:brightness-110 transition select-none"
                      style={{
                        top:    `${b.lane * 24}px`,
                        height: "21px",
                        left:   `calc(${(startIdx / totalDays) * 100}%)`,
                        width:  `calc(${(span / totalDays) * 100}%)`,
                        // Mig 401 — a planning block wears its own colour, dashed
                        // and a touch translucent: a pencil line, not a booking.
                        background: b.plan ? b.plan.colour_hex : c.colour_hex,
                        border: b.plan ? "1px dashed rgba(255,255,255,0.7)" : undefined,
                        opacity: d ? 0.85 : b.plan ? 0.9 : 1,
                        fontStyle: b.plan ? "italic" : undefined,
                        // Mig 425 - a booking sitting inside a stand-down / embargo: red dashed.
                        outline: stopHit ? "2px dashed #ef4444" : b.clash ? "2px solid var(--tw-color-danger, #dc2626)" : undefined,
                        outlineOffset: stopHit ? "1px" : undefined,
                        cursor: canDrag ? (d ? "grabbing" : "grab") : "pointer",
                        boxShadow: d ? "0 0 0 2px rgba(255,255,255,0.6)" : undefined,
                        zIndex: d ? 20 : 1,   // above the today line (z-0)
                      }}
                      title={b.plan ? [
                        `PLANNING BLOCK · ${b.label}`,
                        `${fmtDate(b.from)} → ${fmtDate(b.to)}`,
                        b.plan.note ?? "",
                        "Not a booking - nobody is rostered, the field never sees it.",
                        canDrag ? "Click to edit · drag to move · drag an edge to stretch · drop on another crew" : "",
                      ].filter(Boolean).join("\n") : [
                        b.label,
                        b.project?.project_code ?? "",
                        d
                          ? `Moving to ${fmtDate(pFrom)} → ${fmtDate(pTo)}`
                          : `${fmtDate(b.from)} → ${b.openEnded ? "no finish date" : fmtDate(b.to)}`,
                        stopHit ? `BLOCKED - inside ${stopHit.standdown_number} (${blockedRange(stopHit)}): ${stopHit.reason}. It was booked before the stop; shorten it or move it out.` : "",
                        b.clash ? "OVERLAPS another booking for this crew" : "",
                        b.weekendsOff ? "Mon–Fri job · weekends off" : "",
                        splitting ? `Splitting at today: days before ${fmtDate(TODAY_KEY)} stay put` : "",
                        canDrag && !d ? "Click for dates & who's going · drag to move · drag an edge to resize · drop on another crew to reassign" : "",
                        canDrag && !d && b.from < TODAY_KEY ? "Started: worked days stay where they are - a move shifts today onward" : "",
                      ].filter(Boolean).join("\n")}
                    >
                      <span className="relative z-[1] truncate">{stopHit ? "⛔ " : ""}{b.label}{b.weekendsOff && span > 4 ? <span className="opacity-75"> · M–F</span> : null}</span>
                      {notches.map(i => (
                        <span key={i} className="absolute top-0 bottom-0 pointer-events-none"
                          style={{
                            left:  `${(i / span) * 100}%`, width: `${(1 / span) * 100}%`,
                            background: WEEKEND_NOTCH,
                          }} />
                      ))}
                    </button>
                    </div>
                  );
                })}
                {todayInRange && (
                  <div className="absolute top-0 bottom-0 border-l border-accent/50 pointer-events-none z-0"
                    style={{ left: `calc(${(todayIdx / totalDays) * 100}%)` }} />
                )}
              </div>
            </div>

            {/* ---- the people in this crew ---------------------------------
                One row each, indented under the crew. Their crew work wears the
                crew's colour; anything that took them out of it does not, and
                that difference is the whole point of the row.            */}
            {showPeople && !closedCrews.has(c.crew_id) && (() => {
              const members = membersFor(c.crew_id);
              if (members.length === 0) {
                return (
                  <div key={`${c.crew_id}-empty`}
                    className="grid items-center px-3 py-1.5 border-b border-line/20 bg-ink-raised/20"
                    style={{ gridTemplateColumns: `200px repeat(${totalDays}, minmax(8px, 1fr))` }}>
                    <div className="pl-6 text-[10px] text-text-dim italic">Nobody in this crew</div>
                    <div style={{ gridColumn: `2 / span ${totalDays}` }} />
                  </div>
                );
              }
              return members.map(m => renderPersonRow({
                rowKey:       m.crew_member_id,
                employeeId:   m.employee_id,
                name:         empName(m),
                dotColour:    c.colour_hex,
                isLead:       m.is_lead,
                crewId:       c.crew_id,
                crewMemberId: m.crew_member_id,
                memberFrom:   m.member_from,
                crewRole:     m.crew_role,
                bars: barsForPerson(m.employee_id, empName(m)),
              }));
            })()}
            </div>
          );
        })}

        {/* ---- people who are not in a crew this month ---------------------
            Their own bucket rather than a crew row, because they do not belong
            to one. Same row component, so a person reads identically whether
            they are under RIG-A or down here.                            */}
        {showPeople && (
          <div data-crew-drop={STANDALONE}
            className={personDrag && personDrag.overCrew === STANDALONE && personDrag.crewId
              ? "ring-1 ring-inset ring-accent/70 bg-accent/[0.06]" : ""}>
            <div className="grid items-center px-3 py-1.5 border-b border-line/30 bg-ink-raised/40"
              style={{ gridTemplateColumns: cols }}>
              <div className="min-w-0 pr-3 flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-sm flex-shrink-0"
                  style={{ background: SOLO_BLUE }} />
                <span className="text-[11px] text-text truncate">Not in a crew</span>
              </div>
              <div className="relative flex items-center"
                style={{ gridColumn: `2 / span ${totalDays}` }}>
                <span className="text-[9px] text-text-dim">
                  {standalone.length === 0
                    ? (canDrag ? "Drop somebody here to take them out of their crew" : "Nobody")
                    : `${standalone.length} ${standalone.length === 1 ? "person" : "people"}`
                      + (standaloneFree > 0 ? ` · ${standaloneFree} with nothing booked` : "")
                      + (canDrag ? " · drag them onto a crew to allocate them" : "")}
                </span>
              </div>
            </div>
            {standalone.map(x => renderPersonRow({
              rowKey:       x.person.employee_id,
              employeeId:   x.person.employee_id,
              name:         x.name,
              dotColour:    SOLO_BLUE,
              isLead:       false,
              crewId:       null,
              crewMemberId: null,
              memberFrom:   null,
              crewRole:     "tech",
              bars:         x.bars,
            }))}
          </div>
        )}

        {/* The queue: won work with dates and nobody on it. */}
        <div className="grid items-center px-3 py-2 bg-ink-raised/30"
          style={{ gridTemplateColumns: `200px repeat(${totalDays}, minmax(8px, 1fr))` }}>
          <div className="min-w-0 pr-3">
            <div className="text-[12px] text-text truncate">Needs a crew</div>
            <div className="text-[10px] text-text-muted truncate">
              {unbooked.length} job{unbooked.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="relative" style={{ gridColumn: `2 / span ${totalDays}`, height: `${Math.max(1, unbooked.length) * 24}px` }}>
            {RowBackdrop}
            {unbooked.length === 0 && (
              <div className="absolute inset-0 flex items-center">
                <span className="text-[10px] text-text-dim italic">Everything with dates has a crew</span>
              </div>
            )}
            {unbooked.map((p, i) => {
              const eff = effectiveWindow(p, gridEndKey);
              const from = eff.start ?? gridStartKey;
              const to   = eff.end ?? gridEndKey;
              const startIdx = clipIdx(from);
              const endIdx   = clipIdx(to < from ? from : to);
              const span     = Math.max(1, endIdx - startIdx + 1);
              const isDraggingThis = projDrag?.projectId === p.project_id;
              return (
                <button
                  key={p.project_id}
                  type="button"
                  onClick={() => {
                    if (draggedRef.current) { draggedRef.current = false; return; }
                    onSelectProject(p);
                  }}
                  onPointerDown={e => beginProjDrag(e, {
                    projectId: p.project_id,
                    label: p.sites?.site_name ?? p.project_name,
                    from, to: eff.end,
                  })}
                  className="absolute rounded flex items-center px-2 text-[10px] truncate font-medium transition-shadow"
                  style={{
                    top: `${i * 24}px`, height: "21px",
                    left:  `calc(${(startIdx / totalDays) * 100}%)`,
                    width: `calc(${(span / totalDays) * 100}%)`,
                    background: isDraggingThis ? `${UNBOOKED_GREEN}44` : `${UNBOOKED_GREEN}22`,
                    border: `1px dashed ${UNBOOKED_GREEN}99`,
                    color: UNBOOKED_GREEN,
                    cursor: canDrag ? (isDraggingThis ? "grabbing" : "grab") : "pointer",
                    boxShadow: isDraggingThis ? "0 2px 8px rgba(0,0,0,0.35)" : undefined,
                  }}
                  title={`${p.project_code} · ${p.sites?.site_name ?? p.project_name}\nNo crew booked · ${fmtDate(from)} → ${fmtDate(to)}${canDrag ? "\nDrag onto a crew to book them on it" : ""}`}
                >
                  {p.sites?.site_name ?? p.project_name}
                </button>
              );
            })}
            {todayInRange && (
              <div className="absolute top-0 bottom-0 border-l border-accent/50 pointer-events-none z-0"
                style={{ left: `calc(${(todayIdx / totalDays) * 100}%)` }} />
            )}
          </div>
        </div>
      </div>

      {/* Drag ghost. The whole point of dropping on the grid rather than opening
          a date picker is that the gesture carries the date - so the date has to
          be legible mid-drag, or it is just an invisible guess. */}
      {projDrag && (
        <div className="fixed z-[60] pointer-events-none rounded shadow-lg border px-2.5 py-1.5 bg-ink-raised"
          style={{ left: projDrag.x + 14, top: projDrag.y + 14, borderColor: `${UNBOOKED_GREEN}99` }}>
          <div className="text-[11px] text-text font-medium">{projDrag.label}</div>
          <div className="text-[10px] text-text-muted mt-0.5">
            {projDrag.overCrew && projDrag.overCrew !== STANDALONE
              ? <>Book <strong>{crews.find(c => c.crew_id === projDrag.overCrew)?.crew_name ?? "this crew"}</strong>
                  {" "}· {fmtDate(projDrag.from)} → {projDrag.to ? fmtDate(projDrag.to) : "open ended"}</>
              : "Drop on a crew to book them on this job"}
          </div>
        </div>
      )}

      {sendTo && (
        <SendToJobModal s={sendTo} crews={crews} onClose={() => setSendTo(null)}
          onSaved={() => { setSendTo(null); qcDrag.invalidateQueries({ queryKey: ["schedule_roster"] }); qcDrag.invalidateQueries({ queryKey: ["visit_roster"] }); }} />
      )}
      {personDrag && (
        <div className="fixed z-[60] pointer-events-none rounded shadow-lg border border-accent/60 bg-ink-raised px-2.5 py-1.5"
          style={{ left: personDrag.x + 14, top: personDrag.y + 14 }}>
          <div className="text-[11px] text-text font-medium">{personDrag.name}</div>
          <div className="text-[10px] text-text-muted mt-0.5">
            {personDrag.overBooking
              ? <>→ on <span className="text-accent">{personDrag.overBooking.label}</span> from <span className="text-accent">{format(addDays(gridStart, personDrag.dayIdx), "EEE d MMM")}</span> · stays in their crew</>
              : !personDrag.overCrew
              ? "Drop on a job bar to send them there · drop on a crew row to move crews"
              : personDrag.overCrew === personDrag.crewId
                ? "Already in this crew"
                : <>
                    → {personDrag.overCrew === STANDALONE
                        ? "out of their crew"
                        : (crews.find(c => c.crew_id === personDrag.overCrew)?.crew_name ?? "crew")}
                    {" "}from{" "}
                    <span className="text-accent">
                      {format(addDays(gridStart, personDrag.dayIdx), "EEE d MMM")}
                    </span>
                    {" "}· their site visits follow
                  </>}
          </div>
        </div>
      )}

      {editing && (
        <BookingEditor
          booking={{
            pca_id: editing.pca_id, crew_id: editing.crew_id,
            from: editing.from, to: editing.to, projectId: editing.projectId,
            weekendsOff: editing.weekendsOff ?? false,
          }}
          crews={crews}
          projectLabel={editing.label}
          onClose={() => setEditing(null)}
        />
      )}

      {editingPlan && (
        <PlanBlockEditor
          initial={editingPlan}
          crews={crews}
          onClose={() => setEditingPlan(null)}
        />
      )}

    </Card>
  );
}

// =============================================================================
// PlanBlockEditor — mig 401. A planning block: title, colour, dates, a note.
// =============================================================================
// Kept deliberately small. It is a pencil line: if it needs a project, a
// roster or a client, it should be a booking, and the way to do that is to
// drag the job onto the crew.
// =============================================================================
function PlanBlockEditor({ initial, crews, onClose }: {
  initial: { block?: PlanBlockRow; crew_id: string; plan_from: string; plan_to: string };
  crews: { crew_id: string; crew_name: string; colour_hex: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const user = useRequireAuth();
  const isNew = !initial.block;
  const [title, setTitle]   = useState(initial.block?.title ?? "");
  const [colour, setColour] = useState(initial.block?.colour_hex ?? PLAN_COLOURS[0]);
  const [crewId, setCrewId] = useState(initial.crew_id);
  const [from, setFrom]     = useState(initial.plan_from);
  const [to, setTo]         = useState(initial.plan_to);
  const [note, setNote]     = useState(initial.block?.note ?? "");
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState<string | null>(null);

  const days = Math.max(1, differenceInCalendarDays(parseISO(to < from ? from : to), parseISO(from)) + 1);

  function finish(error: { message: string } | null) {
    setBusy(false);
    if (error) { setErr(error.message); return; }
    qc.invalidateQueries({ queryKey: ["schedule_plan_blocks"] });
    onClose();
  }
  async function save() {
    if (!title.trim()) { setErr("Give it a title - even a question mark."); return; }
    setBusy(true); setErr(null);
    const row = { crew_id: crewId, title: title.trim(), colour_hex: colour, plan_from: from,
                  plan_to: to < from ? from : to, note: note.trim() || null };
    const { error } = isNew
      ? await (supabase as any).from("crew_plan_blocks").insert({ ...row, created_by: user?.user_id ?? null })
      : await (supabase as any).from("crew_plan_blocks").update(row).eq("block_id", initial.block!.block_id);
    finish(error);
  }
  async function remove() {
    if (!initial.block) return;
    if (!window.confirm(`Rub out "${initial.block.title}"?`)) return;
    setBusy(true);
    const { error } = await (supabase as any).from("crew_plan_blocks").delete().eq("block_id", initial.block.block_id);
    finish(error);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg border border-line bg-ink-card shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div>
            <div className="text-[13px] font-semibold text-text">{isNew ? "Pencil in a plan" : "Planning block"}</div>
            <div className="text-[11px] text-text-muted">A note with dates on the crew row — not a booking, nobody is rostered, the field never sees it.</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-ink-hover text-text-muted"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider2 text-text-dim">What</span>
            <input autoFocus value={title} onChange={e => setTitle(e.target.value)} maxLength={80}
              placeholder="e.g. Telstra Toowoomba? · Leave · Training week"
              className="mt-1 block w-full bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text" />
          </label>
          <div>
            <span className="text-[10px] uppercase tracking-wider2 text-text-dim">Colour</span>
            <div className="mt-1 flex items-center gap-1.5">
              {PLAN_COLOURS.map(cx => (
                <button key={cx} type="button" onClick={() => setColour(cx)}
                  className={`w-6 h-6 rounded border-2 ${colour === cx ? "border-white" : "border-transparent"}`}
                  style={{ background: cx }} title={cx} />
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider2 text-text-dim">Crew</span>
            <select value={crewId} onChange={e => setCrewId(e.target.value)}
              className="mt-1 block w-full bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text">
              {crews.map(c => <option key={c.crew_id} value={c.crew_id}>{c.crew_name}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider2 text-text-dim">From</span>
              <CommitDateInput value={from} onCommit={v => { if (v) { setFrom(v); if (to < v) setTo(v); } }}
                className="mt-1 block w-full bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider2 text-text-dim">To</span>
              <CommitDateInput value={to} min={from} onCommit={v => { if (v) setTo(v < from ? from : v); }}
                className="mt-1 block w-full bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text" />
            </label>
          </div>
          <div className="text-[11px] text-text-muted">{fmtDate(from)} → {fmtDate(to < from ? from : to)} · {days} day{days === 1 ? "" : "s"}</div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider2 text-text-dim">Note (optional)</span>
            <textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
              placeholder="Who said what, what needs to happen before this is real."
              className="mt-1 block w-full bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text" />
          </label>
          {err && <div className="text-[11px] text-danger">{err}</div>}
        </div>
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-line">
          {isNew ? <span /> : (
            <Button variant="ghost" size="sm" icon={Trash2} disabled={busy} onClick={remove}>Rub out</Button>
          )}
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={save} disabled={busy}>{busy ? "Saving…" : isNew ? "Pencil it in" : "Save"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// TaskDetail — an ad-hoc task, opened from a calendar chip.
// =============================================================================
// A task was write-only until now: "Assign task" created it, a bell fired, and
// nothing ever showed it again. So the panel has to do the two things a manager
// actually wants from a task they can see - move it, or close it out - without
// making them go and find the person's file.
// =============================================================================
function TaskDetail({
  task, who, onClose, onChanged,
}: {
  task: PersonalBookingRow;
  who: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function setStatus(status: string) {
    setBusy(true); setErr(null);
    const { error } = await supabase
      .from("personal_bookings")
      .update({ status })
      .eq("booking_id", task.booking_id);
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onChanged();
  }

  const start = parseISO(task.scheduled_start);
  const end   = task.scheduled_end ? parseISO(task.scheduled_end) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-raised border border-line rounded-lg p-5 max-w-md w-full"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded flex-shrink-0
                               bg-[rgba(255,120,68,0.18)] text-[rgb(214,88,38)]">
                <Send size={12}/>
              </span>
              <h3 className="text-[15px] font-semibold truncate">{task.title}</h3>
            </div>
            <div className="text-[11px] text-text-muted mt-1">{task.booking_kind === "depot" ? "Depot day" : "Ad-hoc task"} · {who}</div>
          </div>
          <button onClick={onClose} type="button" className="text-text-muted hover:text-text flex-shrink-0">
            <X size={16}/>
          </button>
        </div>

        <div className="space-y-2 text-[12px]">
          <div className="flex items-center gap-2 text-text">
            <Clock size={12} className="text-text-muted flex-shrink-0"/>
            <span>
              {format(start, "EEE d MMM, HH:mm")}
              {end && <> → {format(end, "EEE d MMM, HH:mm")}</>}
            </span>
          </div>
          {task.location_address && (
            <div className="flex items-start gap-2 text-text-muted">
              <MapPin size={12} className="flex-shrink-0 mt-0.5"/>
              <span>{task.location_address}</span>
            </div>
          )}
          {task.notes && (
            <p className="text-text-muted whitespace-pre-wrap pt-1">{task.notes}</p>
          )}
        </div>

        {err && (
          <div className="mt-3 text-[12px] text-danger flex items-start gap-1.5">
            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5"/><span>{err}</span>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-line/50">
          <Pill label={task.status.replace(/_/g, " ")} tone="default" small />
          {canManage && task.status !== "done" && task.status !== "cancelled" && (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" disabled={busy}
                onClick={() => setStatus("cancelled")}>Cancel task</Button>
              <Button variant="primary" size="sm" icon={CheckCircle2} disabled={busy}
                onClick={() => setStatus("done")}>Mark done</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// PeopleView — EVERY schedulable person, one row each. The whole workforce on
// one page.
// =============================================================================
// James asked for this three times before it got built, and he was right every
// time: "I still think we need a people list of all employees, not just
// dropped down in crews - a clean crisp visual of that."
//
// The Crews tab answers "what is this crew doing". This answers the other
// question - "where is EVERYBODY" - and they are different questions. Here a
// person who moved crews mid-month shows BOTH stints on one row, each in the
// colour of the crew they were in at the time, which is the history James
// described wanting to scroll back through. The Crews tab cannot show that,
// because each crew section only knows its own colour.
//
// Design decisions, so they survive the next pass:
//   * Unallocated people sort FIRST, with an amber "No crew" chip. They are
//     who you are looking for when you need a body.
//   * The crew chip is a BUTTON (managers): it opens a move panel - new crew,
//     from a date - running the same close-old/open-new writes as dragging on
//     the Crews tab. Same operation, second doorway.
//   * Names open Assign task, same as everywhere else on this screen.
//   * Rows are taller than the Crews tab's nested rows (this is a primary
//     surface, not a drill-down) and every bar is labelled with its site.
// =============================================================================
function PeopleView({
  schedulablePeople, crewMembers, crews, assignments, projects,
  projectTeam, personalBookings, leaveRows, defectRows, rosterRows, weeks,
  onSelectPerson, onMoved,
}: {
  schedulablePeople: SchedulablePerson[];
  crewMembers: CrewMemberRow[];
  crews: { crew_id: string; crew_code: string; crew_name: string; colour_hex: string;
           crew_kind: string; lead_employee_id: string | null }[];
  assignments: CrewAssignmentRow[];
  projects: ProjectRow[];
  projectTeam: ProjectTeamRow[];
  personalBookings: PersonalBookingRow[];
  leaveRows: LeaveRow[];
  defectRows: DefectRow[];
  rosterRows: RosterRow[];
  weeks: Date[][];
  onSelectPerson: (employeeId: string, name: string, dateKey?: string) => void;
  onMoved: () => void;
}) {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");

  const gridStart    = weeks[0][0];
  const gridEnd      = weeks[5][6];
  const totalDays    = differenceInCalendarDays(gridEnd, gridStart) + 1;
  const gridStartKey = format(gridStart, "yyyy-MM-dd");
  const gridEndKey   = format(gridEnd,   "yyyy-MM-dd");
  const todayKey     = format(new Date(), "yyyy-MM-dd");
  const todayIdx     = differenceInCalendarDays(new Date(), gridStart);
  const todayInRange = todayIdx >= 0 && todayIdx < totalDays;

  const clipIdx = (key: string) => {
    if (key < gridStartKey) return 0;
    if (key > gridEndKey)   return totalDays - 1;
    return differenceInCalendarDays(parseISO(key), gridStart);
  };

  const [search, setSearch] = useState("");
  const [moving, setMoving] = useState<null | { employeeId: string; name: string }>(null);

  const projById = useMemo(
    () => new Map(projects.map(p => [p.project_id, p])),
    [projects],
  );
  const crewById = useMemo(() => new Map(crews.map(c => [c.crew_id, c])), [crews]);

  // Crew colours, for painting roster stints in the colour of the crew whose
  // booking hosts them. Falls back to the assignments embed so a booking held
  // by a crew that has since been deactivated still keeps its colour.
  const crewColour = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of crews) if (c.colour_hex) m.set(c.crew_id, c.colour_hex);
    for (const a of assignments) if (a.crews?.crew_id && a.crews.colour_hex) m.set(a.crews.crew_id, a.crews.colour_hex);
    return m;
  }, [crews, assignments]);

  const ctx: PersonBarsCtx = useMemo(() => ({
    gridStartKey, gridEndKey, projById, projectTeam, personalBookings, leaveRows,
    defects: defectRows, roster: rosterRows, crewColour,
  }), [gridStartKey, gridEndKey, projById, projectTeam, personalBookings, leaveRows,
      defectRows, rosterRows, crewColour]);

  type Row = {
    person: SchedulablePerson;
    name: string;
    // the crew they are in TODAY, for the chip
    crewNow: { crew_id: string; crew_code: string; crew_name: string; colour_hex: string } | null;
    bars: PersonBar[];
    freeToday: boolean;
  };

  const rows: Row[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    return schedulablePeople
      .map(p => {
        const name = `${p.preferred_name?.trim() || p.first_name} ${p.last_name}`.trim();

        // 333 - one call. Site stints come straight off the roster (each in the
        // colour of the crew whose booking hosted it), so a mid-month move shows
        // both sites without any membership-window arithmetic - and the history
        // survives the membership being edited, which the old derivation did not.
        const bars: PersonBar[] = buildPersonBars(ctx, p.employee_id, name);

        // Membership is still what the crew CHIP means: which crew are they in
        // today. It just no longer decides where they were.
        const stints = crewMembers.filter(m =>
          m.employee_id === p.employee_id
          && m.member_from <= gridEndKey
          && (m.member_to ?? "9999-12-31") >= gridStartKey);
        const nowStint = stints.find(st =>
          st.member_from <= todayKey && (st.member_to ?? "9999-12-31") >= todayKey);
        const crewNow = nowStint ? (crewById.get(nowStint.crew_id) ?? null) : null;
        const freeToday = !bars.some(b => b.from <= todayKey && b.to >= todayKey);
        return { person: p, name, crewNow, bars, freeToday };
      })
      .filter(r => !q || r.name.toLowerCase().includes(q)
        || (r.crewNow?.crew_name.toLowerCase().includes(q) ?? false))
      .sort((a, z) =>
        (a.crewNow ? 1 : 0) - (z.crewNow ? 1 : 0)   // no-crew first
        || a.name.localeCompare(z.name));
  }, [schedulablePeople, crewMembers, crewById, ctx, search, todayKey,
      gridStartKey, gridEndKey]);

  const noCrew    = rows.filter(r => !r.crewNow).length;
  const freeToday = rows.filter(r => r.freeToday).length;

  return (
    <Card>
      {/* Header strip: the numbers a scheduler opens this view for. */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-line/50 text-[11px] flex-wrap">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-dim"/>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Find a person or crew…"
            className="bg-ink border border-line rounded pl-6 pr-2 py-1 text-[12px] w-[190px]
                       focus:outline-none focus:border-accent"
          />
        </div>
        <span className="text-text-muted">
          {rows.length} people
          {noCrew > 0 && <> · <span className="text-warn font-medium">{noCrew} in no crew</span></>}
          {freeToday > 0 && <> · {freeToday} free today</>}
        </span>
        <span className="ml-auto inline-flex items-center gap-3 text-[10px] text-text-muted">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-2 rounded-sm bg-text-dim/60"/> crew work
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-2 rounded-sm" style={{ background: SOLO_BLUE }}/> on a job alone
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-2 rounded-sm" style={{ background: DEPOT_SLATE }}/> depot
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-2.5 rounded-sm" style={{
              background: PERSON_BAR_STYLE.listed.hatch, border: PERSON_BAR_STYLE.listed.border }}/> listed
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-2.5 rounded-sm" style={{
              background: PERSON_BAR_STYLE.task.hatch, border: PERSON_BAR_STYLE.task.border }}/> task
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-2.5 rounded-sm" style={{
              background: PERSON_BAR_STYLE.leave.hatch, border: PERSON_BAR_STYLE.leave.border }}/> leave
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-2.5 rounded-sm" style={{
              background: PERSON_BAR_STYLE.defect.hatch, border: PERSON_BAR_STYLE.defect.border }}/> defect
          </span>
        </span>
      </div>

      <div className="overflow-x-auto">
        <ScheduleAxis weeks={weeks} gridStart={gridStart} totalDays={totalDays}
          labelCol="230px" label="person" />
        <StopsLane gridStartKey={gridStartKey} totalDays={totalDays} labelCol="230px" />   {/* mig 425 */}

        {rows.map(r => (
          <div key={r.person.employee_id}
            className={`grid items-center px-3 border-b border-line/25 ${
              !r.crewNow ? "bg-warn/[0.04]" : ""}`}
            style={{ gridTemplateColumns: `230px repeat(${totalDays}, minmax(8px, 1fr))`,
                     paddingTop: 4, paddingBottom: 4 }}>

            <div className="min-w-0 pr-3">
              <button type="button"
                onClick={() => onSelectPerson(r.person.employee_id, r.name)}
                className="text-[12px] text-text hover:text-accent truncate text-left block max-w-full"
                title={`Assign ${r.name} a one-off task`}>
                {r.name}
              </button>
              {/* The chip is the move control. Where they are, and the door to
                  putting them somewhere else. */}
              <button type="button"
                disabled={!canManage}
                onClick={() => canManage && setMoving({ employeeId: r.person.employee_id, name: r.name })}
                title={canManage ? "Move to another crew" : undefined}
                className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10px] border transition-colors ${
                  r.crewNow
                    ? "border-line text-text-muted" + (canManage ? " hover:border-accent hover:text-accent" : "")
                    : "border-warn/50 text-warn bg-warn/10" + (canManage ? " hover:border-warn" : "")}`}>
                {r.crewNow
                  ? <><span className="inline-block w-1.5 h-1.5 rounded-full"
                        style={{ background: r.crewNow.colour_hex }}/>{r.crewNow.crew_name}</>
                  : <><AlertCircle size={9}/>No crew</>}
                {canManage && <ArrowRightLeft size={9} className="opacity-60"/>}
              </button>
            </div>

            {(() => {
              // Same two-lines rule as the Crews tab: overlapping site stints
              // stack; warning hatches span the whole row.
              const laneOf = new Map<string, number>();
              const solids = r.bars
                .filter(b => PERSON_BAR_STYLE[b.kind].solid || b.kind === "listed")
                .sort((a, z) => a.from.localeCompare(z.from) || a.to.localeCompare(z.to));
              const laneEnd: string[] = [];
              for (const b of solids) {
                let l = laneEnd.findIndex(end => b.from > end);
                if (l === -1) { l = laneEnd.length; laneEnd.push(b.to); }
                else laneEnd[l] = b.to;
                laneOf.set(b.key, l);
              }
              const laneCount = Math.max(1, laneEnd.length);
              const rowH = laneCount * 22;
              return (
            <div className="relative" style={{ gridColumn: `2 / span ${totalDays}`, height: `${rowH}px` }}>
              <ScheduleBackdrop gridStart={gridStart} totalDays={totalDays} />
              {r.bars.length === 0 && (
                <div className="absolute inset-0 flex items-center">
                  <span className="text-[9px] text-text-dim italic">
                    {r.crewNow ? "Nothing booked this month" : "Free all month — no crew, nothing booked"}
                  </span>
                </div>
              )}
              {r.bars.map(pb => {
                const startIdx = clipIdx(pb.from);
                const endIdx   = clipIdx(pb.to < pb.from ? pb.from : pb.to);
                const span     = Math.max(1, endIdx - startIdx + 1);
                const st = PERSON_BAR_STYLE[pb.kind];
                const inLane = laneOf.has(pb.key);
                return (
                  <div key={pb.key} title={pb.title}
                    className={`absolute rounded-sm text-[9px] leading-[18px] px-1.5 truncate font-medium ${
                      st.solid ? "text-white/95" : "text-text"}`}
                    style={{
                      left:   `calc(${(startIdx / totalDays) * 100}%)`,
                      width:  `calc(${(span / totalDays) * 100}% - 2px)`,
                      top:    inLane ? 2 + (laneOf.get(pb.key)! * 22) : 1,
                      height: inLane ? 18 : rowH - 2,
                      background: st.solid ? (pb.colour ?? undefined) : st.hatch,
                      border:     st.solid ? undefined : st.border,
                      zIndex:     st.solid ? 1 : 2,
                    }}>
                    {span > 2 && pb.label}
                  </div>
                );
              })}
              {todayInRange && (
                <div className="absolute top-0 bottom-0 border-l border-accent/50 pointer-events-none z-0"
                  style={{ left: `calc(${(todayIdx / totalDays) * 100}%)` }} />
              )}
            </div>
              );
            })()}
          </div>
        ))}

        {rows.length === 0 && (
          <div className="px-3 py-8 text-center text-[12px] text-text-muted italic">
            Nobody matches that search.
          </div>
        )}
      </div>

      {moving && (
        <MoveCrewModal
          employeeId={moving.employeeId}
          name={moving.name}
          crews={crews}
          crewMembers={crewMembers}
          onClose={() => setMoving(null)}
          onMoved={() => { setMoving(null); onMoved(); }}
        />
      )}
    </Card>
  );
}

// =============================================================================
// MoveCrewModal — put one person in a crew, from a date. The same operation as
// dragging them on the Crews tab (close the old dated stint, open the new one)
// through a second doorway, for when you are looking at the person rather than
// the crew.
// =============================================================================
function MoveCrewModal({
  employeeId, name, crews, crewMembers, onClose, onMoved,
}: {
  employeeId: string;
  name: string;
  crews: { crew_id: string; crew_name: string; crew_code: string; colour_hex: string }[];
  crewMembers: CrewMemberRow[];
  onClose: () => void;
  onMoved: () => void;
}) {
  const todayKey = format(new Date(), "yyyy-MM-dd");

  const [target, setTarget] = useState<string>("");   // "" = out of every crew
  const [from, setFrom]     = useState(todayKey);
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState<string | null>(null);

  // The stint to close is the one covering the date they MOVE, not the one
  // covering today. James's planning question made the difference concrete:
  // "Jai is in the Sharon [a future stint] but on the 8th he goes to Beechmont"
  // - a plan like that is a CHAIN of future moves, and closing the today-stint
  // each time would close the wrong row from the second move onward. Keyed on
  // the chosen date, so as James changes "From", the panel re-reads which stint
  // that date actually falls in and says so.
  const current = useMemo(() =>
    crewMembers.find(m =>
      m.employee_id === employeeId
      && m.member_from <= from
      && (m.member_to ?? "9999-12-31") >= from) ?? null,
    [crewMembers, employeeId, from]);

  // A stint that starts AFTER the chosen date would still collide with the new
  // open-ended one - the guard would reject it. Better to say so up front than
  // to let the save bounce.
  const laterStint = useMemo(() =>
    crewMembers.find(m =>
      m.employee_id === employeeId
      && m.member_from > from) ?? null,
    [crewMembers, employeeId, from]);

  const currentCrewName =
    current ? (crews.find(c => c.crew_id === current.crew_id)?.crew_name ?? "their crew") : null;
  const laterCrewName =
    laterStint ? (crews.find(c => c.crew_id === laterStint.crew_id)?.crew_name ?? "another crew") : null;

  async function commit() {
    setBusy(true); setErr(null);
    const dayBefore = format(addDays(parseISO(from), -1), "yyyy-MM-dd");
    try {
      if (current) {
        if (dayBefore < current.member_from) {
          const { error } = await supabase.from("crew_members")
            .delete().eq("crew_member_id", current.crew_member_id);
          if (error) throw error;
        } else {
          const { error } = await supabase.from("crew_members")
            .update({ member_to: dayBefore }).eq("crew_member_id", current.crew_member_id);
          if (error) throw error;
        }
      }
      if (target) {
        const { error } = await supabase.from("crew_members").insert({
          crew_id: target, employee_id: employeeId,
          member_from: from, member_to: null,
          is_lead: false, crew_role: current?.crew_role ?? "tech",
        });
        if (error) throw error;
      }
      onMoved();
    } catch (e: any) {
      setErr(e?.code === "23P01"
        ? `${name} is already in another crew over those dates — end that membership first.`
        : e?.message ?? String(e));
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-raised border border-line rounded-lg p-5 max-w-sm w-full"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[15px] font-semibold inline-flex items-center gap-2">
            <ArrowRightLeft size={14} className="text-accent"/> Move {name}
          </h3>
          <button onClick={onClose} type="button" className="text-text-muted hover:text-text"><X size={16}/></button>
        </div>

        <div className="space-y-3">
          <Field label="To">
            <Select value={target} onChange={e => setTarget(e.target.value)}>
              <option value="">— out of every crew —</option>
              {crews
                .filter(c => c.crew_id !== current?.crew_id)
                .map(c => <option key={c.crew_id} value={c.crew_id}>{c.crew_name}</option>)}
            </Select>
          </Field>
          <Field label="From" hint="Their history up to this date is kept exactly as it was.">
            <Input type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </Field>

          <div className="text-[11px] text-text-muted">
            {current
              ? <>Ends their time in <strong>{currentCrewName}</strong> the day before, {" "}
                  {target ? "and starts the new crew" : "leaving them in no crew"} on {fmtDate(from)}.
                  {" "}Their site visits follow the move — off {currentCrewName}'s upcoming jobs{target ? ", onto the new crew's" : ""}; days already worked stay on record.
                  {from > todayKey && <> A future move — nothing changes until then, and the schedule shows the plan.</>}</>
              : target
                ? <>They are in no crew on that date — this starts them on {fmtDate(from)} and rosters them on that crew's upcoming jobs.</>
                : <>They are already in no crew on that date.</>}
          </div>

          {laterStint && target && (
            <div className="text-[11px] text-warn flex items-start gap-1.5">
              <AlertTriangle size={11} className="flex-shrink-0 mt-0.5"/>
              <span>
                They are already booked into <strong>{laterCrewName}</strong> from{" "}
                {fmtDate(laterStint.member_from)}, which this open-ended move would run into.
                Move them on a date after that stint, or take that booking off first.
              </span>
            </div>
          )}

          {err && (
            <div className="text-[12px] text-danger flex items-start gap-1.5">
              <AlertTriangle size={12} className="flex-shrink-0 mt-0.5"/><span>{err}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 mt-4">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" size="sm"
            disabled={busy || (!current && !target) || (!!laterStint && !!target)} onClick={commit}>
            {busy ? "Moving…" : "Move"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// KeyDateDetail — what you get when you click a promoted date.
// =============================================================================
// James: "if you click it you see the details." Read-only on purpose: this is
// the company view of somebody else's project booking, and the place to change
// it is the project it belongs to - so the panel ends in a link there rather
// than a second edit form that could disagree with the first.
// =============================================================================
function KeyDateDetail({
  booking, onClose,
}: { booking: PromotedBookingRow; onClose: () => void }) {
  const site   = booking.projects?.sites?.site_name ?? booking.projects?.project_name ?? "";
  const outage = booking.booking_type === "outage";
  const from   = booking.scheduled_start;
  const to     = booking.scheduled_end;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-ink-raised border border-line rounded-lg p-5 max-w-md w-full"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded flex-shrink-0"
                style={{ background: PROMOTED_PURPLE }}>
                {outage ? <RadioTower size={13} className="text-white"/> : <Star size={13} className="text-white"/>}
              </span>
              <h3 className="text-[15px] font-semibold truncate">{booking.title}</h3>
            </div>
            <div className="text-[11px] text-text-muted mt-1">
              {booking.booking_type.replace(/_/g, " ")}
              {site && <> · {site}</>}
              {booking.projects?.project_code && (
                <> · <span className="font-mono">{booking.projects.project_code}</span></>
              )}
            </div>
          </div>
          <button onClick={onClose} type="button" className="text-text-muted hover:text-text flex-shrink-0">
            <X size={16}/>
          </button>
        </div>

        <div className="space-y-2 text-[12px]">
          <div className="flex items-center gap-2 text-text">
            <Clock size={12} className="text-text-muted flex-shrink-0"/>
            <span>
              {format(parseISO(from), "EEE d MMM, HH:mm")}
              {to && <> → {format(parseISO(to), "EEE d MMM, HH:mm")}</>}
            </span>
          </div>

          {booking.location_notes && (
            <div className="flex items-start gap-2 text-text-muted">
              <MapPin size={12} className="flex-shrink-0 mt-0.5"/>
              <span>{booking.location_notes}</span>
            </div>
          )}

          {booking.crq_number && (
            <div className="flex items-center gap-2">
              <RadioTower size={12} className="text-text-muted flex-shrink-0"/>
              <span className="font-mono text-orange-300">{booking.crq_number}</span>
              <span className="text-[10px] text-text-dim">CRQ</span>
            </div>
          )}

          {outage && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1">
              {booking.customer_impacting === true && (
                <span className="inline-flex items-center gap-1 text-warn">
                  <AlertTriangle size={12}/> Service affecting
                </span>
              )}
              {booking.customer_impacting === false && (
                <span className="inline-flex items-center gap-1 text-text-muted">
                  <CheckCircle2 size={12}/> Non service affecting
                </span>
              )}
              {booking.customer_impacting == null && (
                <span className="inline-flex items-center gap-1 text-text-dim">
                  <AlertTriangle size={12}/> Impact not confirmed
                </span>
              )}
              <span className={`inline-flex items-center gap-1 ${booking.outage_approved ? "text-ok" : "text-text-dim"}`}>
                {booking.outage_approved
                  ? <><CheckCircle2 size={12}/> Carrier approved</>
                  : <><Clock size={12}/> Awaiting carrier approval</>}
              </span>
            </div>
          )}

          {booking.external_contact_name && (
            <div className="flex items-center gap-2 text-text-muted">
              <Phone size={12} className="flex-shrink-0"/>
              <span>
                {booking.external_contact_name}
                {booking.external_contact_phone && <> · {booking.external_contact_phone}</>}
              </span>
              <span className="text-[10px] text-text-dim">
                {outage ? "NIC engineer" : "external contact"}
              </span>
            </div>
          )}

          {booking.description && (
            <p className="text-text-muted pt-1 whitespace-pre-wrap">{booking.description}</p>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-line/50">
          <Pill label={booking.status.replace(/_/g, " ")} tone="default" small />
          <Link to={`/projects/${booking.project_id}/schedule`}
            className="text-[12px] text-accent hover:underline inline-flex items-center gap-1">
            Open on the project <ExternalLink size={11}/>
          </Link>
        </div>
      </div>
    </div>
  );
}

function GanttView({
  projects, assignments, weeks, onSelectProject, getColour,
  personalBookings, nameFor, onSelectTask,
}: {
  projects: ProjectRow[];
  assignments: CrewAssignmentRow[];
  weeks: Date[][];
  onSelectProject: (p: ProjectRow) => void;
  getColour: (p: ProjectRow) => { colour: string; primaryCrewCode: string | null; crewCount: number };
  personalBookings: PersonalBookingRow[];
  nameFor: (id: string) => string;
  onSelectTask: (pb: PersonalBookingRow) => void;
}) {
  const gridStart = weeks[0][0];
  const gridEnd   = weeks[5][6];
  const totalDays = differenceInCalendarDays(gridEnd, gridStart) + 1;
  const gridStartKey = format(gridStart, "yyyy-MM-dd");
  const gridEndKey   = format(gridEnd,   "yyyy-MM-dd");

  // Sort: projects with crews first (by primary crew code), then unassigned —
  // makes "what needs allocating" easy to spot at the bottom.
  const ordered = useMemo(() => {
    return [...projects].sort((a, z) => {
      const ca = getColour(a);
      const cz = getColour(z);
      if ((cz.crewCount > 0 ? 1 : 0) !== (ca.crewCount > 0 ? 1 : 0)) {
        return (cz.crewCount > 0 ? 1 : 0) - (ca.crewCount > 0 ? 1 : 0);
      }
      return (a.scheduled_start_date ?? "").localeCompare(z.scheduled_start_date ?? "");
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, assignments]);

  // Helper — turn a date string into a "day index" within the visible window,
  // clipped so out-of-window bars stay flush with the edge.
  function clipIdx(key: string): number {
    if (key < gridStartKey) return 0;
    if (key > gridEndKey)   return totalDays - 1;
    return differenceInCalendarDays(parseISO(key), gridStart);
  }

  const todayIdx = differenceInCalendarDays(new Date(), gridStart);
  const todayInRange = todayIdx >= 0 && todayIdx < totalDays;

  // Tasks in the visible window, grouped per person, lane-packed so somebody
  // with two overlapping tasks shows both instead of one hiding the other.
  const taskRows = useMemo(() => {
    type TB = { pb: PersonalBookingRow; from: string; to: string; lane: number };
    const byPerson = new Map<string, TB[]>();
    for (const pb of personalBookings) {
      const from0 = localDayKey(pb.scheduled_start);
      const to0   = localDayKey(pb.scheduled_end ?? pb.scheduled_start);
      const to1   = to0 < from0 ? from0 : to0;
      if (from0 > gridEndKey || to1 < gridStartKey) continue;
      const rec = {
        pb,
        from: from0 < gridStartKey ? gridStartKey : from0,
        to:   to1   > gridEndKey   ? gridEndKey   : to1,
        lane: 0,
      };
      const list = byPerson.get(pb.assignee_employee_id) ?? [];
      list.push(rec);
      byPerson.set(pb.assignee_employee_id, list);
    }
    return [...byPerson.entries()].map(([employeeId, bars]) => {
      bars.sort((a, z) => a.from.localeCompare(z.from));
      const laneEnd: string[] = [];
      for (const b of bars) {
        let lane = laneEnd.findIndex(end => end < b.from);
        if (lane === -1) { lane = laneEnd.length; laneEnd.push(b.to); } else { laneEnd[lane] = b.to; }
        b.lane = lane;
      }
      return { employeeId, name: nameFor(employeeId), bars, laneCount: Math.max(1, laneEnd.length) };
    }).sort((a, z) => a.name.localeCompare(z.name));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalBookings, gridStartKey, gridEndKey]);

  return (
    <Card>
      <div className="overflow-x-auto">
        {/* Same two-row ruler as the Crews tab. James, 21 Aug: "the date header is
            nice, lets get that into gantt as well." */}
        <ScheduleAxis weeks={weeks} gridStart={gridStart} totalDays={totalDays}
          labelCol="260px" label="project · site" />
        <StopsLane gridStartKey={gridStartKey} totalDays={totalDays} labelCol="260px" />   {/* mig 425 */}

        {/* Project rows — ONE BAR PER CREW BOOKING (A4 piece 1).
            The Gantt used to draw a single bar per project spanning its whole
            window, which hid the shape of the work: a job with civil for a week,
            three weeks off, then rigging looked like five solid weeks. Now each
            booking is its own bar, so phasing and re-mobs read at a glance — and
            there is something crew-shaped to grab when drag lands. */}
        {ordered.map(p => {
          const projAs = assignments.filter(a => a.project_id === p.project_id && a.crews);
          const eff = effectiveWindow(p, gridEndKey);

          type GBar = { key: string; from: string; to: string; colour: string;
                        label: string; lane: number; unbooked: boolean; openEnded: boolean };
          const bars: GBar[] = [];

          if (projAs.length === 0) {
            // Same language as the calendar: green dashed = won, needs a crew.
            const s0 = eff.start ?? gridStartKey;
            const e0raw = eff.end ?? gridEndKey;
            bars.push({
              key: `${p.project_id}-none`, from: s0, to: e0raw < s0 ? s0 : e0raw,
              colour: UNBOOKED_GREEN, label: "No crew booked", lane: 0,
              unbooked: true, openEnded: false,
            });
          } else {
            // Lanes so two crews on site at the same time stack instead of
            // drawing on top of each other. Most jobs are phased and use one.
            const sorted = [...projAs].sort((a, z) => a.assigned_from.localeCompare(z.assigned_from));
            const laneEnd: string[] = [];
            for (const a of sorted) {
              const from = a.assigned_from;
              const openEnded = a.assigned_to == null;
              const to = a.assigned_to ?? ((eff.end && eff.end >= from) ? eff.end : gridEndKey);
              if (to < from) continue;
              let lane = laneEnd.findIndex(end => end < from);
              if (lane === -1) { lane = laneEnd.length; laneEnd.push(to); } else { laneEnd[lane] = to; }
              bars.push({
                key: a.pca_id, from, to, colour: a.crews!.colour_hex,
                label: a.crews!.crew_code, lane, unbooked: false, openEnded,
              });
            }
          }

          const laneCount = bars.length ? Math.max(...bars.map(b => b.lane)) + 1 : 1;
          const rowHeight = laneCount * 22;

          return (
            <div
              key={p.project_id}
              role="button"
              tabIndex={0}
              onClick={() => onSelectProject(p)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelectProject(p); } }}
              className="w-full grid items-center gap-px hover:bg-ink-raised/40 transition-colors px-3 py-2 border-b border-line/30 text-left cursor-pointer"
              style={{ gridTemplateColumns: `260px repeat(${totalDays}, minmax(8px, 1fr))` }}
            >
              {/* Left: project label */}
              <div className="min-w-0 pr-3">
                <div className="text-[12px] text-text truncate">
                  {p.sites?.site_name ?? p.project_name}
                </div>
                <div className="text-[10px] text-text-muted truncate">
                  <span className="font-mono">{p.project_code}</span>
                  {p.clients?.legal_name && <> · {p.clients.legal_name}</>}
                </div>
              </div>

              {/* Bars across the time axis */}
              <div className="relative" style={{ gridColumn: `2 / span ${totalDays}`, height: `${rowHeight}px` }}>
                <ScheduleBackdrop gridStart={gridStart} totalDays={totalDays} />
                {bars.map(b => {
                  const startIdx = clipIdx(b.from);
                  const endIdx   = clipIdx(b.to < b.from ? b.from : b.to);
                  const span     = Math.max(1, endIdx - startIdx + 1);
                  return (
                    <div
                      key={b.key}
                      className="absolute rounded flex items-center px-2 text-[10px] truncate font-medium"
                      style={{
                        top:    `${b.lane * 22}px`,
                        height: "20px",
                        left:   `calc(${(startIdx / totalDays) * 100}%)`,
                        width:  `calc(${(span / totalDays) * 100}%)`,
                        background: b.unbooked ? `${b.colour}22` : b.colour,
                        border:     b.unbooked ? `1px dashed ${b.colour}99` : undefined,
                        color:      b.unbooked ? b.colour : "#fff",
                      }}
                      title={[
                        `${p.project_code} · ${p.sites?.site_name ?? p.project_name}`,
                        p.clients?.legal_name ?? "",
                        b.unbooked
                          ? `No crew booked · ${fmtDate(b.from)} → ${fmtDate(b.to)}`
                          : `${b.label} · ${fmtDate(b.from)} → ${b.openEnded ? "open ended" : fmtDate(b.to)}`,
                        eff.isActual ? "Drawn from actual dates" : "Drawn from planned dates",
                      ].filter(Boolean).join("\n")}
                    >
                      {b.unbooked ? (p.sites?.site_name ?? p.project_name) : b.label}
                    </div>
                  );
                })}

                {todayInRange && (
                  <div
                    className="absolute top-0 bottom-0 border-l border-accent/50 pointer-events-none z-0"
                    style={{ left: `calc(${(todayIdx / totalDays) * 100}%)` }}
                  />
                )}
              </div>
            </div>
          );
        })}

        {/* ---- ad-hoc tasks, under the projects ------------------------------
            James: "not bad, but maybe we include tasks in below the projects or
            something like that?"

            Grouped by PERSON rather than one row per task. A Gantt answers "what
            is running and for how long"; the useful companion question is "and
            who is off doing something else while it runs", which is a person per
            row. One row per task would have answered neither.               */}
        {taskRows.length > 0 && (
          <>
            <div className="grid items-center px-3 py-1.5 border-t border-b border-line/40 bg-ink-raised/40"
              style={{ gridTemplateColumns: `260px repeat(${totalDays}, minmax(8px, 1fr))` }}>
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted flex items-center gap-1.5">
                <Send size={10}/> Ad-hoc tasks
              </div>
              <div style={{ gridColumn: `2 / span ${totalDays}` }} />
            </div>
            {taskRows.map(row => (
              <div key={row.employeeId}
                className="grid items-center px-3 py-1 border-b border-line/20"
                style={{ gridTemplateColumns: `260px repeat(${totalDays}, minmax(8px, 1fr))` }}>
                <div className="min-w-0 pr-3 text-[12px] text-text truncate">{row.name}</div>
                <div className="relative"
                  style={{ gridColumn: `2 / span ${totalDays}`, height: `${row.laneCount * 22}px` }}>
                  <ScheduleBackdrop gridStart={gridStart} totalDays={totalDays} />
                  {row.bars.map(b => {
                    const startIdx = clipIdx(b.from);
                    const endIdx   = clipIdx(b.to);
                    const span     = Math.max(1, endIdx - startIdx + 1);
                    return (
                      <button key={b.pb.booking_id} type="button"
                        onClick={() => onSelectTask(b.pb)}
                        title={`${row.name} — ${b.pb.title}${b.pb.location_address ? ` · ${b.pb.location_address}` : ""}\n${fmtDate(b.from)} → ${fmtDate(b.to)}`}
                        className="absolute rounded-sm px-1.5 text-[10px] leading-[18px] truncate text-left
                                   text-text hover:brightness-110 transition"
                        style={{
                          top:    `${b.lane * 22}px`,
                          height: "20px",
                          left:   `calc(${(startIdx / totalDays) * 100}%)`,
                          width:  `calc(${(span / totalDays) * 100}% - 2px)`,
                          background: "repeating-linear-gradient(45deg, rgba(255,120,68,0.32) 0 5px, rgba(255,120,68,0.10) 5px 10px)",
                          border: "1px solid rgba(255,120,68,0.85)",
                        }}>
                        {b.pb.location_address?.trim() || b.pb.title}
                      </button>
                    );
                  })}
                  {todayInRange && (
                    <div className="absolute top-0 bottom-0 border-l border-accent/50 pointer-events-none z-0"
                      style={{ left: `calc(${(todayIdx / totalDays) * 100}%)` }} />
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </Card>
  );
}

// =============================================================================
// ProjectSchedulePanel — slide-in side drawer with project summary + crews +
// inline assign-crew form. Manager+ can add/end assignments. No finance data.
// =============================================================================
interface PanelProps {
  project:     ProjectRow;
  assignments: CrewAssignmentRow[];
  onClose:     () => void;
}

function ProjectSchedulePanel({ project, assignments: gridAssignments, onClose }: PanelProps) {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  // T-414: the prop only carries assignments overlapping the visible month —
  // a return visit booked for another month was invisible here. Fetch the
  // project's FULL crew list (refresh() already invalidates this key).
  const { data: allAssignments } = useQuery({
    queryKey: ["project_crew_assignments", project.project_id],
    queryFn: async () => {
      const { data } = await supabase
        .from("project_crew_assignments")
        .select(`
          pca_id, project_id, assigned_from, assigned_to, notes,
          crews:crew_id (crew_id, crew_code, crew_name, colour_hex)
        `)
        .eq("project_id", project.project_id)
        .order("assigned_from", { ascending: true });
      return (data ?? []) as unknown as CrewAssignmentRow[];
    },
  });
  const assignments = allAssignments ?? gridAssignments;

  // Status-aware date semantics (T-414 Slice A): once a job is live the
  // forecast dates are history — lead with the actual start and the next
  // on-site window instead of inviting edits to a stale plan.
  const isUnderway = ["live", "mobilising", "acceptance_pending"].includes(project.status);
  const todayKey = localDayKey(new Date());
  const currentWindow = assignments.find(a =>
    a.assigned_from <= todayKey && (a.assigned_to ?? "9999-12-31") >= todayKey);
  const nextWindow = assignments
    .filter(a => a.assigned_from > todayKey)
    .sort((a, z) => a.assigned_from.localeCompare(z.assigned_from))[0];
  // T-148 — track expanded roster rows by pca_id.
  const [expandedPcas, setExpandedPcas] = useState<Set<string>>(new Set());
  function togglePca(id: string) {
    setExpandedPcas(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function refresh() {
    qc.invalidateQueries({ queryKey: ["schedule_pcas"] });
    qc.invalidateQueries({ queryKey: ["schedule_roster"] });
    qc.invalidateQueries({ queryKey: ["project_crew_assignments", project.project_id] });
  }

  async function removeAssignment(pcaId: string) {
    if (!window.confirm("Remove this crew assignment from the project?")) return;
    const { error } = await supabase
      .from("project_crew_assignments")
      .delete()
      .eq("pca_id", pcaId);
    if (error) { window.alert(`Couldn't remove: ${error.message}`); return; }
    refresh();
  }

  // In-panel control (so you don't bounce to the project): edit the scheduled
  // dates, and add/see deliverables & bookings.
  const [editDates, setEditDates] = useState(false);
  const [sStart, setSStart] = useState(project.scheduled_start_date ?? "");
  const [sEnd, setSEnd]     = useState(project.scheduled_end_date ?? "");
  async function saveDates() {
    if (sStart && sEnd && sEnd < sStart) return;
    const { error } = await supabase.from("projects")
      .update({ scheduled_start_date: sStart || null, scheduled_end_date: sEnd || null })
      .eq("project_id", project.project_id);
    if (!error) { setEditDates(false); qc.invalidateQueries({ queryKey: ["schedule_projects"] }); }
  }

  const { data: bookings = [], refetch: refetchBookings } = useQuery({
    queryKey: ["panel_bookings", project.project_id],
    queryFn: async () => {
      const { data } = await supabase.from("project_bookings")
        .select("booking_id, booking_type, title, scheduled_start, status")
        .eq("project_id", project.project_id)
        .not("status", "in", "(completed,cancelled)")
        .order("scheduled_start", { ascending: true });
      return (data ?? []) as any[];
    },
  });
  // `as const` so bkType is the enum's own literals, not `string` - this insert
  // has been a standing type error against the generated booking_type union
  // since the file was written.
  const BK_TYPES = ["plant", "materials", "delivery", "electrical", "integration", "subcontractor", "inspection", "meeting", "outage", "other"] as const;
  const [bkAdding, setBkAdding] = useState(false);
  const [bkType, setBkType] = useState<(typeof BK_TYPES)[number]>("plant");
  const [bkTitle, setBkTitle] = useState("");
  const [bkDate, setBkDate] = useState(project.scheduled_start_date ?? localDayKey(new Date()));
  async function addBooking() {
    if (!bkTitle.trim() || !bkDate) return;
    // 3 Nov 2026 — used to write the row bare: no end time, no history, and "confirmed" before anyone confirmed it.
    // Now the same path as Dispatch and the schedule page: 07:00–17:00 that day, tentative, a history row. (Mig 425's refusal still shows.)
    try { await createProjectBooking({ project_id: project.project_id, booking_type: bkType, title: bkTitle, day: bkDate }, "Added from the calendar drawer", user?.user_id ?? null); }
    catch (e: any) { window.alert(`Couldn't add that booking: ${e?.message ?? e}`); return; }
    setBkTitle(""); setBkAdding(false); refetchBookings();
  }

  // My open to-dos on this job (T-347b visibility). RLS scopes to owner/creator,
  // so this shows the current user's outstanding actions for the project.
  const { data: todos = [], refetch: refetchTodos } = useQuery({
    queryKey: ["panel_todos", project.project_id, user?.user_id],
    enabled: !!user?.user_id,
    queryFn: async () => {
      const { data } = await supabase.from("project_user_todos")
        .select("todo_id, body, done, due_date, owner_user_id, created_by_user_id")
        .eq("project_id", project.project_id)
        .eq("done", false)
        .or(`owner_user_id.eq.${user!.user_id},created_by_user_id.eq.${user!.user_id}`)
        .order("due_date", { ascending: true, nullsFirst: false });
      return (data ?? []) as any[];
    },
  });
  const [todoBody, setTodoBody] = useState("");
  const [todoDue, setTodoDue] = useState("");
  async function addTodo() {
    if (!todoBody.trim() || !user?.user_id) return;
    const { error } = await supabase.from("project_user_todos").insert({
      project_id: project.project_id, owner_user_id: user.user_id,
      created_by_user_id: user.user_id, body: todoBody.trim(), due_date: todoDue || null,
    });
    if (!error) { setTodoBody(""); setTodoDue(""); refetchTodos(); }
  }
  async function completeTodo(todoId: string) {
    const { error } = await supabase.from("project_user_todos").update({ done: true }).eq("todo_id", todoId);
    if (!error) refetchTodos();
  }

  // Inline edit of a crew's own on-site window (mirrors the project card).
  const [editingPca, setEditingPca] = useState<string | null>(null);
  const [cFrom, setCFrom] = useState("");
  const [cTo, setCTo] = useState("");
  function startCrewEdit(a: CrewAssignmentRow) { setEditingPca(a.pca_id); setCFrom(a.assigned_from); setCTo(a.assigned_to ?? ""); setReturnFor(null); }
  async function saveCrewDates(pcaId: string) {
    if (!cFrom || (cTo && cTo < cFrom)) return;
    const { error } = await supabase.from("project_crew_assignments")
      .update({ assigned_from: cFrom, assigned_to: cTo || null })
      .eq("pca_id", pcaId);
    // Mig 425 - was swallowed; a refused save (stand-down / embargo) looked like nothing happened.
    if (error) { window.alert(`Couldn't save those dates: ${error.message}`); return; }
    setEditingPca(null); refresh();
  }

  // T-414 — one-tap return visit: same crew, fresh window. Handles the real
  // pattern (crew up, pulled off for foundations/power, re-mob later) without
  // touching the original window — each stint stays its own bar + audit line.
  const [returnFor, setReturnFor] = useState<string | null>(null);
  const [rFrom, setRFrom] = useState("");
  const [rTo, setRTo] = useState("");
  function startReturnVisit(a: CrewAssignmentRow) {
    const dayAfterEnd = a.assigned_to
      ? format(addDays(parseISO(a.assigned_to), 1), "yyyy-MM-dd")
      : todayKey;
    setRFrom(dayAfterEnd > todayKey ? dayAfterEnd : todayKey);
    setRTo("");
    setEditingPca(null);
    setReturnFor(prev => (prev === a.pca_id ? null : a.pca_id));
  }
  async function saveReturnVisit(a: CrewAssignmentRow) {
    if (!a.crews?.crew_id || !rFrom || (rTo && rTo < rFrom)) return;
    const { error } = await supabase.from("project_crew_assignments").insert({
      project_id:    project.project_id,
      crew_id:       a.crews.crew_id,
      assigned_from: rFrom,
      assigned_to:   rTo || null,
      notes:         "Return visit",
    });
    if (error) { window.alert(`Couldn't book the return visit: ${error.message}`); return; }
    setReturnFor(null);
    refresh();
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end pointer-events-none">
      {/* Backdrop — click to close */}
      <button
        type="button"
        className="absolute inset-0 bg-black/40 pointer-events-auto"
        onClick={onClose}
        aria-label="Close panel"
      />
      {/* Drawer */}
      <div className="relative w-full md:max-w-[480px] bg-ink-raised border-l border-line h-full overflow-y-auto pointer-events-auto">
        <div className="sticky top-0 z-10 bg-ink-raised border-b border-line px-4 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to={`/projects/${project.project_id}`}
                className="font-mono text-[12px] text-accent hover:underline inline-flex items-center gap-1"
                title="Open the project"
              >
                {project.project_code}
                <ExternalLink size={10}/>
              </Link>
              <Pill label={project.status.replace(/_/g, " ")} tone={statusTone(project.status)} small/>
            </div>
            <Link to={`/projects/${project.project_id}`} title="Open the project" className="block hover:text-accent transition-colors">
              <h2 className="text-[15px] font-semibold mt-0.5 truncate">
                {project.sites?.site_name ?? project.project_name}
              </h2>
            </Link>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text p-1 rounded hover:bg-ink"
            aria-label="Close"
          >
            <X size={16}/>
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Site + client */}
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Site</div>
            <div className="text-[13px]">
              {project.sites?.site_code && (
                <span className="font-mono text-[11px] text-text-muted mr-2">{project.sites.site_code}</span>
              )}
              {project.sites?.site_name ?? "—"}
            </div>
            {project.sites?.address_line1 && (
              <div className="text-[12px] text-text-muted inline-flex items-start gap-1">
                <MapPin size={11} className="mt-0.5 flex-shrink-0"/>
                <span>{project.sites.address_line1}{project.sites.suburb ? `, ${project.sites.suburb}` : ""}</span>
              </div>
            )}
            <div className="text-[12px] text-text-muted">{project.clients?.legal_name ?? "—"}</div>
          </div>

          {/* Schedule dates — T-414 Slice A: live jobs lead with actuals +
              the next on-site window; the forecast demotes to one line. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Schedule</div>
              {canManage && (
                <button type="button" onClick={() => { setSStart(project.scheduled_start_date ?? ""); setSEnd(project.scheduled_end_date ?? ""); setEditDates(v => !v); }}
                  className="text-[11px] text-accent hover:underline">{editDates ? "Cancel" : isUnderway ? "Edit plan dates" : "Edit dates"}</button>
              )}
            </div>
            {editDates ? (
              <div className="space-y-1.5">
                {isUnderway && (
                  <p className="text-[11px] text-warn">
                    This job is underway — these are the original planned dates. What actually happens on site is driven by the crew bookings below.
                  </p>
                )}
                <div className="flex items-end gap-2 flex-wrap">
                  <label className="text-[10px] text-text-muted">Scheduled start
                    <Input type="date" value={sStart} onChange={e => setSStart(e.target.value)} />
                  </label>
                  <label className="text-[10px] text-text-muted">Scheduled end
                    <Input type="date" value={sEnd} min={sStart || undefined} onChange={e => setSEnd(e.target.value)} />
                  </label>
                  <Button size="sm" onClick={saveDates}>Save</Button>
                </div>
              </div>
            ) : isUnderway ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-[12px]">
                  <Kv label="Actual start" value={project.actual_start_date ? fmtDate(project.actual_start_date) : "—"}/>
                  <Kv label={currentWindow ? "On site now" : "Next time on site"}
                    value={currentWindow
                      ? `${currentWindow.crews?.crew_code ?? "Crew"} until ${currentWindow.assigned_to ? fmtDate(currentWindow.assigned_to) : "project end"}`
                      : nextWindow
                        ? `${nextWindow.crews?.crew_code ?? "Crew"} · ${fmtDate(nextWindow.assigned_from)}${nextWindow.assigned_to ? ` → ${fmtDate(nextWindow.assigned_to)}` : ""}`
                        : "None booked"}/>
                </div>
                {!currentWindow && !nextWindow && canManage && (
                  <button type="button" onClick={() => setAdding(true)}
                    className="text-[11px] text-accent hover:underline inline-flex items-center gap-1">
                    <Plus size={11}/> Book a crew on site
                  </button>
                )}
                <div className="text-[11px] text-text-dim">
                  Planned dates: {project.scheduled_start_date ? fmtDate(project.scheduled_start_date) : "—"} → {project.scheduled_end_date ? fmtDate(project.scheduled_end_date) : "—"}
                  {project.actual_end_date && <span className="ml-2">· Actual end {fmtDate(project.actual_end_date)}</span>}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 text-[12px]">
                <Kv label="Scheduled start" value={project.scheduled_start_date ? fmtDate(project.scheduled_start_date) : "—"}/>
                <Kv label="Scheduled end"   value={project.scheduled_end_date   ? fmtDate(project.scheduled_end_date)   : "—"}/>
                <Kv label="Actual start"    value={project.actual_start_date    ? fmtDate(project.actual_start_date)    : "—"}/>
                <Kv label="Actual end"      value={project.actual_end_date      ? fmtDate(project.actual_end_date)      : "—"}/>
              </div>
            )}
          </div>

          {/* My open to-dos — visibility without leaving the schedule (T-347b) */}
          <div className="space-y-2 border-t border-line pt-4">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">My open to-dos</div>
            {todos.length === 0 && (
              <div className="text-[12px] text-text-muted italic">Nothing open for you on this job.</div>
            )}
            {todos.length > 0 && (
              <div className="space-y-1">
                {todos.map((t: any) => (
                  <div key={t.todo_id} className="flex items-start gap-2 text-[12px] bg-ink rounded border border-line/40 px-2.5 py-1.5">
                    <input type="checkbox" className="mt-0.5 flex-shrink-0" onChange={() => completeTodo(t.todo_id)} title="Mark done" />
                    <span className="text-text flex-1 min-w-0">{t.body}</span>
                    {t.due_date && (
                      <span className={`font-mono text-[11px] flex-shrink-0 ${todoDueTone(t.due_date)}`}>{fmtDate(t.due_date)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Input value={todoBody} onChange={e => setTodoBody(e.target.value)} placeholder="Add a to-do for yourself…" />
              <Input type="date" value={todoDue} onChange={e => setTodoDue(e.target.value)} className="w-[140px]" />
              <Button size="sm" disabled={!todoBody.trim()} onClick={addTodo}>Add</Button>
            </div>
          </div>

          {/* Deliverables & bookings — quick add + open list, in place */}
          <div className="space-y-2 border-t border-line pt-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Deliverables &amp; bookings</div>
              {canManage && !bkAdding && (
                <Button size="sm" variant="secondary" icon={Plus} onClick={() => setBkAdding(true)}>Add</Button>
              )}
            </div>
            {bookings.length === 0 && !bkAdding && (
              <div className="text-[12px] text-text-muted italic">Nothing open — add plant, materials, delivery, electrical…</div>
            )}
            {bookings.length > 0 && (
              <div className="space-y-1">
                {bookings.map((b: any) => (
                  <div key={b.booking_id} className="flex items-center gap-2 text-[12px] bg-ink rounded border border-line/40 px-2.5 py-1.5">
                    <span className="text-[10px] font-mono uppercase tracking-wider2 text-accent bg-accent/10 rounded px-1.5 py-0.5 flex-shrink-0">{b.booking_type}</span>
                    <span className="text-text truncate flex-1 min-w-0">{b.title}</span>
                    <span className="text-text-muted font-mono text-[11px] flex-shrink-0">{fmtDate(b.scheduled_start)}</span>
                    <Pill label={b.status.replace(/_/g, " ")} tone={BK_STATUS_TONE[b.status] ?? "default"} small/>
                  </div>
                ))}
              </div>
            )}
            {bkAdding && canManage && (
              <div className="bg-ink-raised/40 border border-dashed border-line rounded-md p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Type">
                    <Select value={bkType} onChange={e => setBkType(e.target.value as (typeof BK_TYPES)[number])}>
                      {BK_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                    </Select>
                  </Field>
                  <Field label="Date">
                    <Input type="date" value={bkDate} onChange={e => setBkDate(e.target.value)} />
                  </Field>
                </div>
                <Field label="What">
                  <Input value={bkTitle} onChange={e => setBkTitle(e.target.value)} placeholder="e.g. 20T franna, concrete pour, NBN connection" />
                </Field>
                <div className="flex items-center gap-2">
                  <Button size="sm" disabled={!bkTitle.trim()} onClick={addBooking}>Add</Button>
                  <Button size="sm" variant="secondary" onClick={() => setBkAdding(false)}>Cancel</Button>
                  <Link to={`/projects/${project.project_id}/schedule`} className="ml-auto text-[11px] text-accent hover:underline">Full schedule →</Link>
                </div>
              </div>
            )}
          </div>

          {/* Crews — list + add */}
          <div className="space-y-2 border-t border-line pt-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Crews on this project</div>
              {canManage && !adding && (
                <Button size="sm" variant="secondary" icon={Plus} onClick={() => setAdding(true)}>Assign</Button>
              )}
            </div>

            {assignments.length === 0 && !adding && (
              <div className="text-[12px] text-text-muted italic">
                No crews assigned yet.
              </div>
            )}

            {assignments.length > 0 && (
              <div className="space-y-1.5">
                {assignments.map(a => {
                  const _today = localDayKey(new Date());
                  const _ended = a.assigned_to && a.assigned_to < _today;
                  const _upcoming = a.assigned_from > _today;
                  const expanded  = expandedPcas.has(a.pca_id);
                  return (
                    <div key={a.pca_id} className="bg-ink rounded-md border border-line/50 overflow-hidden">
                      <div className="flex items-center justify-between gap-2 pr-3">
                        <button
                          type="button"
                          onClick={() => a.crews?.crew_id && togglePca(a.pca_id)}
                          className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 hover:bg-ink-hover text-left"
                          title={expanded ? "Hide team" : "Show team"}
                        >
                          <ChevronDown
                            size={12}
                            className={`text-text-muted flex-shrink-0 transition-transform ${expanded ? "" : "-rotate-90"}`}
                          />
                          <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: a.crews?.colour_hex ?? UNASSIGNED_GREY }}/>
                          <div className="min-w-0">
                            <div className="text-[12px]">
                              <span className="font-mono">{a.crews?.crew_code ?? "—"}</span>
                              <span className="text-text-muted ml-2">{a.crews?.crew_name ?? ""}</span>
                            </div>
                            <div className="text-[10px] text-text-muted">
                              {fmtDate(a.assigned_from)} → {a.assigned_to ? fmtDate(a.assigned_to) : "project end"}
                              {a.notes && <span className="ml-2 italic">· {a.notes}</span>}
                            </div>
                          </div>
                        </button>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {_ended
                            ? <Pill label="Past" tone="closed" small/>
                            : _upcoming
                              ? <Pill label="Upcoming" tone="pending" small/>
                              : <Pill label="Current" tone="ok" small/>}
                          {canManage && (
                            <button
                              type="button"
                              onClick={() => (editingPca === a.pca_id ? setEditingPca(null) : startCrewEdit(a))}
                              className="text-[10px] text-text-muted hover:text-accent p-1 rounded hover:bg-accent/10"
                              title="Edit this crew's dates on site"
                            >
                              <CalendarDays size={11}/>
                            </button>
                          )}
                          {canManage && a.crews?.crew_id && (
                            <button
                              type="button"
                              onClick={() => startReturnVisit(a)}
                              className="text-[10px] text-text-muted hover:text-accent p-1 rounded hover:bg-accent/10"
                              title="Book a return visit — same crew, new dates"
                            >
                              <Plus size={11}/>
                            </button>
                          )}
                          {canManage && (
                            <button
                              type="button"
                              onClick={() => removeAssignment(a.pca_id)}
                              className="text-[10px] text-text-muted hover:text-lost p-1 rounded hover:bg-lost/10"
                              title="Remove this crew assignment"
                            >
                              <Trash2 size={11}/>
                            </button>
                          )}
                        </div>
                      </div>
                      {editingPca === a.pca_id && canManage && (
                        <div className="border-t border-line/40 bg-ink-raised/40 px-3 py-2 flex items-end gap-2 flex-wrap">
                          <label className="text-[10px] text-text-muted">On site from
                            <Input type="date" value={cFrom} onChange={e => setCFrom(e.target.value)} />
                          </label>
                          <label className="text-[10px] text-text-muted">Until
                            <Input type="date" value={cTo} min={cFrom || undefined} onChange={e => setCTo(e.target.value)} />
                          </label>
                          <Button size="sm" onClick={() => saveCrewDates(a.pca_id)} disabled={!cFrom}>Save</Button>
                          <span className="text-[10px] text-text-dim italic self-center">Blank = project end</span>
                        </div>
                      )}
                      {returnFor === a.pca_id && canManage && (
                        <div className="border-t border-line/40 bg-ink-raised/40 px-3 py-2 space-y-1.5">
                          <div className="text-[10px] uppercase tracking-wider text-text-muted">Return visit — {a.crews?.crew_code}, new dates</div>
                          <div className="flex items-end gap-2 flex-wrap">
                            <label className="text-[10px] text-text-muted">Back on site
                              <Input type="date" value={rFrom} onChange={e => setRFrom(e.target.value)} />
                            </label>
                            <label className="text-[10px] text-text-muted">Until
                              <Input type="date" value={rTo} min={rFrom || undefined} onChange={e => setRTo(e.target.value)} />
                            </label>
                            <Button size="sm" onClick={() => saveReturnVisit(a)} disabled={!rFrom}>Book it</Button>
                          </div>
                          <div className="text-[10px] text-text-dim italic">The first visit stays untouched — each trip to site is its own bar.</div>
                        </div>
                      )}
                      {expanded && a.crews?.crew_id && (
                        <div className="border-t border-line/40 bg-ink-raised/40 px-3 py-2">
                          <CrewRosterPanel crewId={a.crews.crew_id} compact/>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {adding && (
              <AddCrewForm
                projectId={project.project_id}
                projectScheduledStart={project.scheduled_start_date}
                projectScheduledEnd={project.scheduled_end_date}
                onClose={() => { setAdding(false); refresh(); }}
              />
            )}
          </div>

          {/* Open full project */}
          <div className="border-t border-line pt-4">
            <Link
              to={`/projects/${project.project_id}`}
              className="text-[12px] text-accent hover:underline inline-flex items-center gap-1"
            >
              <ExternalLink size={12}/> Open full project page
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function AddCrewForm({
  projectId, projectScheduledStart, projectScheduledEnd, onClose,
}: {
  projectId: string;
  projectScheduledStart: string | null;
  projectScheduledEnd: string | null;
  onClose: () => void;
}) {
  // Phased crews (2026-07-02): each assignment carries its own window so crews
  // can stagger (Civil first, Rigging later). Defaults to the project window.
  const today = localDayKey(new Date());
  const [crewId, setCrewId]   = useState("");
  const [fromDate, setFromDate] = useState(projectScheduledStart ?? today);
  const [toDate,   setToDate]   = useState(projectScheduledEnd ?? "");
  const [notes,  setNotes]    = useState("");
  const [saving, setSaving]   = useState(false);
  const [error,  setError]    = useState<string | null>(null);

  const { data: crews } = useQuery({
    queryKey: ["crews_pickable_for_schedule"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crews")
        .select("crew_id, crew_code, crew_name, colour_hex, active")
        .eq("active", true)
        .order("crew_code");
      if (error) throw error;
      return (data ?? []) as { crew_id: string; crew_code: string; crew_name: string; colour_hex: string; active: boolean }[];
    },
  });

  async function save() {
    if (!crewId || !fromDate) return;
    if (toDate && toDate < fromDate) { setError("End date can't be before the start date."); return; }
    setSaving(true); setError(null);
    try {
      const { error } = await supabase.from("project_crew_assignments").insert({
        project_id:    projectId,
        crew_id:       crewId,
        assigned_from: fromDate,
        assigned_to:   toDate || null,   // blank = runs to project end
        notes:         notes.trim() || null,
      });
      if (error) throw error;
      onClose();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-ink-raised/40 border border-dashed border-line rounded-md p-3 space-y-2.5">
      <div className="text-[11px] uppercase tracking-wider text-text-muted">Assign crew</div>

      <Field label="Crew">
        <Select value={crewId} onChange={e => setCrewId(e.target.value)}>
          <option value="">— pick a crew —</option>
          {(crews ?? []).map(c => (
            <option key={c.crew_id} value={c.crew_id}>{c.crew_code} · {c.crew_name}</option>
          ))}
        </Select>
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="On site from">
          <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
        </Field>
        <Field label="Until" hint="Blank = project end">
          <Input type="date" value={toDate} min={fromDate || undefined} onChange={e => setToDate(e.target.value)} />
        </Field>
      </div>
      <Field label="Notes" hint="Optional context">
        <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional"/>
      </Field>
      <div className="text-[10px] text-text-muted italic">
        Defaults to the project window — change to stagger crews (Civil first, Rigging later).
      </div>

      {error && (
        <div className="text-[11px] text-lost inline-flex items-center gap-1">
          <AlertCircle size={11}/> {error}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!crewId || saving} onClick={save}>
          {saving ? <Spinner size={12}/> : "Save assignment"}
        </Button>
        <Button size="sm" variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className="text-[12px] text-text mt-0.5 font-mono">{value}</div>
    </div>
  );
}

// RAG colour for a to-do due date (matches the project todo card convention).
function todoDueTone(due: string | null): string {
  if (!due) return "text-text-muted";
  const days = Math.round((new Date(due + "T23:59:59").getTime() - Date.now()) / 86400000);
  if (days < 0)  return "text-danger";  // overdue
  if (days <= 0) return "text-danger";  // due today
  if (days <= 2) return "text-warn";    // due soon
  return "text-ok";
}

function statusTone(status: string): "live" | "mobilising" | "acceptance_pending" | "on_hold" | "closed" | "default" {
  switch (status) {
    case "live":               return "live";
    case "mobilising":         return "mobilising";
    case "scheduled":          return "mobilising"; // pre-mob — same blue-ish tone
    case "acceptance_pending": return "acceptance_pending";
    case "on_hold":            return "on_hold";
    case "closed":
    case "cancelled":
    case "lost":               return "closed";
    default:                   return "default";
  }
}
